/**
 * PanelRenderer — recursive renderer for the AI's page-spec language.
 *
 * The substrate that lets the Operative compose entire dashboard
 * sections without us writing per-section React. The AI emits a
 * `panel_spec` tree of `{kind, ...props, children?}` nodes; this
 * renderer walks the tree and produces real DOM.
 *
 * Relationship to the existing renderer registry:
 *   - The registry (chart, member_grid, funnel, diff_panel, etc.)
 *     ships full-card renderers — each takes a complete input shape
 *     and produces a self-contained card.
 *   - This file ships LAYOUT PRIMITIVES (header, stat_card, hero,
 *     prose_card, list, badge_row, divider, row, column, grid) —
 *     building blocks the AI composes into bigger structures.
 *   - The "renderer" and "widget" panel kinds bridge to the registry +
 *     the dashboard widget grid: any existing renderer / widget is
 *     also a valid panel kind. So the AI gets one unified vocabulary.
 *
 * Style discipline (encoded into every primitive):
 *   - Tailwind utility classes only; no inline styles for visuals
 *   - Color tokens: --primary, --accent, plus Tailwind stones for
 *     neutrals. Never decorative color.
 *   - Spacing: gap-2 / gap-3 / gap-4. No margins inside primitives.
 *
 * The spec language is intentionally narrow. Each kind has a fixed
 * set of props; children are typed; nesting rules are enforced by
 * the renderer (e.g. a stat_card can't contain a grid). When the AI
 * needs richer composition than the primitives allow, that's a
 * signal we need a new kind, not freeform HTML.
 */

import { SafeMarkdown } from "./SafeMarkdown.js";
import React, { Fragment, useEffect, useState } from "react";
// React 19 types no longer declare a global JSX namespace.
import type { JSX } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Calendar, MapPin, Clock, AlertTriangle,
  Lightbulb, Eye, AlertOctagon, CheckCircle2,
  ArrowRight, BarChart3, Check, Circle, CircleDot, Minus, PauseCircle,
  Pause, Play, Star, XCircle,
  type LucideIcon,
} from "lucide-react";
import { SpecIcon, resolveSpecIcon } from "./SpecIcon.js";
import { RendererSlot } from "./renderers/RendererSlot.js";
import { EntityRef } from "./EntityRef.js";
import { useCycle } from "./useCycle.js";
import { effectiveCycle, useTvMode } from "./tvMode.js";

// ── Spec tree ───────────────────────────────────────────────────────────

/** A node in the page-spec tree. Recursive. */
export type PanelSpec =
  | HeaderSpec
  | StatCardSpec
  | HeroSpec
  | ProseCardSpec
  | ListSpec
  | BadgeRowSpec
  | DividerSpec
  | RowSpec
  | ColumnSpec
  | GridSpec
  | RendererSpec
  | WidgetSpec
  | SlideSpec
  | DeckSpec
  | CalloutSpec
  | ProgressBarSpec
  | LegislatorChipSpec
  | TimelineSpec
  | CarouselSpec
  | MemberCardSpec
  | HeatmapSpec
  | StatComparisonSpec
  | CalendarSpec
  | TaskListSpec
  | ComparisonTableSpec
  | StepProgressSpec
  | ArticleSummarySpec
  | PullquoteSpec
  | NewsFeedSpec
  | AvatarStackSpec
  | VoteRecordStripSpec
  | EmptyStateSpec
  | SignalCardSpec
  | BillTextExcerptSpec
  | EventCardSpec
  | TagCloudSpec
  | BreadcrumbSpec
  | PollCardSpec
  | HeadToHeadSpec
  | CandidateCardSpec
  | PartisanLeanMeterSpec
  | FavorabilitySplitSpec
  | CrosstabTableSpec
  | ElectoralMapSpec
  | RaceRatingStripSpec
  | FundraisingRaceSpec
  | DonorBreakdownSpec
  | WhipCountSummarySpec
  | AffiliationListSpec
  | HeadlineStripSpec
  | MeetingPrepCardSpec
  | VoteBreakdownChamberSpec
  | TargetQuadrantSpec
  | CoalitionSignonCardSpec
  | DistrictDemoCardSpec
  | HistoricalResultsStripSpec
  | TalkingPointsCardSpec
  | BillAmendmentTreeSpec
  | KpiGridSpec
  | GapChartSpec
  | EngagementFunnelCardSpec
  | PressReleaseCardSpec
  | DecisionMatrixSpec
  | DonorCardSpec
  | DelegationMapCardSpec
  | CosponsorGrowthChartSpec
  | MissionAlignmentRadarSpec
  | MemberPositionDiffSpec
  | EndorsementCardSpec
  | EventAttendanceCardSpec
  | ScorecardStripSpec
  | CosponsorInfluenceMapSpec
  | CosponsorAvalancheBenchSpec
  | BillCosponsorAvalancheSpec
  | BillReactivationPlanSpec
  | BillCascadeSimulatorSpec
  | BillObstacleNetworkSpec
  | ProceduralStateStripSpec
  | PrecedentAnchorSpec
  | LeveragePointsSpec
  | ParliamentChartSpec
  | DiagnosisPanelSpec
  | CommitteeMarkupSummarySpec
  | RecipeSpec
  | DataSpec;

export interface HeaderSpec {
  kind: "header";
  /** 1 → page-level (one per page), 2 → section, 3 → subsection. */
  level?: 1 | 2 | 3;
  text: string;
  subtitle?: string;
}

export interface StatCardSpec {
  kind: "stat_card";
  label: string;
  /** Hero number / string. Right-sized via the level field. */
  value: string | number;
  /** Optional delta string (e.g. "+12 this week", "-3 vs target"). */
  delta?: string;
  /** Optional inline comparison ("vs 47 last week"). */
  comparison?: string;
  /** Optional inline sparkline (Tufte: numbers want trends). */
  sparkline?: number[];
  /** Optional theme — colorless by default. */
  theme?: "default" | "alert" | "success" | "warning";
}

export interface HeroSpec {
  kind: "hero";
  title: string;
  /** Big number / phrase. */
  value?: string | number;
  /** One-line subtitle below the value. */
  subtitle?: string;
  /** Theme color. Default uses the workspace's --primary. */
  theme?: "default" | "alert" | "success" | "warning";
  /** Children render as a row beneath the hero. Use for badge rows or
   *  inline stat strips that complete the hero composition. */
  children?: PanelSpec[];
}

export interface ProseCardSpec {
  kind: "prose_card";
  /** Markdown body. Rendered with safe defaults — no script/iframe. */
  markdown: string;
  title?: string;
}

export interface ListSpec {
  kind: "list";
  title?: string;
  items: Array<{
    title: string;
    subtitle?: string;
    href?: string;
    /** Right-aligned trailing text (e.g. count, date, status). */
    trailing?: string;
    /** Optional structured entity reference. When set, the renderer
     *  auto-derives the click-through URL via entityHref(kind, id).
     *  This is the preferred way to make a row navigate to a member /
     *  contact / bill / account detail page — beats setting href
     *  manually and lets the substrate own the URL contract. */
    entity?: {
      kind: "member" | "contact" | "bill" | "account" | "district" | "state" | "committee" | "coalition";
      id: string;
    };
  }>;
  /** When true, items render with no surrounding card chrome — useful
   *  when the list is inside a hero or another container. */
  flush?: boolean;
  /** Auto-rotate one-item-at-a-time on a timer. Honors workspace
   *  view_preferences.rotation_enabled — AI can globally pause via
   *  set_view_preferences without re-composing. */
  cycle?: { interval_ms?: number };
}

export interface BadgeRowSpec {
  kind: "badge_row";
  badges: Array<{
    label: string;
    value?: string | number;
    color?: "default" | "primary" | "accent" | "success" | "warning" | "alert";
  }>;
}

export interface DividerSpec {
  kind: "divider";
  /** Optional centered label rendered on the divider line. */
  label?: string;
}

export interface RowSpec {
  kind: "row";
  children: PanelSpec[];
  /** Tailwind gap step (1-8). Default 3. */
  gap?: number;
  /** Vertical alignment. Default "start". */
  align?: "start" | "center" | "stretch";
}

export interface ColumnSpec {
  kind: "column";
  children: PanelSpec[];
  /** Tailwind gap step (1-8). Default 3. */
  gap?: number;
}

export interface GridSpec {
  kind: "grid";
  children: PanelSpec[];
  /** Columns (1-12). Default 3. */
  cols?: number;
  /** Tailwind gap step (1-8). Default 3. */
  gap?: number;
}

/** Delegate to the existing renderer registry (chart / member_grid /
 *  funnel / diff_panel / entity_card / surface_conflict / etc.). */
export interface RendererSpec {
  kind: "renderer";
  /** Tool name from the registry (e.g. "render_chart"). */
  tool: string;
  /** Input matching the renderer's schema. */
  input: Record<string, unknown>;
}

/** Delegate to a workspace dashboard widget (the registry in
 *  src/app/widgets/). The AI references widgets by their widget_id. */
export interface WidgetSpec {
  kind: "widget";
  widget_id: string;
  instance_id?: string;
  params?: Record<string, unknown>;
}

/**
 * One slide in a deck. The body is a `PanelSpec[]` so the AI gets the
 * full primitive vocabulary inside a slide (heroes, stat_cards, lists,
 * embedded widgets, Tufte charts, etc.).
 *
 * Embedded inside a non-presentation page, slides render as bordered
 * "preview frames" inline. Inside a deck/presentation player they fill
 * the viewport one at a time with keyboard navigation.
 */
export interface SlideSpec {
  kind: "slide";
  /** Headline at the top of the slide. */
  title: string;
  /** Optional one-liner subtitle below the title. */
  subtitle?: string;
  /** Body composed of any panel kinds (header / stat_card / hero / list /
   *  row / column / grid / renderer / widget / etc.). */
  body?: PanelSpec[];
  /** Speaker notes — visible only in editor / off-screen, never in the
   *  rendered slide. */
  notes?: string;
  /** Layout intent. "title" centers a title-only slide; "centered" centers
   *  the body block; "split" reserved for future two-column slides;
   *  "full" (default) is full-bleed body. */
  layout?: "title" | "split" | "full" | "centered";
  /** Theme for the slide chrome. */
  theme?: "default" | "dark" | "primary" | "accent";
}

/**
 * A presentation deck. Slides[] is ordered. When written to
 * workspaces.ai_panels.presentation[deck_id] via compose_panel, the
 * player at /app/presentations/:deck_id renders fullscreen with
 * keyboard navigation. Embedded inside a non-presentation page, decks
 * render as a contact-sheet preview that links to the player.
 */
export interface DeckSpec {
  kind: "deck";
  /** Deck title — shown in the player chrome and the index. */
  title: string;
  /** Optional subtitle (date, audience, occasion). */
  subtitle?: string;
  /** Ordered slides. At least one. */
  slides: SlideSpec[];
  /** Optional footer text shown on every slide (e.g. "Confidential · Q4 2026"). */
  footer?: string;
  /** Theme applied to all slides that don't override. */
  theme?: "default" | "dark" | "primary" | "accent";
}

/**
 * Highlighted text block with icon + tone. Use sparingly — one or two
 * per panel — to call out a single decision-relevant insight, risk, or
 * tip. Reach for this when prose_card would understate the point but a
 * stat_card would overstate it.
 */
export interface CalloutSpec {
  kind: "callout";
  /** Body text (plain string, line breaks preserved). */
  body: string;
  /** Optional title above the body. */
  title?: string;
  /** Tone — sets icon + accent color. */
  tone?: "tip" | "insight" | "warning" | "alert" | "success";
  /** Optional inline icon override — a lucide icon name such as
   *  "trending-up" or "alert-triangle" (legacy single emoji are mapped
   *  to their lucide equivalent; unknown values fall back to the
   *  tone-default). */
  icon?: string;
}

/**
 * Labeled progress toward a target threshold. The bar fills proportional
 * to value/max; an optional `target` line marks a discrete threshold
 * (e.g. cloture at 60). Beats stat_card when the *position relative to a
 * threshold* is the point — whip math, fundraising goals, vote counts.
 */
export interface ProgressBarSpec {
  kind: "progress_bar";
  /** Label shown above the bar. */
  label: string;
  /** Current value. */
  value: number;
  /** Maximum the bar can fill (chamber size, fundraising target, etc.). */
  max: number;
  /** Optional threshold line (e.g. 60 for Senate cloture, 218 for House majority). */
  target?: number;
  /** Optional caption below the bar (e.g. "+3 since Monday"). */
  caption?: string;
  /** Tone — sets the fill color. */
  tone?: "default" | "success" | "warning" | "alert";
  /** Show the value as a fraction "47/60" instead of just "47". Default true. */
  show_fraction?: boolean;
}

/**
 * A small inline legislator chip — photo (from
 * /api/v1/photos/members/:bioguide.jpg), name, party badge, optional
 * state/role/subtitle. Use inside hero children, badge rows, or list
 * subtitles when names alone are losing the visual battle.
 *
 * For a fuller rendering with stance chips and last-touch metadata,
 * use the legislator_card primitive (TBD) or the renderer registry's
 * render_member_grid for tabular views.
 */
export interface LegislatorChipSpec {
  kind: "legislator_chip";
  /** Bioguide ID — drives the photo URL. */
  bioguide: string;
  /** Display name (e.g. "Sen. Cruz"). Required so the chip is meaningful
   *  even if the photo 404s. */
  name: string;
  /** Optional party single-letter code (D / R / I). Renders as a colored badge. */
  party?: "D" | "R" | "I" | string;
  /** Optional state abbr (CA / NY / TX). */
  state?: string;
  /** Optional secondary line — role, stance, last touch, etc. */
  subtitle?: string;
  /** Optional clickable href to a detail page. */
  href?: string;
  /** Compact = chip without the secondary line, smaller photo. */
  compact?: boolean;
}

/**
 * A pill-tab carousel that rotates through a small set of PanelSpec
 * slides. Reach for this when the principal wants two or three views
 * of the same surface (House vs Senate, this-week vs last-week,
 * coalition vs opposition) without the layout cost of stacking them.
 *
 * Autoplay rotates on a timer; clicking a tab pauses autoplay and
 * jumps to that slide. Set autoplay_seconds=0 for purely manual.
 */
export interface CarouselSpec {
  kind: "carousel";
  /** Ordered slides. Each is a label + a full PanelSpec body. */
  slides: Array<{
    /** Pill label (e.g. "House", "Senate"). Required so the tab strip
     *  is meaningful. */
    label: string;
    /** The slide body — any PanelSpec. Typically a column with a
     *  renderer + supporting prose. */
    spec: PanelSpec;
  }>;
  /** Autoplay rotation in seconds. Default 6. Set 0 to disable autoplay. */
  autoplay_seconds?: number;
  /** Initial slide index. Default 0. */
  initial?: number;
  /** Optional title above the tab strip. */
  title?: string;
}

/**
 * The richer companion to legislator_chip. Photo + name + party + state
 * + relationship tier + last-touch date + optional stance chips. Use
 * inside hero children, grids, or as a slide focal point — anywhere a
 * single member is the subject and you have meaningful CRM context.
 *
 * Pair with the get_member_card_context aggregator: it returns a single
 * payload that drops directly into this primitive.
 */
export interface MemberCardSpec {
  kind: "member_card";
  bioguide: string;
  /** Display name (e.g. "Sen. Cruz"). Required. */
  name: string;
  party?: "D" | "R" | "I" | string;
  state?: string;
  /** District number for House members. Set 0 for at-large. */
  district?: number;
  /** Relationship tier — drives the colored dot. */
  tier?: "hot" | "warm" | "cool" | "cold" | string;
  /** Optional relationship score (0–100). Renders next to the tier dot. */
  score?: number;
  /** Last-touch label (e.g. "Met 3d ago", "Last call: Apr 22"). */
  last_touch?: string;
  /** Optional one-line headline (e.g. role, committee, current stance). */
  headline?: string;
  /** Optional stance chips — short labels with optional tone. */
  stances?: Array<{
    label: string;
    tone?: "default" | "primary" | "success" | "warning" | "alert";
  }>;
  /** Optional labeled facts — ad-hoc metric rows ("KOSA: Confirmed Yes",
   *  "Co-sponsors this year: 47", "Avg attendance: 96%"). Use when the
   *  detail doesn't fit tier / score / last_touch / stances. Rendered
   *  as a tight definition-list row beneath stances. */
  facts?: Array<{
    label: string;
    value: string | number;
    tone?: "default" | "primary" | "success" | "warning" | "alert";
  }>;
  /** Optional clickable href to the member detail page. */
  href?: string;
}

/**
 * 2-D color grid: rows × columns of cells, each filled by a value.
 * The natural shape for member × issue alignment matrices, scorecard
 * grids, signal density per source-type by week, etc.
 *
 * The renderer is generic over content — the AI supplies the row labels,
 * column labels, and a cells map keyed by `${row_id}:${col_id}`. Each
 * cell's fill is a CSS color; an optional value/label gets a tooltip.
 */
export interface HeatmapSpec {
  kind: "heatmap";
  /** Optional title above the grid. */
  title?: string;
  /** Row headers (left edge). */
  rows: Array<{ id: string; label: string; sublabel?: string }>;
  /** Column headers (top edge). */
  columns: Array<{ id: string; label: string; sublabel?: string }>;
  /** Cells keyed by `${row_id}:${col_id}`. Missing cells render muted. */
  cells: Record<string, { fill: string; value?: string | number; label?: string }>;
  /** Optional legend rows shown below the grid. */
  legend?: Array<{ label: string; color: string }>;
  /** Cell side length in pixels. Default 28. */
  cell_size?: number;
}

/**
 * Two values side-by-side with a delta arrow. Beats two stat_cards in a
 * row when the comparison itself is the story — "Lee yes vs AOC no",
 * "Q3 $4.2M vs Q4 $5.1M", "us 47 yea vs them 41 no". The delta is
 * AI-supplied so you can phrase it however the comparison reads.
 */
export interface StatComparisonSpec {
  kind: "stat_comparison";
  /** Optional title above the pair. */
  title?: string;
  /** Left side — label + value + optional sublabel. */
  left: { label: string; value: string | number; sublabel?: string; tone?: "default" | "primary" | "success" | "warning" | "alert" };
  /** Right side — same shape. */
  right: { label: string; value: string | number; sublabel?: string; tone?: "default" | "primary" | "success" | "warning" | "alert" };
  /** The delta between them — text + tone (e.g. "+18 swing", "alert"). */
  delta?: { label: string; tone?: "default" | "success" | "warning" | "alert" };
}

/**
 * Calendar — week or month grid with items per day. The natural shape
 * for upcoming votes, member meetings, fundraising events, briefing
 * deliverables, and any "what's on the calendar" question. Pair with
 * the get_calendar_events aggregator: it unions workspace_events +
 * upcoming bill_actions + opportunity expected_close_dates + scheduled
 * touches and returns events keyed by date.
 */
export interface CalendarSpec {
  kind: "calendar";
  /** Optional title above the grid. */
  title?: string;
  /** "week" (Mon–Sun) or "month" (4–6 weeks). Default "month". */
  view?: "week" | "month";
  /** Anchor ISO date (YYYY-MM-DD). The calendar centers around this date.
   *  Defaults to today (renderer-side). */
  anchor_date?: string;
  /** Events keyed by ISO date (YYYY-MM-DD). Each event renders as a
   *  small chip in that day's cell. */
  events: Record<string, Array<{
    title: string;
    /** Optional time string ("09:00", "2pm", or omit for all-day). */
    time?: string;
    /** Optional href — wraps the chip in a link. */
    href?: string;
    /** Optional tone — sets the chip color. */
    tone?: "default" | "primary" | "success" | "warning" | "alert";
    /** Optional icon — lucide icon name (e.g. "calendar"); legacy emoji mapped. */
    icon?: string;
  }>>;
}

/**
 * task_list — checkbox-style list with due_date + assignee + status.
 * Different from `list` (which is read-only): each item carries
 * actionable metadata. Pair with get_my_task_list, which surfaces
 * attention_items shaped for this primitive.
 */
export interface TaskListSpec {
  kind: "task_list";
  /** Optional title above the list. */
  title?: string;
  /** Items in display order. */
  items: Array<{
    /** Item id — surfaced to the AI on click for follow-up routing. */
    id: string;
    title: string;
    subtitle?: string;
    /** Status drives the icon (open circle, in_progress dot, done check, deferred pause, dismissed x). */
    status?: "open" | "in_progress" | "done" | "deferred" | "dismissed";
    /** Due date string — passed verbatim, format to taste (e.g. "Fri", "May 12"). */
    due?: string;
    /** Whether the due date is past (drives a red badge). */
    overdue?: boolean;
    /** Assignee label — e.g. "You", "Liz", or omit. */
    assignee?: string;
    /** Optional href — clicking the row navigates. */
    href?: string;
    /** Optional tone — sets the row accent. */
    tone?: "default" | "primary" | "warning" | "alert";
  }>;
  /** Optional empty-state copy. Default: "No items." */
  empty_label?: string;
}

/**
 * comparison_table — N-column side-by-side comparison of entities. The
 * natural shape for "compare these 3 bills" or "rank these 4 members"
 * or "stack these 2 orgs' positions next to each other." Different
 * from `heatmap` (which is many×many color grid) and `stat_comparison`
 * (which is exactly two values).
 *
 * Cell content is per-cell — text + optional badge + optional value
 * formatting — keyed by `${row.key}:${column.key}`.
 */
export interface ComparisonTableSpec {
  kind: "comparison_table";
  /** Optional title above the table. */
  title?: string;
  /** Subjects being compared (the columns of the table). */
  columns: Array<{
    key: string;
    /** Header label (e.g. bill_ref, member name). */
    label: string;
    /** Optional sublabel under the header (e.g. sponsor, party). */
    sublabel?: string;
  }>;
  /** Attributes being compared (the rows of the table). */
  rows: Array<{
    key: string;
    /** Row label (e.g. "Sponsor", "Cosponsors", "Our position"). */
    label: string;
    /** Optional sublabel. */
    sublabel?: string;
  }>;
  /** Cells keyed by `${row.key}:${col.key}`. */
  cells: Record<string, {
    /** Plain text content. */
    text?: string;
    /** Optional small badge (party letter, status, etc.). */
    badge?: { label: string; tone?: "default" | "primary" | "success" | "warning" | "alert" };
    /** Optional tone — sets text emphasis. */
    tone?: "default" | "primary" | "success" | "warning" | "alert";
  }>;
}

/**
 * step_progress — discrete-stage horizontal progress indicator. Each
 * step is "done", "current", or "pending". Use for bill lifecycle
 * (introduced ✓ → committee ✓ → reported ◯ → floor ◯ → law ◯),
 * onboarding flows, sequential checkpoints. Different from
 * progress_bar (continuous fill) and timeline (chronological events).
 */
export interface StepProgressSpec {
  kind: "step_progress";
  /** Optional title above the steps. */
  title?: string;
  /** Steps in order. */
  steps: Array<{
    label: string;
    sublabel?: string;
    /** Status — drives the marker style. */
    status: "done" | "current" | "pending" | "skipped";
    /** Optional date label (e.g. "Mar 18", "today"). */
    date?: string;
  }>;
}

/**
 * Full-card rendering for one news article. Headline + outlet + date +
 * sentiment + AI summary + optional pullquote + optional row of mentioned
 * legislators (with photos pulled from /api/v1/photos/members/:bioguide).
 *
 * Use as a focal element when one specific article is the story (the
 * piece that flipped a member, the press hit that landed). For a list
 * of mentions, use `news_feed` instead.
 */
export interface ArticleSummarySpec {
  kind: "article_summary";
  /** Headline / source title. Required. */
  headline: string;
  /** Outlet name (e.g. "Politico", "NYT"). Optional but strongly preferred. */
  outlet?: string;
  /** Optional outlet classification — "tier_1_national", "trade", "regional", etc. */
  outlet_classification?: string;
  /** Optional outlet political lean — "left" | "center_left" | "center" | "center_right" | "right". */
  outlet_lean?: "left" | "center_left" | "center" | "center_right" | "right" | "unknown";
  /** Published date string. Format to taste (e.g. "May 9", "2026-05-09"). */
  date?: string;
  /** Sentiment dot color. */
  sentiment?: "positive" | "negative" | "neutral" | "mixed";
  /** Optional byline ("by Jane Reporter"). */
  byline?: string;
  /** AI summary or lede. Plain text, line breaks preserved. */
  summary?: string;
  /** Optional pullquote — a single highlighted quote from the article. */
  pullquote?: {
    text: string;
    /** Optional speaker name (for the attribution line). */
    attribution?: string;
    /** Optional bioguide — drives the speaker photo via the CRM photo proxy. */
    speaker_bioguide?: string;
  };
  /** Optional list of mentioned legislators — renders as an avatar_stack-style row. */
  mentioned_legislators?: Array<{
    bioguide: string;
    name: string;
    /** Optional role tag — "quoted", "cited", "covered", "mentioned". */
    role?: "quoted" | "cited" | "covered" | "mentioned" | "aggregated_from" | "background";
  }>;
  /** Optional source URL — wraps the headline in a link. */
  href?: string;
  /** Optional hit_role — sets a colored badge on the headline. */
  hit_role?: "quoted" | "cited" | "covered" | "mentioned" | "aggregated_from" | "background";
}

/**
 * Standalone highlighted pullquote with attribution and optional speaker
 * photo (from the CRM via bioguide → /api/v1/photos/members proxy).
 *
 * Use sparingly — one per panel — to give a moment of weight to a single
 * quote. Beats wrapping a quote in prose_card when the quote is THE point.
 */
export interface PullquoteSpec {
  kind: "pullquote";
  /** The quote text. Required. */
  text: string;
  /** Optional attribution line (e.g. "Sen. Cruz, Senate floor, May 8"). */
  attribution?: string;
  /** Optional speaker bioguide — drives the photo. Falls back to initials. */
  speaker_bioguide?: string;
  /** Optional speaker name — required if speaker_bioguide is set so initials work. */
  speaker_name?: string;
  /** Optional source URL — wraps the attribution in a link. */
  source_href?: string;
  /** Optional source label (e.g. "The Daily Beast", "Senate floor"). */
  source_label?: string;
  /** Optional theme for emphasis. */
  theme?: "default" | "primary" | "alert";
}

/**
 * Compact list of news mentions — each row is sentiment dot + outlet
 * badge + headline + small summary + optional mentioned-legislators row.
 * For when "what's been written about us this week" is the question.
 *
 * Pair with the get_press_mentions_feed aggregator: it shapes
 * briefing_items into this exact structure.
 */
export interface NewsFeedSpec {
  kind: "news_feed";
  /** Optional title above the feed. */
  title?: string;
  /** Items in display order (typically newest-first). */
  items: Array<{
    /** Stable id (briefing_item.id). */
    id: string;
    headline: string;
    outlet?: string;
    date?: string;
    sentiment?: "positive" | "negative" | "neutral" | "mixed";
    /** Short summary line — 1–2 sentences. */
    summary?: string;
    /** Optional URL — clicking the row opens it. */
    href?: string;
    /** Optional hit_role for a colored badge ("quoted", "cited", "covered", etc.). */
    hit_role?: "quoted" | "cited" | "covered" | "mentioned" | "aggregated_from" | "background";
    /** Optional inline list of mentioned legislators (renders as an avatar_stack row). */
    mentioned_legislators?: Array<{ bioguide: string; name: string }>;
  }>;
  /** Optional empty-state copy. */
  empty_label?: string;
  /** Auto-rotate one-item-at-a-time on a timer. Honors workspace
   *  view_preferences.rotation_enabled. */
  cycle?: { interval_ms?: number };
}

/**
 * Overlapping circles of legislator photos with a "+N more" indicator
 * for crowded counts. Reach for this when the message is "these N
 * people" and you don't have the vertical real estate for chips or a
 * grid. Photos pulled from the CRM photo proxy with initials fallback.
 */
export interface AvatarStackSpec {
  kind: "avatar_stack";
  /** Optional title above the stack. */
  title?: string;
  /** Members to show. The first N render as avatars; the rest fold into "+N more". */
  members: Array<{
    bioguide: string;
    name: string;
    /** Optional href — wraps the avatar in a link. */
    href?: string;
  }>;
  /** Max avatars to show before folding into "+N more". Default 6. */
  max_visible?: number;
  /** Avatar size in pixels. Default 32. */
  size?: number;
  /** Optional caption below the stack. */
  caption?: string;
}

/**
 * Horizontal strip of vote markers for one member across a series of
 * votes. Each vote = a small colored block (yea = green, nay = red,
 * missed = grey, present = amber). Reads left-to-right as a vote
 * record / scorecard pass.
 *
 * Use for "Sen. Cruz on KOSA scorecard" or "House Republicans on the
 * climate package votes." Pair with a column of these strips for a
 * quick at-a-glance whip-card view.
 */
export interface VoteRecordStripSpec {
  kind: "vote_record_strip";
  /** Optional title above the strip (typically the member name). */
  title?: string;
  /** Bioguide of the member — drives the photo on the leading edge. */
  bioguide?: string;
  /** Display name when bioguide is set (or used standalone). */
  member_name?: string;
  /** Optional party single-letter (D / R / I) for the leading badge. */
  party?: "D" | "R" | "I" | string;
  /** Optional state. */
  state?: string;
  /** Ordered votes — left = oldest, right = newest. */
  votes: Array<{
    /** The member's vote on this roll-call. */
    vote: "yea" | "nay" | "missed" | "present" | "not_eligible";
    /** Optional bill_ref / vote label rendered as a tooltip. */
    label?: string;
    /** Optional date for tooltip. */
    date?: string;
  }>;
  /** Optional aggregate counts shown to the right of the strip. */
  totals?: {
    yea?: number;
    nay?: number;
    missed?: number;
    present?: number;
  };
  /** Optional href to drill into the member. */
  href?: string;
}

/**
 * Bordered placeholder card for "no data here yet" / "nothing to see
 * yet" states. Icon + headline + description + optional next-step hint
 * (the AI surfaces what to ask for in chat).
 *
 * Use everywhere a list/grid/feed could be empty so the principal
 * always knows the recommended next move.
 */
export interface EmptyStateSpec {
  kind: "empty_state";
  /** Optional lucide icon name shown above the headline (e.g. "inbox",
   *  "search"); legacy emoji are mapped, unknown values render nothing. */
  icon?: string;
  /** Headline — required, short. */
  headline: string;
  /** Optional supporting description. */
  description?: string;
  /** Optional hint text — typically the chat prompt the principal
   *  should send next ("Ask me to ingest a briefing"). */
  hint?: string;
  /** Theme — "default" muted, "primary" blue, "success" green, "warning"
   *  amber, "alert" red. */
  tone?: "default" | "primary" | "success" | "warning" | "alert";
}

/**
 * One signal as a focal card — direction, confidence, member, source,
 * provenance, and optional context excerpt. The natural shape for
 * "explain why we think Cruz is leaning no."
 */
export interface SignalCardSpec {
  kind: "signal_card";
  /** Member bioguide — drives the photo. */
  member_bioguide?: string;
  /** Member display name. */
  member_name?: string;
  /** Direction of the signal toward yea / nay. */
  direction: "support" | "lean_support" | "neutral" | "lean_oppose" | "oppose";
  /** Optional bill or issue label this signal pertains to. */
  subject?: string;
  /** Source label (e.g. "press release", "floor statement", "vote", "tip"). */
  source?: string;
  /** Confidence 0–1 — drives a small inline gauge. */
  confidence?: number;
  /** Short context excerpt or summary. */
  context?: string;
  /** Optional source URL. */
  source_href?: string;
  /** Optional date string. */
  date?: string;
  /** Optional approval status — "pending", "approved", "rejected". */
  status?: "pending" | "approved" | "rejected" | "superseded";
}

/**
 * Bill text excerpt with line numbers and optional highlight ranges.
 * Different from prose_card (which is plain markdown). Use for showing
 * a contested clause, a manager amendment, a section the AI is
 * reasoning about.
 */
export interface BillTextExcerptSpec {
  kind: "bill_text_excerpt";
  /** Optional bill ref + section reference for the header. */
  bill_ref?: string;
  /** Optional section heading (e.g. "Sec. 4(a)", "§ 230"). */
  section_label?: string;
  /** Optional title above the section_label. */
  title?: string;
  /** Lines of text. Rendered with monospace + line numbers. */
  lines: string[];
  /** Optional zero-indexed line ranges to highlight (inclusive). */
  highlights?: Array<{ from: number; to: number; tone?: "default" | "primary" | "warning" | "alert" }>;
  /** Optional starting line number — defaults to 1. */
  start_line?: number;
  /** Optional source URL (link to the full text). */
  source_href?: string;
}

/**
 * Single event card — fundraising event, coalition meeting, hill day,
 * markup. Title + date/time + location + attendee count + optional
 * status. Different from `calendar` (which is the grid view).
 */
export interface EventCardSpec {
  kind: "event_card";
  /** Event title. Required. */
  title: string;
  /** Pre-formatted date label (e.g. "Fri May 12 · 3pm"). */
  date?: string;
  /** Optional venue / address / room. */
  location?: string;
  /** Optional attendee count or roster summary. */
  attendees?: string;
  /** Optional status — drives the badge. */
  status?: "scheduled" | "in_progress" | "completed" | "cancelled" | "rsvp_open";
  /** Optional kind icon — lucide icon name (e.g. "mic", "users"); legacy emoji mapped. */
  icon?: string;
  /** Optional description body. */
  description?: string;
  /** Optional tone for the left accent. */
  tone?: "default" | "primary" | "success" | "warning" | "alert";
  /** Optional link to the event detail. */
  href?: string;
}

/**
 * Tag cloud — sized labels weighted by frequency. Use for "what topics
 * are showing up" / "what issues are getting press" / "what tags are
 * trending." Different from badge_row (uniform sizing).
 */
export interface TagCloudSpec {
  kind: "tag_cloud";
  /** Optional title above the cloud. */
  title?: string;
  /** Tags — count drives the visual weight (size + opacity). */
  tags: Array<{
    label: string;
    count: number;
    /** Optional href — wraps the tag in a link. */
    href?: string;
    /** Optional tone — applies to the tag chip. */
    tone?: "default" | "primary" | "success" | "warning" | "alert";
  }>;
  /** Maximum tags to render. Default 30. */
  max?: number;
}

/**
 * Breadcrumb path — context-navigation strip with optional href per
 * crumb. Use when the AI redirects the principal across surfaces and
 * wants to show the path explicitly.
 */
export interface BreadcrumbSpec {
  kind: "breadcrumb";
  /** Crumbs in left-to-right order. The last crumb renders un-linked
   *  even if href is set (it's the current page). */
  crumbs: Array<{
    label: string;
    href?: string;
  }>;
}

/**
 * Single poll display: pollster + sample + dates + question + a row of
 * candidate results with optional margin of error. Reads top-to-bottom
 * as one credible poll datum.
 */
export interface PollCardSpec {
  kind: "poll_card";
  /** Pollster name (e.g. "Quinnipiac", "Marist", "NYT/Siena"). Required. */
  pollster: string;
  /** Optional sponsor (e.g. "Wall Street Journal"). */
  sponsor?: string;
  /** Date range — pre-formatted (e.g. "May 4–7, 2026" or "March 12–15"). */
  date_range?: string;
  /** Sample description (e.g. "1,200 likely voters" or "847 RV"). */
  sample?: string;
  /** Method ("phone", "online", "mixed", or freeform). */
  method?: string;
  /** Margin of error (e.g. "±3.2%"). */
  margin_of_error?: string;
  /** The poll question — short, plain. */
  question?: string;
  /** Candidates / options with their support %. */
  results: Array<{
    label: string;
    /** Percentage support (0–100). */
    pct: number;
    /** Optional party single-letter for color tinting. */
    party?: "D" | "R" | "I" | string;
    /** Optional photo bioguide for incumbents (uses CRM photo proxy). */
    bioguide?: string;
    /** Optional photo URL for non-legislators (challengers, etc.). */
    photo_url?: string;
    /** Optional sub-label (e.g. "Inc.", "Open seat"). */
    sublabel?: string;
  }>;
  /** Optional source URL for the full crosstabs / methodology. */
  source_href?: string;
}

/**
 * Two candidates side-by-side with photos, party badges, polling
 * percentages, and a center spread indicator. The iconic shape for
 * head-to-head election or primary contests. Different from
 * stat_comparison (which is generic two-values) — this primitive
 * carries political identity (party tinting, candidate photos,
 * polling-spread arrow color).
 */
export interface HeadToHeadSpec {
  kind: "head_to_head";
  /** Optional title above the matchup. */
  title?: string;
  /** Race / context label (e.g. "Pennsylvania Senate 2028"). */
  race_label?: string;
  /** Optional date / poll context. */
  context?: string;
  /** Left side candidate. */
  left: {
    name: string;
    party?: "D" | "R" | "I" | string;
    /** Optional sublabel — incumbency, hometown, role. */
    sublabel?: string;
    /** Polling percentage for the visual bar (0–100). */
    pct?: number;
    /** Optional bioguide for CRM photo. */
    bioguide?: string;
    /** Optional explicit photo URL (for non-legislators). */
    photo_url?: string;
    /** Optional href to candidate detail. */
    href?: string;
  };
  /** Right side candidate. */
  right: {
    name: string;
    party?: "D" | "R" | "I" | string;
    sublabel?: string;
    pct?: number;
    bioguide?: string;
    photo_url?: string;
    href?: string;
  };
  /** Optional spread label (e.g. "+4.2 lean L", "tied", "+3 toss-up"). */
  spread?: { label: string; tone?: "default" | "primary" | "alert" | "warning" };
  /** Optional footer line (sample / margin / methodology). */
  footer?: string;
}

/**
 * Single candidate card — for non-incumbent challengers, primary
 * opponents, governor / executive candidates. Different from
 * member_card (which assumes a bioguide-keyed sitting legislator);
 * this takes an explicit photo_url so it works for anyone.
 */
export interface CandidateCardSpec {
  kind: "candidate_card";
  /** Display name. Required. */
  name: string;
  /** Party single-letter (D / R / I). */
  party?: "D" | "R" | "I" | string;
  /** Race label (e.g. "TX-15 Open seat", "Senate primary, 2028"). */
  race?: string;
  /** Optional incumbent flag — drives a tag. */
  is_incumbent?: boolean;
  /** Photo: explicit URL OR bioguide (for incumbents in the CRM). */
  photo_url?: string;
  bioguide?: string;
  /** Optional headline or tagline. */
  headline?: string;
  /** Optional metric strip — usually polling, fundraising, name ID. */
  stats?: Array<{
    label: string;
    value: string | number;
    /** Optional sublabel (delta, period). */
    sublabel?: string;
  }>;
  /** Optional clickable href. */
  href?: string;
}

/**
 * Horizontal partisan lean meter — bar from D+30 (deep blue, left) to
 * R+30 (deep red, right), with a marker at the actual lean value. Use
 * for district lean ("VA-7: D+5 lean"), state lean, or any single-axis
 * partisan measure.
 */
export interface PartisanLeanMeterSpec {
  kind: "partisan_lean_meter";
  /** Optional label above the meter (e.g. "VA-7 partisan lean"). */
  label?: string;
  /** Lean value: negative = D, positive = R. Range typically -30 to +30. */
  lean: number;
  /** Optional max for the scale. Default 30. */
  max?: number;
  /** Optional caption below the meter. */
  caption?: string;
}

/**
 * Three-segment horizontal bar showing favorability split: Favorable /
 * Neutral / Unfavorable percentages stacked end-to-end with an optional
 * net score on the right. Use for any 3-bucket split where total = 100.
 */
export interface FavorabilitySplitSpec {
  kind: "favorability_split";
  /** Optional label / title. */
  label?: string;
  /** Favorable percentage (0–100). */
  favorable: number;
  /** Neutral / no opinion percentage. */
  neutral: number;
  /** Unfavorable percentage. */
  unfavorable: number;
  /** Optional net favorability label override. Default: computed favorable - unfavorable. */
  net_label?: string;
  /** Optional source / sample line. */
  footer?: string;
}

/**
 * Polling crosstabs — demographic rows × candidate columns, each cell a
 * percentage with optional sample size. Different from heatmap (which
 * renders fill colors by intensity); this is structured for displaying
 * percentages that should sum to ~100% across each row.
 */
export interface CrosstabTableSpec {
  kind: "crosstab_table";
  /** Optional title above the table. */
  title?: string;
  /** Optional question or topline. */
  question?: string;
  /** Columns — typically candidates, options, or response categories. */
  columns: Array<{
    label: string;
    /** Optional party tinting. */
    party?: "D" | "R" | "I" | string;
  }>;
  /** Rows — demographic breakdowns or subgroups. */
  rows: Array<{
    /** Group name (e.g. "Age 18–29", "Suburban women"). */
    label: string;
    /** Optional sample size. */
    n?: number;
    /** Percentages aligned to columns. Length must match columns. */
    pcts: number[];
  }>;
  /** Optional footer (source, methodology). */
  footer?: string;
}

/**
 * Electoral-college tally — D / Tossup / R running totals to a
 * threshold (default 270), plus a per-state grid colored by call
 * status. Different from render_choropleth (which is a literal
 * geographic map); this is the iconic election-night tally graphic.
 */
export interface ElectoralMapSpec {
  kind: "electoral_map";
  /** Optional title above the visual. */
  title?: string;
  /** Election year (e.g. "2028"). */
  year?: string;
  /** EC threshold to win. Default 270. */
  threshold?: number;
  /** Per-state results. */
  states: Array<{
    /** 2-letter state abbreviation (or "DC"). */
    abbr: string;
    /** EC votes for the state. */
    electors: number;
    /** Status — drives state cell color. */
    status: "called_d" | "called_r" | "lean_d" | "lean_r" | "tossup" | "no_data";
    /** Optional margin (positive = R lead, negative = D lead). */
    margin?: number;
  }>;
  /** Optional totals override. If absent, computed from states by status. */
  totals?: { d: number; r: number; tossup?: number };
  /** Optional footer (poll source, last update). */
  footer?: string;
}

/**
 * Cook-style race rating: a 7-segment horizontal bar (Solid D / Likely
 * D / Lean D / Tossup / Lean R / Likely R / Solid R) with a marker on
 * the active rating. Different from partisan_lean_meter (which is a
 * continuous lean number) — this is for the discrete competitive
 * categorization used by Cook, Sabato, Inside Elections, etc.
 */
export interface RaceRatingStripSpec {
  kind: "race_rating_strip";
  /** Optional title (e.g. "Pennsylvania Senate 2028"). */
  title?: string;
  /** Active rating bucket. */
  rating: "solid_d" | "likely_d" | "lean_d" | "tossup" | "lean_r" | "likely_r" | "solid_r";
  /** Source organization (e.g. "Cook Political Report", "Sabato"). */
  source?: string;
  /** Date of the rating. */
  date?: string;
  /** Optional caption below. */
  caption?: string;
  /** Optional rating-change indicator. */
  delta?: {
    /** Previous rating bucket. */
    from: "solid_d" | "likely_d" | "lean_d" | "tossup" | "lean_r" | "likely_r" | "solid_r";
    /** Tone for the change indicator. */
    tone?: "primary" | "warning" | "alert";
  };
}

/**
 * Quarterly side-by-side fundraising comparison — left and right
 * candidate, periods as rows, mirrored horizontal bars (left grows
 * leftward, right grows rightward) for direct visual comparison. Use
 * for FEC quarterly raise + COH comparisons.
 */
export interface FundraisingRaceSpec {
  kind: "fundraising_race";
  /** Optional title. */
  title?: string;
  /** Race / context label. */
  race_label?: string;
  /** Left side candidate. */
  left: {
    name: string;
    party?: "D" | "R" | "I" | string;
    bioguide?: string;
    photo_url?: string;
  };
  /** Right side candidate. */
  right: {
    name: string;
    party?: "D" | "R" | "I" | string;
    bioguide?: string;
    photo_url?: string;
  };
  /** Per-period fundraising (Q1 2026, Q2 2026, ...). Latest-first or
   *  earliest-first — caller picks the order. */
  periods: Array<{
    label: string;
    /** Dollars raised by left candidate this period. */
    left_amount: number;
    /** Dollars raised by right candidate this period. */
    right_amount: number;
  }>;
  /** Optional running cycle totals. */
  totals?: { left: number; right: number };
  /** Optional cash-on-hand at end of latest period. */
  coh?: { left: number; right: number };
  /** Optional footer (FEC filing date, source). */
  footer?: string;
}

/**
 * Money-source donut chart — shows where contribution dollars come
 * from (individual donors / PACs / party committees / self-fund / etc.)
 * as an SVG donut with center total + per-segment legend below. Each
 * segment lists label + $ amount + computed %.
 */
export interface DonorBreakdownSpec {
  kind: "donor_breakdown";
  /** Optional title. */
  title?: string;
  /** Optional center label override (default: $ formatted total). */
  total_label?: string;
  /** Optional center sublabel (e.g. "Cycle to date"). */
  total_sublabel?: string;
  /** Funding sources. */
  segments: Array<{
    label: string;
    /** Dollar amount for this segment. */
    amount: number;
    /** Optional tone — drives segment fill color. */
    tone?: "default" | "primary" | "success" | "warning" | "alert" | "accent";
  }>;
  /** Optional footer (filing period, source). */
  footer?: string;
}

/**
 * Visual whip-count: 5- or 6-bucket tally (yes / lean_yes / undecided /
 * lean_no / no / no_response) with member-photo piles inside each
 * bucket, count, and optional pass threshold line. Different from
 * progress_bar (which is single-axis to threshold) — this shows the
 * full distribution and *who* is in each bucket.
 */
export interface WhipCountSummarySpec {
  kind: "whip_count_summary";
  /** Optional title. */
  title?: string;
  /** Bill reference for context (e.g. "S. 1748 (KOSA)"). */
  bill_ref?: string;
  /** Threshold to pass (e.g. 218 House, 60 cloture). */
  threshold?: number;
  /** Threshold label (e.g. "218 to pass", "60 for cloture"). */
  threshold_label?: string;
  /** Members in each bucket — keys are the buckets, values are the
   *  members. The renderer caps visible avatars per bucket and shows
   *  +N more. */
  buckets: {
    yes:          Array<{ bioguide: string; name: string; party?: string }>;
    lean_yes?:    Array<{ bioguide: string; name: string; party?: string }>;
    undecided?:   Array<{ bioguide: string; name: string; party?: string }>;
    lean_no?:     Array<{ bioguide: string; name: string; party?: string }>;
    no?:          Array<{ bioguide: string; name: string; party?: string }>;
    no_response?: Array<{ bioguide: string; name: string; party?: string }>;
  };
  /** Override the bucket totals shown in the header + progress bar +
   *  overflow pill. Use when you know the true count but only have a
   *  representative sample of bioguides — e.g. "73 yes, here's 6
   *  notable ones." If absent, the per-bucket count falls back to
   *  buckets[k].length. */
  total_counts?: {
    yes?: number;
    lean_yes?: number;
    undecided?: number;
    lean_no?: number;
    no?: number;
    no_response?: number;
  };
  /** Optional footer. */
  footer?: string;
}

/**
 * Grouped affiliations — committees, subcommittees, caucuses, working
 * groups, leadership posts. Renders as a single card with one section
 * per affiliation kind, each item carrying an optional role badge
 * (chair / ranking / vice chair / member). Use when answering "what is
 * this member on" or "what working groups overlap on this issue."
 */
export interface AffiliationListSpec {
  kind: "affiliation_list";
  /** Optional title. */
  title?: string;
  /** Optional member context (subject of the affiliations). */
  member_name?: string;
  bioguide?: string;
  /** Affiliation entries. */
  groups: Array<{
    /** Affiliation name. */
    label: string;
    /** Type of affiliation — drives section grouping + icon. */
    kind: "committee" | "subcommittee" | "caucus" | "working_group" | "leadership";
    /** Optional role on the affiliation. */
    role?: "chair" | "ranking" | "vice_chair" | "member";
    /** Optional sublabel (chamber, since-date, etc.). */
    sublabel?: string;
    /** Optional click target. */
    href?: string;
  }>;
  /** Optional footer. */
  footer?: string;
}

/**
 * Horizontal scrolling headline ticker. Use for "what's getting press
 * on this topic right now" — different from news_feed (vertical
 * infinite scroll, more prose) and pullquote (single quote with photo).
 * Each headline carries outlet lean + sentiment dots so the strip
 * reads as a cross-section of coverage.
 */
export interface HeadlineStripSpec {
  kind: "headline_strip";
  /** Optional title. */
  title?: string;
  /** Headlines, displayed left-to-right. */
  headlines: Array<{
    headline: string;
    outlet: string;
    /** Pre-formatted date (e.g. "May 8" or "2h ago"). */
    date?: string;
    href?: string;
    /** Outlet political lean — drives outlet-badge tint. */
    outlet_lean?: "left" | "lean_left" | "center" | "lean_right" | "right" | "unknown";
    /** Sentiment of the coverage toward the subject. */
    sentiment?: "positive" | "neutral" | "negative" | "mixed";
  }>;
  /** Optional footer (source query, sample size). */
  footer?: string;
}

/**
 * Pre-meeting prep brief for a member or contact — photo + role + temperature
 * pill, "their priorities", "your asks", recent history (max 3), optional
 * "watch out" callout. Different from member_card (a profile snapshot) —
 * this is the *operational* brief you walk into a meeting holding.
 */
export interface MeetingPrepCardSpec {
  kind: "meeting_prep_card";
  /** Optional title above the card. */
  title?: string;
  /** Subject of the meeting. */
  subject: {
    name: string;
    /** Sub-role: "Sen.", "Rep.", "COS", "Leg. Director", or freeform. */
    role?: string;
    party?: "D" | "R" | "I" | string;
    state?: string;
    district?: string;
    bioguide?: string;
    photo_url?: string;
  };
  /** Optional meeting metadata. */
  meeting?: {
    /** Pre-formatted date/time. */
    when?: string;
    where?: string;
    duration?: string;
    /** Format: in-person, video, phone. */
    format?: string;
  };
  /** Relationship temperature pill. */
  temperature?: "cold" | "warm" | "hot" | "champion" | "hostile";
  /** Their priorities going into this meeting. */
  their_priorities?: string[];
  /** Your asks for this meeting. */
  your_asks?: string[];
  /** Recent history (up to 3 most recent touches). */
  recent_history?: Array<{
    /** Pre-formatted date. */
    date: string;
    /** Short title. */
    title: string;
    /** Optional outcome. */
    outcome?: string;
  }>;
  /** Optional warning / watch-out callout. */
  watch_out?: string;
  /** Optional footer (prep author, date generated). */
  footer?: string;
}

/**
 * Recorded-vote breakdown — chamber-shaped visualization showing yea/nay
 * by party with seat counts, total per row, and outcome chip
 * (Passed/Failed). Different from whip_count_summary (which is the
 * pre-vote whip projection) — this is the after-the-fact recorded vote.
 */
export interface VoteBreakdownChamberSpec {
  kind: "vote_breakdown_chamber";
  /** Optional title (e.g. "S. 1748 — final passage"). */
  title?: string;
  /** Bill or vote reference. */
  vote_ref?: string;
  /** Pre-formatted date / time of the vote. */
  date?: string;
  /** Outcome chip — drives color tone of the outcome badge. */
  outcome?: "passed" | "failed" | "tabled" | "withdrawn";
  /** Optional outcome label override. */
  outcome_label?: string;
  /** Vote totals broken down by party. The primitive renders rows for
   *  Yea / Nay / Present / Not Voting; each row shows D / R / I splits +
   *  total. Missing rows are omitted. */
  totals: {
    yea?:        { d?: number; r?: number; i?: number };
    nay?:        { d?: number; r?: number; i?: number };
    present?:    { d?: number; r?: number; i?: number };
    not_voting?: { d?: number; r?: number; i?: number };
  };
  /** Optional threshold (e.g. "60 for cloture", "218 for passage"). */
  threshold?: number;
  threshold_label?: string;
  /** Optional footer (margin notes, defectors, etc.). */
  footer?: string;
}

/**
 * 2×2 strategic quadrant chart — entities placed by x/y coordinates with
 * labeled axes. Use for any "alignment vs influence" / "priority vs
 * likelihood" / "support level vs effort to win" matrix. Each entity is a
 * dot with optional label and party tinting.
 */
export interface TargetQuadrantSpec {
  kind: "target_quadrant";
  /** Optional title. */
  title?: string;
  /** X-axis label and bounds. */
  x_axis: {
    label: string;
    min: number;
    max: number;
    /** Optional left/right bound labels (e.g. "Low" / "High"). */
    left_label?: string;
    right_label?: string;
  };
  /** Y-axis label and bounds. */
  y_axis: {
    label: string;
    min: number;
    max: number;
    /** Optional bottom/top bound labels. */
    bottom_label?: string;
    top_label?: string;
  };
  /** Optional quadrant labels (top-left, top-right, bottom-left, bottom-right). */
  quadrant_labels?: { tl?: string; tr?: string; bl?: string; br?: string };
  /** Entities to plot. */
  points: Array<{
    label: string;
    x: number;
    y: number;
    party?: "D" | "R" | "I" | string;
    /** Optional bioguide for member dots — drives photo lookup. */
    bioguide?: string;
    /** Optional href for click target. */
    href?: string;
    /** Optional radius scale 1–3. Default 1. */
    size?: 1 | 2 | 3;
  }>;
  /** Optional footer. */
  footer?: string;
}

/**
 * Coalition sign-on letter card — title + body excerpt + signer piles.
 * Renders org logos in one pile and individual avatars in another, each
 * with overflow indicator. Use when reporting "who signed on to the
 * letter" or recruiting visibility into the existing signers.
 */
export interface CoalitionSignonCardSpec {
  kind: "coalition_signon_card";
  /** Letter title. */
  title: string;
  /** Optional dateline / send date. */
  date?: string;
  /** Optional addressee (e.g. "To: Senate Leadership"). */
  to?: string;
  /** Optional body excerpt — the key paragraph or summary. */
  body?: string;
  /** Organization signers. Logos rendered as avatar circles. */
  org_signers?: Array<{
    name: string;
    /** Logo URL. */
    logo_url?: string;
    href?: string;
  }>;
  /** Individual signers (members, prominent individuals). */
  individual_signers?: Array<{
    name: string;
    bioguide?: string;
    photo_url?: string;
    /** Optional sublabel (party-state, role). */
    sublabel?: string;
    href?: string;
  }>;
  /** Optional source / link to full letter. */
  source_href?: string;
  /** Optional footer. */
  footer?: string;
}

/**
 * District demographics summary card — population, median income,
 * education, race composition, urbanicity, partisan lean. Designed to
 * sit next to a map/choropleth as the "who lives here" gloss. Each stat
 * is optional — omit fields that aren't relevant.
 */
export interface DistrictDemoCardSpec {
  kind: "district_demo_card";
  /** District / state label (e.g. "PA-7", "Pennsylvania"). */
  label: string;
  /** Optional sub-label (e.g. "119th Congress · Lehigh Valley"). */
  sublabel?: string;
  /** Total population. */
  population?: number;
  /** Median household income. */
  median_income?: number;
  /** Percent with bachelor's degree or higher. */
  bachelors_pct?: number;
  /** Race breakdown (percentages, may not sum to 100). */
  race?: {
    white?: number;
    black?: number;
    hispanic?: number;
    asian?: number;
    other?: number;
  };
  /** Urbanicity split (percentages). */
  urbanicity?: {
    urban?: number;
    suburban?: number;
    rural?: number;
  };
  /** Partisan lean — Cook PVI value (negative D, positive R). */
  partisan_lean?: number;
  /** Optional incumbent context. */
  incumbent?: {
    name: string;
    party?: "D" | "R" | "I" | string;
    bioguide?: string;
    /** Last winning margin (percentage points). */
    last_margin?: number;
  };
  /** Optional footer (source, vintage). */
  footer?: string;
}

/**
 * Historical-results strip — past N cycles of margins for a state or
 * district as horizontal mini-bars (negative left blue D, positive right
 * red R). Each cycle shows year + margin label + bar. Use for "how has
 * PA voted in the last 5 presidential cycles" or "this district's
 * trend over the last 4 elections."
 */
export interface HistoricalResultsStripSpec {
  kind: "historical_results_strip";
  /** Optional title (e.g. "PA presidential margins"). */
  title?: string;
  /** Optional sub-context (e.g. "Last 5 cycles · 2-party share"). */
  sublabel?: string;
  /** Per-cycle results. Earliest-first or latest-first (caller picks). */
  cycles: Array<{
    /** Cycle label (e.g. "2024" or "2024 — Pres."). */
    label: string;
    /** Margin (negative = D won, positive = R won). */
    margin: number;
    /** Optional winner name. */
    winner?: string;
    /** Optional turnout / sublabel. */
    sublabel?: string;
  }>;
  /** Optional max bound for bar scaling. Default: max abs margin. */
  max?: number;
  /** Optional footer. */
  footer?: string;
}

/**
 * Talking-points card — structured bullets for a meeting / press call /
 * floor speech. Each point has the headline (the *thing to say*),
 * optional supporting evidence (data, quote, source), and optional
 * "if asked" rebuttal preparation. Different from list (flat bullets)
 * and prose_card (markdown) — this is structured for the prep workflow.
 */
export interface TalkingPointsCardSpec {
  kind: "talking_points_card";
  /** Optional title. */
  title?: string;
  /** Optional context / venue ("Floor speech, May 12"). */
  context?: string;
  /** Talking points — ordered. */
  points: Array<{
    /** The point itself — what to say. */
    point: string;
    /** Optional supporting evidence (data, citation). */
    evidence?: string;
    /** Optional "if asked" rebuttal block. */
    if_asked?: string;
    /** Optional tone for the point bullet. */
    tone?: "default" | "primary" | "warning" | "alert";
  }>;
  /** Optional footer (prep author, version). */
  footer?: string;
}

/**
 * Bill amendment tree — parent/child amendment relationships rendered
 * as an indented hierarchy with sponsor + status badge + vote outcome.
 * Use for legislative deep dives where amendment structure matters
 * (manager amendment + 12 second-degree amendments + en bloc grouping).
 */
export interface BillAmendmentTreeSpec {
  kind: "bill_amendment_tree";
  /** Optional title. */
  title?: string;
  /** Bill reference for context. */
  bill_ref?: string;
  /** Tree of amendments. Each node has children for second-degree, etc. */
  amendments: Array<AmendmentTreeNode>;
  /** Optional footer (sources, freshness). */
  footer?: string;
}
export interface AmendmentTreeNode {
  /** Amendment number (e.g. "SA 2173", "HR 8573 #1"). */
  amendment_ref: string;
  /** Short title or purpose. */
  title?: string;
  /** Sponsor name. */
  sponsor?: string;
  sponsor_party?: "D" | "R" | "I" | string;
  sponsor_bioguide?: string;
  /** Status of this amendment. */
  status?: "pending" | "agreed" | "rejected" | "withdrawn" | "ruled_out_of_order" | "considered_en_bloc";
  /** Optional vote outcome label (e.g. "47-53"). */
  vote_outcome?: string;
  /** Optional date. */
  date?: string;
  /** Optional kind tag — "manager", "second_degree", "perfecting", "substitute". */
  kind_tag?: string;
  /** Children (second-degree amendments to this one). */
  children?: AmendmentTreeNode[];
  /** Optional href to amendment text. */
  href?: string;
}

/**
 * KPI grid — N×M grid of metric tiles, each with label, value, optional
 * delta (with arrow + tone), optional sparkline, optional comparison
 * line. Different from a row of stat_cards (which lays cards in a flat
 * row): kpi_grid is denser, has consistent sizing, and renders
 * deltas/sparklines as a unified visual language.
 */
export interface KpiGridSpec {
  kind: "kpi_grid";
  /** Optional title. */
  title?: string;
  /** Optional time-range / context label. */
  context?: string;
  /** Number of columns. Default 4. */
  cols?: 2 | 3 | 4 | 5 | 6;
  /** KPI tiles. */
  kpis: Array<{
    label: string;
    /** Big-number value (already formatted). */
    value: string | number;
    /** Optional delta string (e.g. "+12.4%", "↓ 3"). */
    delta?: string;
    /** Direction tone for the delta. */
    delta_tone?: "positive" | "negative" | "neutral";
    /** Optional comparison sublabel (e.g. "vs last week"). */
    comparison?: string;
    /** Optional sparkline values. */
    sparkline?: number[];
    /** Optional tone for the tile background tint. */
    tone?: "default" | "primary" | "success" | "warning" | "alert";
    /** Optional click target. */
    href?: string;
  }>;
  /** Optional footer. */
  footer?: string;
}

/**
 * Gap chart — horizontal progress bars with target markers, one row per
 * goal. Each row shows current value as a filled bar, target as a
 * vertical line, gap distance + ETA. Use for "here's where each goal
 * stands and how far we are from target."
 */
export interface GapChartSpec {
  kind: "gap_chart";
  /** Optional title. */
  title?: string;
  /** Optional sublabel (period covered). */
  sublabel?: string;
  /** Goals being tracked. */
  goals: Array<{
    label: string;
    /** Current value. */
    current: number;
    /** Target value. */
    target: number;
    /** Optional unit suffix (e.g. "%", "$M", "members"). */
    unit?: string;
    /** Optional ETA / pace label (e.g. "on pace · Jun 12"). */
    eta?: string;
    /** Optional tone — drives bar color. */
    tone?: "default" | "primary" | "success" | "warning" | "alert";
  }>;
  /** Optional footer. */
  footer?: string;
}

/**
 * Engagement funnel card — declarative version of render_funnel for
 * inline composition. Stages from top (widest) to bottom (narrowest),
 * each with a label, count, and computed conversion % from previous
 * stage. Use for the relationship-building progression: aware →
 * engaged → committed → champion → activist.
 */
export interface EngagementFunnelCardSpec {
  kind: "engagement_funnel_card";
  /** Optional title. */
  title?: string;
  /** Optional sublabel (period). */
  sublabel?: string;
  /** Stages from widest (top) to narrowest (bottom). */
  stages: Array<{
    label: string;
    count: number;
    /** Optional explicit conversion percent override. */
    conversion_pct?: number;
    /** Optional tone — drives bar color. */
    tone?: "default" | "primary" | "success" | "warning" | "alert";
    /** Optional click target. */
    href?: string;
  }>;
  /** Optional footer (definitions, source). */
  footer?: string;
}

/**
 * Press release card — full press-release layout with title, dateline,
 * lede paragraph, body paragraphs, boilerplate, and contact block.
 * Different from article_summary (which is a *reader-facing* press
 * card describing third-party coverage); this is the *author-facing*
 * shape — the press release you're about to send.
 */
export interface PressReleaseCardSpec {
  kind: "press_release_card";
  /** Optional pre-headline tag (e.g. "FOR IMMEDIATE RELEASE"). */
  tag?: string;
  /** Headline. */
  headline: string;
  /** Optional sub-headline. */
  subheadline?: string;
  /** Pre-formatted dateline (e.g. "WASHINGTON, D.C. — May 9, 2026"). */
  dateline?: string;
  /** Lede paragraph (the first paragraph). */
  lede: string;
  /** Optional additional body paragraphs. */
  body?: string[];
  /** Optional embedded quotes — name + role + quote text. */
  quotes?: Array<{
    speaker: string;
    role?: string;
    quote: string;
  }>;
  /** Optional boilerplate paragraph (about the org, standing). */
  boilerplate?: string;
  /** Optional contact block. */
  contact?: {
    name: string;
    title?: string;
    email?: string;
    phone?: string;
  };
  /** Optional status pill (drafted/approved/sent). */
  status?: "drafted" | "in_review" | "approved" | "sent";
  /** Optional footer. */
  footer?: string;
}

/**
 * Decision matrix — N options × M criteria scored, with optional
 * criterion weights and a computed weighted-total column. Highest score
 * gets a "★ recommended" tag. Use for any structured decision support:
 * scheduling tradeoffs, candidate endorsement comparisons, vendor
 * selection, target-list prioritization.
 */
export interface DecisionMatrixSpec {
  kind: "decision_matrix";
  /** Optional title. */
  title?: string;
  /** Optional question / decision being made. */
  question?: string;
  /** Criteria — column headers. Score scale assumed 0–10. */
  criteria: Array<{
    label: string;
    /** Optional weight (default 1). Rendered as "×N" in the header. */
    weight?: number;
  }>;
  /** Options being scored. */
  options: Array<{
    label: string;
    /** Optional sublabel. */
    sublabel?: string;
    /** Per-criterion scores (length must match criteria.length). */
    scores: number[];
    /** Optional notes per option. */
    note?: string;
  }>;
  /** Optional override of which option is recommended. */
  recommended_index?: number;
  /** Optional footer. */
  footer?: string;
}

/**
 * Donor profile card — donor name, tier badge, giving history
 * sparkline, last gift, lifetime total, employer/occupation, optional
 * suggested ask. Use as the "who is this donor" focal card on a
 * donor-detail surface or in development outreach prep.
 */
export interface DonorCardSpec {
  kind: "donor_card";
  /** Donor display name. */
  name: string;
  /** Optional tier badge label (e.g. "Major", "Mid", "Grassroots"). */
  tier?: string;
  /** Optional tier tone. */
  tier_tone?: "default" | "primary" | "success" | "warning" | "accent";
  /** Optional employer. */
  employer?: string;
  /** Optional occupation / title. */
  occupation?: string;
  /** Optional city/state. */
  location?: string;
  /** Optional photo. */
  photo_url?: string;
  /** Optional party affiliation (D/R/I or freeform). */
  party?: "D" | "R" | "I" | string;
  /** Last gift summary. */
  last_gift?: { amount: number; date: string; designation?: string };
  /** Lifetime total. */
  lifetime_total?: number;
  /** Cycle total (e.g. this election cycle). */
  cycle_total?: number;
  /** Per-period giving history (for the sparkline). */
  giving_history?: number[];
  /** Optional suggested ask amount + label. */
  suggested_ask?: { amount: number; reason?: string };
  /** Optional notes. */
  notes?: string;
  /** Optional click target. */
  href?: string;
  /** Optional footer. */
  footer?: string;
}

/**
 * State delegation overview — senators on top in a row, then a grid of
 * representatives sorted by district, party-color borders, optional
 * leadership / committee-chair / ranking / freshman role badges,
 * party-split summary in the header. Different from member_card (single
 * member); this is the state-as-a-whole shape.
 */
export interface DelegationMapCardSpec {
  kind: "delegation_map_card";
  /** Optional title (e.g. "Pennsylvania delegation"). */
  title?: string;
  /** State (label or 2-letter abbr). */
  state: string;
  /** Optional sub-context (e.g. "119th Congress · 17 reps + 2 sens"). */
  state_label?: string;
  /** Senators (typically 2). */
  senators?: Array<{
    bioguide: string;
    name: string;
    party?: "D" | "R" | "I" | string;
    /** Senior / junior senator. */
    role?: "senior" | "junior";
    /** Optional context (committee, since-year). */
    sublabel?: string;
    href?: string;
  }>;
  /** Representatives. Render order is by district. */
  representatives?: Array<{
    bioguide: string;
    name: string;
    /** District number (string for "AL", "00", "01"). */
    district: string;
    party?: "D" | "R" | "I" | string;
    /** Special role badge. */
    role?: "leadership" | "committee_chair" | "ranking" | "freshman";
    sublabel?: string;
    href?: string;
  }>;
  /** Optional party split summary. */
  party_split?: { d: number; r: number; i: number };
  /** Optional footer. */
  footer?: string;
}

/**
 * Cosponsor growth chart — stacked-area sparkline of cumulative D / R /
 * I cosponsor counts over time, with optional threshold line (e.g. 218
 * House majority) and annotations for key events (introduction, markup,
 * floor action). Use for bill-momentum visualization.
 */
export interface CosponsorGrowthChartSpec {
  kind: "cosponsor_growth_chart";
  /** Optional title. */
  title?: string;
  /** Bill reference for context. */
  bill_ref?: string;
  /** Time series — each point is a snapshot of cumulative counts. */
  series: Array<{
    /** Pre-formatted date label (e.g. "Mar 14"). */
    date: string;
    /** Cumulative D cosponsors. */
    d_count: number;
    /** Cumulative R cosponsors. */
    r_count: number;
    /** Cumulative I cosponsors (optional). */
    i_count?: number;
  }>;
  /** Optional threshold (e.g. 218 House, 51 Senate). */
  threshold?: number;
  /** Optional threshold label. */
  threshold_label?: string;
  /** Optional annotations — vertical lines + label callouts. */
  annotations?: Array<{
    /** Date — must match a series.date for the line to land correctly. */
    date: string;
    /** Annotation label. */
    label: string;
    tone?: "default" | "primary" | "warning" | "alert";
  }>;
  /** Optional footer. */
  footer?: string;
}

/**
 * Radar / spider chart — N-axis polygon with values plotted on each
 * dimension, optional target polygon overlay for "where we are vs
 * where we should be" framing. Use for multi-dimensional alignment
 * scoring (member vs issue portfolio, candidate vs voter values),
 * organization positioning, brand alignment, etc.
 */
export interface MissionAlignmentRadarSpec {
  kind: "mission_alignment_radar";
  /** Optional title. */
  title?: string;
  /** Optional subject label (e.g. "Sen. Tillis", "Common Sense Media"). */
  subject?: string;
  /** Dimensions / axes (3-8 recommended). */
  dimensions: Array<{
    label: string;
    /** Value 0-scale. */
    value: number;
    /** Optional target value for overlay. */
    target?: number;
    /** Optional tone. */
    tone?: "default" | "primary" | "alert";
  }>;
  /** Optional max scale. Default 100. */
  scale?: number;
  /** Optional footer. */
  footer?: string;
}

/**
 * Member position diff — N rows showing one member's position across
 * different signal sources (public statement, voting record,
 * cosponsorship, whip-count input, constituent letters), with each row
 * tagged with a position chip (support/lean_support/neutral/lean_oppose
 * /oppose/no_record) and optional context. Highlights inconsistencies
 * (e.g. publicly supports but voted against). Different from
 * comparison_table (generic N×M) — this is structured for the
 * specific position-tracking workflow.
 */
export interface MemberPositionDiffSpec {
  kind: "member_position_diff";
  /** Optional title. */
  title?: string;
  /** Bill / issue reference. */
  bill_ref?: string;
  /** Subject member. */
  member: {
    name: string;
    party?: "D" | "R" | "I" | string;
    bioguide?: string;
    state?: string;
  };
  /** Position rows — one per signal source. */
  rows: Array<{
    /** Source label (e.g. "Public statement", "Voting record"). */
    label: string;
    /** Position taken on this source. */
    value: "support" | "lean_support" | "neutral" | "lean_oppose" | "oppose" | "no_record";
    /** Optional detail / quote / vote outcome. */
    detail?: string;
    /** Optional date. */
    date?: string;
    /** Optional source URL. */
    source_href?: string;
  }>;
  /** Optional footer. */
  footer?: string;
}

/**
 * Endorsement card — endorser (org or individual) endorsing an
 * endorsee (candidate, official, ballot measure). Logo / photo on the
 * left, arrow, photo / name on the right; optional pull-quote
 * statement, date, status pill, related media coverage list.
 */
export interface EndorsementCardSpec {
  kind: "endorsement_card";
  /** Optional context title. */
  title?: string;
  /** The endorser. */
  endorser: {
    name: string;
    /** Logo URL (orgs) or photo URL (individuals). */
    logo_url?: string;
    /** Optional role / title. */
    role?: string;
    /** Type — drives the visual treatment. */
    type?: "org" | "individual" | "publication" | "elected";
  };
  /** The endorsee. */
  endorsee: {
    name: string;
    bioguide?: string;
    photo_url?: string;
    /** Race / role context. */
    sublabel?: string;
    party?: "D" | "R" | "I" | string;
  };
  /** Optional endorsement statement. */
  statement?: string;
  /** Pre-formatted endorsement date. */
  date?: string;
  /** Status pill. */
  status?: "rumored" | "confirmed" | "withdrawn";
  /** Optional related media coverage. */
  media_coverage?: Array<{
    outlet: string;
    headline: string;
    href?: string;
  }>;
  /** Optional source / link to full endorsement. */
  source_href?: string;
  /** Optional footer. */
  footer?: string;
}

/**
 * Event RSVP card — 4 buckets (confirmed / maybe / declined /
 * no_response) with member-photo piles per bucket, count, and capacity
 * indicator. Different from event_card (which is event metadata) and
 * whip_count_summary (which is a vote whip) — this is the operational
 * RSVP board for hill days, fundraisers, hearings.
 */
export interface EventAttendanceCardSpec {
  kind: "event_attendance_card";
  /** Optional title. */
  title?: string;
  /** Event name (e.g. "May 12 KOSA Hill Day"). */
  event_label?: string;
  /** Pre-formatted event date. */
  date?: string;
  /** RSVP buckets. */
  buckets: {
    confirmed?:   Array<{ bioguide?: string; name: string; photo_url?: string; party?: string }>;
    maybe?:       Array<{ bioguide?: string; name: string; photo_url?: string; party?: string }>;
    declined?:    Array<{ bioguide?: string; name: string; photo_url?: string; party?: string }>;
    no_response?: Array<{ bioguide?: string; name: string; photo_url?: string; party?: string }>;
  };
  /** Optional venue capacity. */
  capacity?: number;
  /** Optional footer. */
  footer?: string;
}

/**
 * Scorecard strip — list of members with their letter grade on a
 * scorecard. Each row: member photo + name + party-state-district +
 * big colored letter grade + optional 0-100 score + optional sublabel.
 * Use for "how did the delegation grade out on our scorecard."
 */
export interface ScorecardStripSpec {
  kind: "scorecard_strip";
  /** Optional title. */
  title?: string;
  /** Scorecard label (e.g. "Children's Safety Scorecard 2026"). */
  scorecard_label?: string;
  /** Members — render order preserved. */
  members: Array<{
    bioguide: string;
    name: string;
    party?: "D" | "R" | "I" | string;
    state?: string;
    district?: string;
    /** Letter grade. */
    grade: "A+" | "A" | "A-" | "B+" | "B" | "B-" | "C+" | "C" | "C-" | "D+" | "D" | "D-" | "F" | "INC";
    /** Optional 0-100 score. */
    score?: number;
    /** Optional sublabel (key issue, year). */
    sublabel?: string;
    href?: string;
  }>;
  /** Optional footer. */
  footer?: string;
}

/**
 * Recipe reference — placeholder node that compose_panel resolves
 * server-side by looking up a saved panel_recipe by name and
 * substituting {{var}} placeholders from `params` into the recipe's
 * spec tree. Recipes are workspace/org/system scoped and expanded at
 * write time; the renderer SHOULD NEVER see a recipe node in
 * production. If it does (debug, mid-migration, or bug), we render a
 * visible "unexpanded recipe" marker so the issue is loud, not silent.
 */
/**
 * Live data binding. The server replaces it with the source's current
 * output (as the viewer) before a living report or page reaches the
 * renderer; seeing one here means it was not resolved (e.g. a preview).
 */
export interface DataSpec {
  kind: "data";
  tool: string;
  params?: Record<string, unknown>;
  as?: string;
  title?: string;
}

export interface RecipeSpec {
  kind: "recipe";
  /** Name of the recipe to expand. Looked up workspace → org → system. */
  recipe_name: string;
  /** Param values to bind into the recipe's {{placeholders}}. */
  params?: Record<string, unknown>;
}

/**
 * Cosponsor influence map — composite primitive that renders a full
 * relationship analysis for one member in a single spec line. Fetches
 * /api/v1/cosponsor-influence-network/:bioguide and renders pattern
 * mix + coordinators + short-order leaders + cascade leaders + cascade
 * followers + non-listeners as separate sections.
 *
 * Replaces the ~150-line hand-composed influence panel with one
 * declarative node. The data refreshes every 15 minutes upstream
 * (pg_cron on the cmcp_cosponsor_influence_network view).
 */
export interface CosponsorInfluenceMapSpec {
  kind: "cosponsor_influence_map";
  /** Bioguide of the anchor member to analyze. */
  bioguide: string;
  /** Congress to scope the analysis to. Defaults to 119. */
  congress?: number;
  /** Subset of sections to render. Defaults to all. */
  sections?: Array<
    | "pattern_mix"
    | "coordinators"
    | "short_order_leaders"
    | "cascade_leaders"
    | "cascade_followers"
    | "non_listeners"
  >;
  /** Max chips per section. Default 8. */
  limit_per_section?: number;
}

/**
 * Cosponsor avalanche bench — focused "the N people you need to get
 * to net X as a cosponsor." Picks the top-N short-order leaders
 * (median ≤ 7 days) of the anchor, ordered by predictive lift, with
 * operational framing ("recruit these first, the anchor follows").
 */
export interface CosponsorAvalancheBenchSpec {
  kind: "cosponsor_avalanche_bench";
  /** Bioguide of the target member to recruit. */
  bioguide: string;
  /** Congress to scope the analysis to. Defaults to 119. */
  congress?: number;
  /** Number of "must-get" leaders to surface. Default 5. */
  target_size?: number;
  /** Max median gap (days) to qualify as short-order. Default 7. */
  max_median_days?: number;
}

/**
 * Bill cosponsor avalanche — time-aware projection for a specific bill.
 * Fetches /api/v1/bill-cosponsor-projection/:billRef and renders:
 *   - The current cosponsor timeline (sponsor + cosponsors with join dates)
 *   - Predicted next cosponsors, bucketed by status:
 *     · in-window  — typical follow-time still open (cold ask, may already be moving)
 *     · overdue    — typical window just closed (warm ask, may need a nudge)
 *     · late       — long past typical window (probably not coming)
 *   - For each prediction: who their strongest current-cosponsor leader is + the gap
 *
 * Use on bill_detail pages or wherever you want "who's missing from this bill"
 * intelligence. Auto-refreshes every 60 seconds.
 */
export interface BillCosponsorAvalancheSpec {
  kind: "bill_cosponsor_avalanche";
  /** Bill ref like "119-s-1748". */
  bill_ref: string;
  /** Sections to render. Defaults to all. */
  sections?: Array<"timeline" | "in_window" | "overdue" | "late">;
  /** Max predictions per status bucket. Default 8. */
  limit_per_bucket?: number;
}

/**
 * Bill reactivation plan — the consultant deliverable for stalled or
 * saturated bills. Takes the same /api/v1/bill-cosponsor-projection
 * data the avalanche primitive uses and reframes it as an operational
 * plan: diagnosis (where the bill is stuck), key holdouts (members
 * whose stand-with cohort — peers they reliably stand with by both
 * time signal and percentage signal — is on the bill while they are
 * not, with cohort quorum ≥60%), first-call list (concrete recruitment
 * targets), and reactivation tactics.
 *
 * Use on bill_detail pages for any bill that's stalled (saturated or
 * sitting in committee). The avalanche primitive is the analyst tool;
 * this is the client-facing strategy doc.
 */
export interface BillReactivationPlanSpec {
  kind: "bill_reactivation_plan";
  /** Bill ref like "119-s-1748". */
  bill_ref: string;
  /** Top N key holdouts to call out by name. Default 8. */
  holdout_limit?: number;
  /** Top N first-call members to recommend. Default 6. */
  first_call_limit?: number;
}

/**
 * Bill cascade simulator — forward-projection of cosponsor waves.
 * Two modes:
 *   - Real bill: takes bill_ref, uses actual sponsor as Wave 0,
 *     simulates waves forward, compares to actual cosponsors
 *     (annotating each predicted member as as-predicted / slightly-late
 *     / late / missing).
 *   - Hypothetical: takes a sponsor bioguide (+ optional seeds),
 *     projects what the cascade would look like if introduced.
 * Plus: predicted blockers — sponsor's high-overlap-but-no-cascade
 * peers, who would likely not join. The structural absences you can
 * see on day zero, before the bill is even introduced.
 */
export interface BillCascadeSimulatorSpec {
  kind: "bill_cascade_simulator";
  /** Real bill mode — takes precedence over `sponsor` if both set. */
  bill_ref?: string;
  /** Hypothetical mode — sponsor bioguide. Required if no bill_ref. */
  sponsor?: string;
  /** Optional additional seed cosponsors at day 0 (comma-separated bioguides). */
  seed?: string;
  /** Max number of waves to project. Default 4. */
  max_waves?: number;
  /** Congress to scope to. Default 119. */
  congress?: number;
}

/**
 * Bill obstacle network — the gatekeepers blocking this bill's passage,
 * ranked by COMMITTEE POWER × NETWORK DISTANCE from the sponsor.
 *
 * For any bill, identifies its referral committee(s), pulls all
 * committee members, filters out the cosponsors (not obstacles), and
 * ranks the rest by:
 *   role weight:   Chair (10) > RM (6) > Vice Chair (4) > Member (1)
 *   cascade anomaly: in sponsor's network but zero signal (+3) /
 *                    has signal with sponsor (-2)
 * The chair-who-is-also-a-voter-of-silence is the single highest-
 * leverage obstacle — they control the gate AND aren't responsive to
 * the sponsor's typical signals.
 */
export interface BillObstacleNetworkSpec {
  kind: "bill_obstacle_network";
  /** Bill ref like "119-s-1748". */
  bill_ref: string;
  /** Max obstacles to show. Default 15. */
  limit?: number;
}

/**
 * Procedural state strip — single dense line summarizing where a bill
 * sits in the legislative lifecycle. Replaces the three-stat-card
 * pattern (days stalled / stage / actions) that became dashboard
 * chartjunk on bill state-of-play pages.
 *
 * One row of typography with semantic structure: stage + days-since-
 * introduction + total actions joined by middle dots; secondary line
 * carries the procedural locus + action breakdown in smaller stone.
 * Tone (default/warning/alert) auto-derives from days_since_introduction
 * when not overridden — ≥60d → warning, ≥180d → alert.
 *
 * Use as the top-of-page header on any bill state-of-play surface in
 * place of stat_card / kpi_grid for procedural facts.
 */
export interface ProceduralStateStripSpec {
  kind: "procedural_state_strip";
  /** Optional bill / measure ref label, rendered as a small-caps
   *  leader to the left of the stage. */
  bill_ref?: string;
  /** Current lifecycle stage: "Introduced", "Markup", "Reported",
   *  "Floor", "Passed Chamber", "Conferenced", "Enrolled", etc. */
  stage: string;
  /** Procedural locus — the committee / chamber that currently holds
   *  the bill, with status hint. e.g. "Senate Commerce — no markup". */
  locus?: string;
  /** Days since introduction. Drives auto-tone unless overridden. */
  days_since_introduction?: number;
  /** Total legislative actions to date (read + referred + markup +
   *  amendments + floor + etc.). */
  total_actions?: number;
  /** Optional action-type breakdown rendered as middle-dot-joined
   *  secondary text. e.g.
   *  [{label: "read+referred", count: 2}, {label: "amendments", count: 0}] */
  action_breakdown?: Array<{
    label: string;
    count?: number;
  }>;
  /** Optional tone override. Default auto-tones by days_since_introduction. */
  tone?: "default" | "warning" | "alert";
}

/**
 * Precedent anchor — comparable-case strip. Codifies the "we've seen
 * this movie before" pattern as a first-class primitive. Each entry
 * is a comparable bill with its duration-at-stage + outcome, plus a
 * rationale for similarity. Closes with an implication line tying the
 * precedent back to the current case.
 *
 * Replaces freehand prose like "Procedural precedent: Women's Sports
 * Act (HR-28) sat 477 days; companion S.9 cloture failed 51-45.
 * KOSA's structural risk is identical." — that's good analysis but
 * narrative-shaped, which makes it easy to skim past. The structured
 * primitive enforces visual rhythm and makes the comparison legible
 * at a glance.
 */
export interface PrecedentAnchorSpec {
  kind: "precedent_anchor";
  /** Section caption above the precedents. Default "Procedural precedent". */
  caption?: string;
  /** 1–4 comparable bills with their outcomes. */
  precedents: Array<{
    /** Bill ref (e.g. "118-hr-28") or a human label if no ref. */
    bill_ref: string;
    /** Optional bill name / short title. */
    name?: string;
    /** Days the bill sat at the analogous stage. */
    days_at_stage?: number;
    /** Brief outcome: "died in committee", "passed Senate 51-45". */
    outcome: string;
    /** Optional tone — alert when the precedent predicts a poor
     *  outcome, success when it predicts a good one. */
    tone?: "default" | "warning" | "alert" | "success";
    /** Optional rationale for why this is structurally similar
     *  ("same committee + same sponsor pattern"). */
    rationale?: string;
  }>;
  /** Optional implication line ("KOSA's structural risk is identical
   *  — political alignment doesn't move a bill that isn't on the
   *  floor"). Substrate-grounded; not freehand AI narration. */
  implication?: string;
}

/**
 * Leverage points — verb-led action list for a stalled situation.
 * Replaces the wall-of-text "Leverage points:" bullet list that
 * accumulates at the bottom of bill state-of-play prose.
 *
 * Each point is an actionable lever: a verb-led action line, an
 * optional target entity rendered as an EntityRef chip, and an
 * optional rationale on a second line. Substrate emits the leverage
 * map (who has scheduling authority, what committee holds the bill,
 * what House companion exists); AI orders them by priority.
 */
export interface LeveragePointsSpec {
  kind: "leverage_points";
  /** Title above the list. Default "Leverage points". */
  title?: string;
  /** Ordered actions, highest-leverage first. */
  points: Array<{
    /** Verb-led action line ("Reach Schumer's scheduling office —
     *  he's an original cosponsor"). */
    action: string;
    /** Optional target entity, surfaced as an EntityRef chip after
     *  the action line. */
    target?: {
      kind: "member" | "contact" | "bill" | "account" | "coalition" | "committee";
      id: string;
      name?: string;
    };
    /** Optional rationale on a second line. */
    rationale?: string;
    /** Optional priority tag — drives a leading numeral / tone. */
    priority?: "p1" | "p2" | "p3";
  }>;
  /** Optional footer. */
  footer?: string;
}

/**
 * Parliament chart — semicircle dot array where each dot is a single
 * legislator. The shape that makes "who controls / where everyone
 * stands" visible at a glance: every seat is a tangible cell instead
 * of an abstract bar segment.
 *
 * Powers many surfaces with one primitive:
 *   - Whip count for a bill: status drives color (yes / lean_y /
 *     undecided / lean_n / no / absent); Key Holdouts get an amber
 *     highlight ring with the ▽ marker.
 *   - Party control of a chamber: party drives color, no status.
 *   - Vote breakdown: each member's vote is the status.
 *   - Committee composition / caucus membership: highlight tags
 *     mark chairs, ranking members, freshmen, etc.
 *
 * Seat layout: rows of concentric arcs. Senate = 4 rows of 25, House
 * = 8 rows averaging ~54, custom auto-derives by total count. Seats
 * are placed by angular position so the array order (left → right)
 * survives the layout — pass seats sorted in the order you want them
 * to appear (Dems left, Reps right, Independents middle is typical).
 *
 * Each seat with a bioguide carries the legislator's ambient signal
 * via the framework's AmbientSignalRegistry — at the parliament-chart
 * scale that just means a per-seat <title> tooltip with the name +
 * party + status, but the slot is wired so future hover-cards drop in
 * with no per-renderer work.
 */
export interface ParliamentChartSpec {
  kind: "parliament_chart";
  /** Chamber size hint — drives row count + dot scale. */
  chamber: "senate" | "house" | "custom";
  /**
   * Congress number — used by the hydrator when seats is omitted. The
   * hydrator only honors this in conjunction with chamber="senate" or
   * "house". Defaults to the current congress when unset.
   */
  congress?: number;
  /**
   * Partial overlay: per-bioguide whip / vote status. Applied by the
   * hydrator on top of the auto-fetched roster. Members not in the
   * map render with no status (party-color fallback). Ignored when
   * `seats` is explicitly provided.
   */
  status_by_bioguide?: Record<
    string,
    "yes" | "lean_y" | "undecided" | "lean_n" | "no" | "absent" | "vacant"
  >;
  /**
   * Partial overlay: per-bioguide highlight ring. Applied by the
   * hydrator alongside status_by_bioguide. Ignored when `seats` is
   * explicitly provided.
   */
  highlights_by_bioguide?: Record<
    string,
    "holdout" | "champion" | "target" | "leader"
  >;
  /**
   * Seats in left-to-right placement order. For a whip-count visual,
   * sort by status: yes → lean_y → undecided → lean_n → no, then no
   * status differentiation reflows naturally. For party control,
   * sort: D → I → R.
   *
   * Optional — when omitted and `chamber` is "senate" or "house", the
   * compose_panel server-side hydrator fetches the live roster from
   * api_members, sorts D → I → R, fills the chamber's fixed slot
   * count (Senate=100, House=435), pads vacancies, and applies the
   * status/highlight overlays. Vacancies render as outline-only
   * cells. For `chamber: "custom"`, seats is required (no hydration).
   */
  seats?: Array<{
    /** Optional legislator identity. Drives the per-seat tooltip. */
    bioguide?: string;
    /** Optional display name for the tooltip. */
    name?: string;
    /** Party — used as fallback color when no status is set. */
    party?: "D" | "R" | "I";
    /**
     * Whip / vote status. When set, drives seat color and overrides
     * party color. yes/lean_y → green tones, undecided → stone,
     * lean_n/no → red tones, vacant → outline-only, absent → muted.
     */
    status?:
      | "yes"
      | "lean_y"
      | "undecided"
      | "lean_n"
      | "no"
      | "vacant"
      | "absent";
    /**
     * Highlight ring tag — overlays a colored ring + glyph on the
     * seat. holdout = the cohort_gap / key_holdout marker (▽ amber),
     * champion = designated champion (◆ emerald), target = active
     * recruitment target (○ indigo), leader = committee chair / whip
     * (★ violet).
     */
    highlight?: "holdout" | "champion" | "target" | "leader";
  }>;
  title?: string;
  /** Short subtitle / framing line. */
  subtitle?: string;
  /** Footer caption below the chart. */
  caption?: string;
}

/**
 * Diagnosis panel — the large diagnostic frame that names what's
 * politically happening on a bill / situation in one punchy line.
 * Codifies the "Schumer Paradox" pattern from KOSA's state-of-play
 * surface ("The Senate Majority Leader is an original cosponsor.
 * And won't schedule a vote."). Replaces freehand hero + prose_card
 * stitching that lacks visual rhythm.
 *
 * Visual structure: small-caps caption (the diagnostic name) → setup
 * observation (smaller, color-tinted) → punch line (large, bold, tone-
 * driven color) → optional body prose for the structural explanation
 * → optional fact pills (the contradictions, scored cleanly).
 *
 * Tone-driven palette: alert (red) for the egregious contradictions
 * (Schumer Paradox), warning (amber) for less-dramatic surface
 * tensions, primary (blue) for affirmative-but-stalled patterns,
 * default (stone) for neutral.
 *
 * Use one per state-of-play surface as the headline diagnostic; pair
 * with procedural_state_strip above (the dry facts) and Key Holdouts
 * / precedent_anchor / leverage_points below (the operational
 * response).
 */
export interface DiagnosisPanelSpec {
  kind: "diagnosis_panel";
  /** Small-caps caption above the diagnosis (e.g. "THE SCHUMER
   *  PARADOX"). Optional but recommended — it's how the operator
   *  remembers the framing. */
  caption?: string;
  /** Setup line — the observation that frames the punch. */
  observation: string;
  /** The diagnostic punch — large, tone-colored. */
  punch: string;
  /** Optional body prose: the structural explanation behind the
   *  punch. Keep short — two sentences max. */
  body?: string;
  /** Optional fact pills rendered as a badge row at the bottom. */
  facts?: Array<{
    label: string;
    value?: string;
    tone?: "default" | "primary" | "alert" | "warning";
  }>;
  /** Tone of the panel as a whole — drives border, background tint,
   *  and punch color. Default "alert" since this primitive's most
   *  common use is naming contradictions. */
  tone?: "default" | "primary" | "warning" | "alert";
}

/**
 * Committee markup summary — compact card surfacing committee-stage
 * amendments and the final markup vote for a bill. Fetches from
 * /api/v1/workspaces/:wsId/bills/:bill_ref/committee-reports.
 *
 * Distinct from the full CommitteeReportsSection on the Amendments
 * tab — this is the bill-detail-overview / state-of-play variant:
 * one-line summary per report + top 5 amendments with vote results,
 * link to the full report PDF.
 *
 * Empty-state honest: when no committee report has been ingested
 * (no markup yet, or the report ingest stream — FB-128D849D —
 * hasn't shipped), renders a one-line "no committee markup
 * recorded" rather than hiding silently. The substrate doesn't lie
 * about what it has.
 */
export interface CommitteeMarkupSummarySpec {
  kind: "committee_markup_summary";
  bill_ref: string;
  /** Max amendments to surface in the inline list. Default 5; cap 12. */
  limit?: number;
  /** Optional title override. Default "Committee markup". */
  title?: string;
}

/**
 * Vertical timeline of events — date column, icon dot on a continuous
 * line, title + optional subtitle. Use for bill lifecycles, member
 * engagement history, whip-count change logs, and any "what happened
 * when" narrative.
 */
export interface TimelineSpec {
  kind: "timeline";
  /** Optional title above the timeline. */
  title?: string;
  /** Ordered events. Earliest-first or latest-first — the AI picks based
   *  on the story it's telling; the renderer doesn't sort. */
  items: Array<{
    /** Event title — should fit on one line. */
    title: string;
    /** Date / time string. Rendered verbatim — pass it pre-formatted
     *  (e.g. "May 9", "2026-05-09 09:14") for the right level of detail. */
    date?: string;
    /** Optional secondary line for context. */
    subtitle?: string;
    /** Optional icon — lucide icon name (renders beside the dot on the line); legacy emoji mapped. */
    icon?: string;
    /** Optional href — wraps the row in a link. */
    href?: string;
    /** Optional tone — sets the dot color. */
    tone?: "default" | "success" | "warning" | "alert" | "primary";
  }>;
  /** "compact" hides subtitles. Default density shows them. */
  density?: "default" | "compact";
}

// ── Renderer ───────────────────────────────────────────────────────────

// Detect a bill_ref pattern inside arbitrary text — e.g. "HR 7640" /
// "S 1748" / "HJRES 12". Returns an /app/bills/:ref href when found,
// null otherwise. Used by list and prose-style renderers to make
// AI-composed text auto-link to bill detail pages without requiring
// the AI to remember to set an href on every row.
//
// Defaults to congress 119 (current). When data carries an explicit
// congress prefix ("119-hr-7640") it's accepted as-is.
const BILL_TYPE_TOKENS = [
  "hjres",
  "sjres",
  "hconres",
  "sconres",
  "hres",
  "sres",
  "hr",
  "s",
];
const BILL_REF_RE = new RegExp(
  `\\b(?:(1[0-9]{2})[\\-\\s])?(${BILL_TYPE_TOKENS.join("|")})[\\.\\s]+?(\\d+)\\b`,
  "i",
);
function detectBillRefHref(text: string | undefined | null): string | null {
  if (!text) return null;
  const m = text.match(BILL_REF_RE);
  if (!m) return null;
  const congress = m[1] ?? "119";
  const type = m[2].toLowerCase();
  const num = m[3];
  return `/app/bills/${congress}-${type}-${num}`;
}

// Same idea for bioguides — `[A-Z]\d{6}` is the standard format
// (e.g. M001177 / S000148 / T000250). The substrate uses bioguides
// throughout, so AI compositions sometimes carry them as subtitle
// hints ("Sen. McClintock · M001177"). Auto-link to the member page.
const BIOGUIDE_RE = /\b([A-Z]\d{6})\b/;
function detectBioguideHref(text: string | undefined | null): string | null {
  if (!text) return null;
  const m = text.match(BIOGUIDE_RE);
  if (!m) return null;
  return `/app/members/${m[1]}`;
}

const ENTITY_HREF: Record<string, (id: string) => string> = {
  member: (id) => `/app/members/${encodeURIComponent(id)}`,
  contact: (id) => `/app/contacts/${encodeURIComponent(id)}`,
  bill: (id) => `/app/bills/${encodeURIComponent(id)}`,
  account: (id) => `/app/accounts/${encodeURIComponent(id)}`,
  district: (id) => `/app/districts/${encodeURIComponent(id)}`,
  state: (id) => `/app/states/${encodeURIComponent(id)}`,
  committee: (id) => `/app/committees/${encodeURIComponent(id)}`,
  coalition: (id) => `/app/coalitions/${encodeURIComponent(id)}`,
};
function entityRefHref(
  entity: { kind?: string; id?: string } | undefined,
): string | null {
  if (!entity?.kind || !entity?.id) return null;
  const fn = ENTITY_HREF[entity.kind];
  return fn ? fn(entity.id) : null;
}

const GAP_CLASS: Record<number, string> = {
  1: "gap-1",
  2: "gap-2",
  3: "gap-3",
  4: "gap-4",
  5: "gap-5",
  6: "gap-6",
  7: "gap-7",
  8: "gap-8",
};

// Responsive column classes — collapse to fewer cols on narrow viewports
// so AI-composed grids (Network Tiers, Bill-by-Bill cards, etc.) stack
// readably on mobile instead of forcing 3-4 truncated columns into 375px.
// Tailwind needs each class as a literal string for the JIT compiler;
// these all generate CSS at build time.
const COLS_CLASS: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
  5: "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5",
  6: "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6",
  7: "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7",
  8: "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8",
  9: "grid-cols-9",
  10: "grid-cols-10",
  11: "grid-cols-11",
  12: "grid-cols-12",
};

const THEME_BG: Record<string, string> = {
  default: "bg-white border-stone-200",
  alert: "bg-red-50 border-red-200",
  success: "bg-emerald-50 border-emerald-200",
  warning: "bg-amber-50 border-amber-200",
};

const THEME_TEXT: Record<string, string> = {
  default: "text-stone-900",
  alert: "text-red-700",
  success: "text-emerald-700",
  warning: "text-amber-800",
};

const BADGE_COLOR: Record<string, string> = {
  default: "bg-stone-100 text-stone-700",
  primary: "bg-accent/60 text-ink",
  accent: "bg-accent text-ink",
  success: "bg-emerald-100 text-emerald-800",
  warning: "bg-amber-100 text-amber-800",
  alert: "bg-red-100 text-red-800",
};

export interface PanelRendererProps {
  spec: PanelSpec | PanelSpec[];
  /** Workspace id passed through to widget renderers. */
  workspaceId?: string | null;
  /**
   * Optional widget resolver. The framework doesn't ship a workspace
   * widget registry (that lives at the app layer per the boundary
   * rule); host apps pass a resolver that maps widget_id → React
   * component. Without a resolver, "widget" panel kinds render a
   * dim placeholder card.
   *
   * Signature matches the host's WIDGET_REGISTRY shape — components
   * take {spec: {widget_id, instance_id?, title, params?}, workspaceId}.
   */
  resolveWidget?: (widget_id: string) => React.ComponentType<{
    spec: { widget_id: string; instance_id?: string; title?: string; params?: Record<string, unknown> };
    workspaceId: string;
  }> | null | undefined;
  /**
   * When true, slides render at viewport scale (used by the
   * presentation player). When false (default), slides render as
   * inline bordered preview frames.
   */
  fullscreen?: boolean;
  /**
   * Optional URL builder for opening a deck in the dedicated player.
   * Without this, embedded decks render contact-sheet previews
   * without navigation links.
   */
  buildDeckHref?: (deckId: string) => string;
  /**
   * The deck instance_id for the currently-rendering panel, when
   * known. Used by embedded decks to wire the "Open" link.
   */
  deckInstanceId?: string;
}

/**
 * Recursive renderer. Top-level spec can be a single node or an array
 * (auto-wrapped in a column).
 */
export function PanelRenderer({ spec, workspaceId, resolveWidget, fullscreen, buildDeckHref, deckInstanceId }: PanelRendererProps): JSX.Element {
  if (Array.isArray(spec)) {
    return (
      <div className="flex flex-col gap-3">
        {spec.map((s, i) => (
          <PanelRenderer key={i} spec={s} workspaceId={workspaceId} resolveWidget={resolveWidget} fullscreen={fullscreen} buildDeckHref={buildDeckHref} deckInstanceId={deckInstanceId} />
        ))}
      </div>
    );
  }
  return <PanelNode spec={spec} workspaceId={workspaceId} resolveWidget={resolveWidget} fullscreen={fullscreen} buildDeckHref={buildDeckHref} deckInstanceId={deckInstanceId} />;
}

interface PanelNodeProps {
  spec: PanelSpec;
  workspaceId?: string | null;
  resolveWidget?: PanelRendererProps["resolveWidget"];
  fullscreen?: boolean;
  buildDeckHref?: (deckId: string) => string;
  deckInstanceId?: string;
}

const SLIDE_THEME_BG: Record<string, string> = {
  default: "bg-white border-stone-200",
  dark: "bg-stone-900 border-stone-800",
  primary: "bg-action border-ink",
  accent: "bg-ink border-ink",
};

// Tone tables for callout / progress_bar — kept colorless by default so
// the AI has to reach for tone deliberately.
const CALLOUT_BG: Record<string, string> = {
  tip: "bg-info-soft border-info/30",
  insight: "bg-accent/25 border-accent",
  warning: "bg-amber-50 border-amber-200",
  alert: "bg-red-50 border-red-200",
  success: "bg-emerald-50 border-emerald-200",
};
const CALLOUT_TEXT: Record<string, string> = {
  tip: "text-info",
  insight: "text-ink",
  warning: "text-amber-900",
  alert: "text-red-900",
  success: "text-emerald-900",
};
// Lucide equivalents — when spec.icon isn't a literal string override,
// the callout case below prefers these for consistency with the
// de-emoji'd nav + meeting card iconography.
const CALLOUT_LUCIDE: Record<string, LucideIcon> = {
  tip: Lightbulb,
  insight: Eye,
  warning: AlertTriangle,
  alert: AlertOctagon,
  success: CheckCircle2,
};

const PROGRESS_FILL: Record<string, string> = {
  default: "bg-action",
  success: "bg-emerald-600",
  warning: "bg-amber-500",
  alert: "bg-red-600",
};

const PARTY_BADGE: Record<string, string> = {
  D: "bg-blue-100 text-blue-800",
  R: "bg-red-100 text-red-800",
  I: "bg-stone-100 text-stone-700",
};

const VOTE_FILL: Record<string, string> = {
  yea: "bg-emerald-500",
  nay: "bg-red-500",
  missed: "bg-stone-300",
  present: "bg-amber-400",
  not_eligible: "bg-stone-200",
};
const VOTE_LABEL: Record<string, string> = {
  yea: "Y",
  nay: "N",
  missed: "—",
  present: "P",
  not_eligible: " ",
};

const EMPTY_STATE_TONE: Record<string, string> = {
  default: "border-stone-200 bg-white",
  primary: "border-accent bg-accent/15",
  success: "border-emerald-200 bg-emerald-50",
  warning: "border-amber-200 bg-amber-50",
  alert: "border-red-200 bg-red-50",
};

const SIGNAL_DIRECTION_TONE: Record<string, { fill: string; label: string }> = {
  support: { fill: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "Supports" },
  lean_support: { fill: "bg-blue-100 text-blue-800 border-blue-300", label: "Leans support" },
  neutral: { fill: "bg-stone-100 text-stone-700 border-stone-300", label: "Neutral" },
  lean_oppose: { fill: "bg-amber-100 text-amber-800 border-amber-300", label: "Leans oppose" },
  oppose: { fill: "bg-red-100 text-red-800 border-red-300", label: "Opposes" },
};

const SIGNAL_STATUS_BADGE: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-stone-100 text-stone-500",
  superseded: "bg-stone-100 text-stone-500",
};

const HIGHLIGHT_TONE: Record<string, string> = {
  default: "bg-amber-100",
  primary: "bg-accent/60",
  warning: "bg-amber-100",
  alert: "bg-red-100",
};

const EVENT_STATUS_BADGE: Record<string, string> = {
  scheduled: "bg-blue-100 text-blue-800",
  in_progress: "bg-amber-100 text-amber-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-stone-100 text-stone-500",
  rsvp_open: "bg-blue-100 text-blue-800",
};

const EVENT_TONE_BORDER: Record<string, string> = {
  default: "border-l-stone-300",
  primary: "border-l-action",
  success: "border-l-emerald-500",
  warning: "border-l-amber-500",
  alert: "border-l-red-500",
};

const TAG_TONE_CHIP: Record<string, string> = {
  default: "bg-stone-100 text-stone-700",
  primary: "bg-accent/60 text-ink",
  success: "bg-emerald-100 text-emerald-800",
  warning: "bg-amber-100 text-amber-800",
  alert: "bg-red-100 text-red-800",
};

const SENTIMENT_DOT: Record<string, string> = {
  positive: "bg-emerald-500",
  negative: "bg-red-500",
  neutral: "bg-stone-400",
  mixed: "bg-amber-500",
};

const HIT_ROLE_BADGE: Record<string, string> = {
  quoted: "bg-emerald-700 text-white",
  cited: "bg-emerald-100 text-emerald-800",
  covered: "bg-blue-100 text-blue-800",
  mentioned: "bg-stone-100 text-stone-700",
  aggregated_from: "bg-amber-100 text-amber-800",
  background: "bg-stone-50 text-stone-500",
};

const OUTLET_LEAN_TINT: Record<string, string> = {
  left: "bg-blue-50 text-blue-700",
  center_left: "bg-sky-50 text-sky-700",
  center: "bg-stone-100 text-stone-700",
  center_right: "bg-amber-50 text-amber-800",
  right: "bg-red-50 text-red-700",
  unknown: "bg-stone-100 text-stone-500",
};

const PULLQUOTE_THEME_BORDER: Record<string, string> = {
  default: "border-stone-300",
  primary: "border-action",
  alert: "border-red-500",
};

const CAL_TONE: Record<string, string> = {
  default: "bg-stone-100 text-stone-700",
  primary: "bg-accent/60 text-ink",
  success: "bg-emerald-100 text-emerald-800",
  warning: "bg-amber-100 text-amber-800",
  alert: "bg-red-100 text-red-800",
};

const TASK_TONE: Record<string, string> = {
  default: "border-stone-200",
  primary: "border-action/40",
  warning: "border-amber-300",
  alert: "border-red-300",
};

const TASK_STATUS_ICON: Record<string, LucideIcon> = {
  open: Circle,
  in_progress: CircleDot,
  done: CheckCircle2,
  deferred: PauseCircle,
  dismissed: XCircle,
};

const TASK_STATUS_COLOR: Record<string, string> = {
  open: "text-stone-400",
  in_progress: "text-action",
  done: "text-emerald-600",
  deferred: "text-amber-600",
  dismissed: "text-stone-300",
};

const STEP_STATUS_MARKER: Record<string, string> = {
  done: "bg-emerald-600 text-white border-emerald-600",
  current: "bg-action text-white border-action ring-2 ring-accent",
  pending: "bg-white text-stone-400 border-stone-300",
  skipped: "bg-stone-100 text-stone-400 border-stone-200",
};

const STEP_STATUS_LINE: Record<string, string> = {
  done: "bg-emerald-600",
  current: "bg-stone-200",
  pending: "bg-stone-200",
  skipped: "bg-stone-100",
};

const STEP_STATUS_SYMBOL: Record<string, LucideIcon> = {
  done: Check,
  current: CircleDot,
  pending: Circle,
  skipped: Minus,
};

const TIER_DOT: Record<string, string> = {
  hot: "bg-red-500",
  warm: "bg-orange-500",
  cool: "bg-sky-500",
  cold: "bg-stone-400",
};

const STANCE_TONE: Record<string, string> = {
  default: "bg-stone-100 text-stone-700",
  primary: "bg-accent/60 text-ink",
  success: "bg-emerald-100 text-emerald-800",
  warning: "bg-amber-100 text-amber-800",
  alert: "bg-red-100 text-red-800",
};

const COMPARISON_TONE_TEXT: Record<string, string> = {
  default: "text-stone-900",
  primary: "text-action",
  success: "text-emerald-700",
  warning: "text-amber-700",
  alert: "text-red-700",
};
const COMPARISON_DELTA_TONE: Record<string, string> = {
  default: "bg-stone-100 text-stone-700",
  success: "bg-emerald-100 text-emerald-800",
  warning: "bg-amber-100 text-amber-800",
  alert: "bg-red-100 text-red-800",
};

const TIMELINE_DOT: Record<string, string> = {
  default: "bg-stone-400 border-stone-300",
  primary: "bg-action border-action/30",
  success: "bg-emerald-600 border-emerald-300",
  warning: "bg-amber-500 border-amber-300",
  alert: "bg-red-600 border-red-300",
};

// Political party styling for the new poll/candidate primitives.
// Solid colors for bars/markers; tinted bg+text for chips/badges.
const PARTY_BAR_FILL: Record<string, string> = {
  D: "bg-blue-600",
  R: "bg-red-600",
  I: "bg-stone-500",
};
const PARTY_BAR_TINT: Record<string, string> = {
  D: "bg-blue-100",
  R: "bg-red-100",
  I: "bg-stone-100",
};
const PARTY_TEXT: Record<string, string> = {
  D: "text-blue-700",
  R: "text-red-700",
  I: "text-stone-700",
};
const SPREAD_TONE: Record<string, string> = {
  default: "bg-stone-100 text-stone-700",
  primary: "bg-accent/60 text-ink",
  warning: "bg-amber-100 text-amber-800",
  alert: "bg-red-100 text-red-800",
};

// Electoral map — state cell fills + EC tally bar segments.
const EC_STATUS_FILL: Record<string, string> = {
  called_d: "bg-blue-700 text-white border-blue-800",
  lean_d:   "bg-blue-300 text-blue-900 border-blue-400",
  tossup:   "bg-stone-200 text-stone-700 border-stone-300",
  lean_r:   "bg-red-300 text-red-900 border-red-400",
  called_r: "bg-red-700 text-white border-red-800",
  no_data:  "bg-white text-stone-400 border-stone-200",
};
const EC_TALLY_FILL: Record<string, string> = {
  d: "bg-blue-700",
  lean_d: "bg-blue-300",
  tossup: "bg-stone-300",
  lean_r: "bg-red-300",
  r: "bg-red-700",
};

// 7-tier race rating bar.
const RACE_RATING_ORDER: ReadonlyArray<"solid_d" | "likely_d" | "lean_d" | "tossup" | "lean_r" | "likely_r" | "solid_r"> =
  ["solid_d", "likely_d", "lean_d", "tossup", "lean_r", "likely_r", "solid_r"];
const RACE_RATING_FILL: Record<string, string> = {
  solid_d: "bg-blue-700",
  likely_d: "bg-blue-500",
  lean_d: "bg-blue-300",
  tossup: "bg-stone-300",
  lean_r: "bg-red-300",
  likely_r: "bg-red-500",
  solid_r: "bg-red-700",
};
const RACE_RATING_LABEL: Record<string, string> = {
  solid_d: "Solid D",
  likely_d: "Likely D",
  lean_d: "Lean D",
  tossup: "Toss-up",
  lean_r: "Lean R",
  likely_r: "Likely R",
  solid_r: "Solid R",
};

// Whip-count buckets — fill + label + position.
const WHIP_BUCKET_ORDER: ReadonlyArray<"yes" | "lean_yes" | "undecided" | "lean_no" | "no" | "no_response"> =
  ["yes", "lean_yes", "undecided", "lean_no", "no", "no_response"];
const WHIP_BUCKET_FILL: Record<string, string> = {
  yes: "bg-emerald-600",
  lean_yes: "bg-emerald-300",
  undecided: "bg-stone-300",
  lean_no: "bg-red-300",
  no: "bg-red-600",
  no_response: "bg-stone-200",
};
const WHIP_BUCKET_TEXT: Record<string, string> = {
  yes: "text-emerald-800",
  lean_yes: "text-emerald-700",
  undecided: "text-stone-700",
  lean_no: "text-red-700",
  no: "text-red-800",
  no_response: "text-stone-500",
};
const WHIP_BUCKET_LABEL: Record<string, string> = {
  yes: "Yes",
  lean_yes: "Lean Y",
  undecided: "Undecided",
  lean_no: "Lean N",
  no: "No",
  no_response: "No reply",
};

// Donor breakdown segment colors (matches existing tone vocabulary).
const DONOR_SEGMENT_FILL: Record<string, string> = {
  default: "rgb(120 113 108)",  // stone-500
  primary: "rgb(40 78 59)",     // action
  success: "rgb(5 150 105)",    // emerald-600
  warning: "rgb(217 119 6)",    // amber-600
  alert:   "rgb(220 38 38)",    // red-600
  accent:  "rgb(122 154 46)",  // brand lime (darkened for contrast)
};

// Affiliation grouping — section label only. Visual differentiation
// comes from section ordering + section label typography, not from
// per-kind decorative icons (those felt CRM-shop circa 2011).
const AFFILIATION_KIND_ICON: Record<string, string> = {
  committee: "",
  subcommittee: "",
  caucus: "",
  working_group: "",
  leadership: "",
};
const AFFILIATION_KIND_LABEL: Record<string, string> = {
  committee: "Committees",
  subcommittee: "Subcommittees",
  caucus: "Caucuses",
  working_group: "Working groups",
  leadership: "Leadership",
};
const AFFILIATION_ROLE_BADGE: Record<string, string> = {
  chair:      "bg-amber-100 text-amber-800",
  ranking:    "bg-blue-100 text-blue-800",
  vice_chair: "bg-purple-100 text-purple-800",
  member:     "bg-stone-100 text-stone-600",
};
const AFFILIATION_ROLE_LABEL: Record<string, string> = {
  chair: "Chair",
  ranking: "Ranking",
  vice_chair: "Vice chair",
  member: "Member",
};

// Headline strip — sentiment dot.
const HEADLINE_SENTIMENT_DOT: Record<string, string> = {
  positive: "bg-emerald-500",
  neutral:  "bg-stone-300",
  negative: "bg-red-500",
  mixed:    "bg-amber-400",
};

// Meeting-prep relationship temperature pill.
const TEMPERATURE_PILL: Record<string, string> = {
  cold:     "bg-blue-100 text-blue-700",
  warm:     "bg-amber-100 text-amber-800",
  hot:      "bg-orange-100 text-orange-800",
  champion: "bg-emerald-100 text-emerald-800",
  hostile:  "bg-red-100 text-red-800",
};
// Temperature labels — clean text-only badges. The background color
// (TEMPERATURE_BG above) carries the visual weight; emoji prefixes
// felt CRM-shop circa 2011. A colored dot via ::before in the chip
// CSS would also work but kept out here so this stays a pure string
// dictionary the AI can read structurally.
const TEMPERATURE_LABEL: Record<string, string> = {
  cold:     "Cold",
  warm:     "Warm",
  hot:      "Hot",
  champion: "Champion",
  hostile:  "Hostile",
};

// Vote-breakdown outcome chip.
const VOTE_OUTCOME_BADGE: Record<string, string> = {
  passed:    "bg-emerald-100 text-emerald-800 border-emerald-200",
  failed:    "bg-red-100 text-red-800 border-red-200",
  tabled:    "bg-stone-100 text-stone-700 border-stone-200",
  withdrawn: "bg-stone-100 text-stone-500 border-stone-200",
};
const VOTE_OUTCOME_LABEL: Record<string, string> = {
  passed:    "PASSED",
  failed:    "FAILED",
  tabled:    "TABLED",
  withdrawn: "WITHDRAWN",
};

// Talking-points tone bullet color.
const TALKING_POINT_BULLET: Record<string, string> = {
  default: "bg-stone-400",
  primary: "bg-action",
  warning: "bg-amber-500",
  alert:   "bg-red-500",
};

// Amendment status badge.
const AMEND_STATUS_BADGE: Record<string, string> = {
  pending:              "bg-stone-100 text-stone-600",
  agreed:               "bg-emerald-100 text-emerald-800",
  rejected:             "bg-red-100 text-red-800",
  withdrawn:            "bg-stone-100 text-stone-500",
  ruled_out_of_order:   "bg-amber-100 text-amber-800",
  considered_en_bloc:   "bg-blue-100 text-blue-800",
};
const AMEND_STATUS_LABEL: Record<string, string> = {
  pending:              "Pending",
  agreed:               "Agreed to",
  rejected:             "Rejected",
  withdrawn:            "Withdrawn",
  ruled_out_of_order:   "Ruled OOO",
  considered_en_bloc:   "En bloc",
};

// KPI grid tile background tint.
const KPI_TONE_BG: Record<string, string> = {
  default: "bg-white",
  primary: "bg-accent/15",
  success: "bg-emerald-50",
  warning: "bg-amber-50",
  alert:   "bg-red-50",
};
const KPI_DELTA_TONE: Record<string, string> = {
  positive: "text-emerald-700",
  negative: "text-red-700",
  neutral:  "text-stone-500",
};

// Gap chart bar tone.
const GAP_BAR_FILL: Record<string, string> = {
  default: "bg-stone-500",
  primary: "bg-action",
  success: "bg-emerald-600",
  warning: "bg-amber-500",
  alert:   "bg-red-600",
};
const GAP_BAR_TINT: Record<string, string> = {
  default: "bg-stone-100",
  primary: "bg-accent/60",
  success: "bg-emerald-100",
  warning: "bg-amber-100",
  alert:   "bg-red-100",
};

// Press-release status pill.
const PR_STATUS_PILL: Record<string, string> = {
  drafted:    "bg-stone-100 text-stone-600",
  in_review:  "bg-amber-100 text-amber-800",
  approved:   "bg-blue-100 text-blue-800",
  sent:       "bg-emerald-100 text-emerald-800",
};
const PR_STATUS_LABEL: Record<string, string> = {
  drafted:    "DRAFT",
  in_review:  "IN REVIEW",
  approved:   "APPROVED",
  sent:       "SENT",
};

// Donor tier badge.
const DONOR_TIER_BADGE: Record<string, string> = {
  default: "bg-stone-100 text-stone-700",
  primary: "bg-accent/60 text-ink",
  success: "bg-emerald-100 text-emerald-800",
  warning: "bg-amber-100 text-amber-800",
  accent:  "bg-accent text-ink",
};

// Delegation member-card party border.
const PARTY_BORDER: Record<string, string> = {
  D: "border-l-blue-600",
  R: "border-l-red-600",
  I: "border-l-stone-500",
};
const DELEGATION_ROLE_BADGE: Record<string, string> = {
  senior:           "bg-blue-100 text-blue-800",
  junior:           "bg-stone-100 text-stone-600",
  leadership:       "bg-amber-100 text-amber-800",
  committee_chair:  "bg-purple-100 text-purple-800",
  ranking:          "bg-blue-100 text-blue-800",
  freshman:         "bg-emerald-100 text-emerald-800",
};
const DELEGATION_ROLE_LABEL: Record<string, string> = {
  senior:           "Senior",
  junior:           "Junior",
  leadership:       "Leadership",
  committee_chair:  "Chair",
  ranking:          "Ranking",
  freshman:         "Freshman",
};

// Member position diff position chip.
const POSITION_CHIP: Record<string, string> = {
  support:        "bg-emerald-100 text-emerald-800 border-emerald-200",
  lean_support:   "bg-emerald-50 text-emerald-700 border-emerald-100",
  neutral:        "bg-stone-100 text-stone-600 border-stone-200",
  lean_oppose:    "bg-red-50 text-red-700 border-red-100",
  oppose:         "bg-red-100 text-red-800 border-red-200",
  no_record:      "bg-white text-stone-400 border-stone-200 italic",
};
const POSITION_LABEL: Record<string, string> = {
  support:        "Support",
  lean_support:   "Lean support",
  neutral:        "Neutral",
  lean_oppose:    "Lean oppose",
  oppose:         "Oppose",
  no_record:      "No record",
};

// Endorsement status pill.
const ENDORSEMENT_STATUS_PILL: Record<string, string> = {
  rumored:    "bg-amber-100 text-amber-800",
  confirmed:  "bg-emerald-100 text-emerald-800",
  withdrawn:  "bg-stone-100 text-stone-500",
};
const ENDORSEMENT_STATUS_LABEL: Record<string, string> = {
  rumored:    "RUMORED",
  confirmed:  "CONFIRMED",
  withdrawn:  "WITHDRAWN",
};

// Event attendance bucket styling.
const RSVP_BUCKET_FILL: Record<string, string> = {
  confirmed:   "bg-emerald-500",
  maybe:       "bg-amber-400",
  declined:    "bg-red-500",
  no_response: "bg-stone-300",
};
const RSVP_BUCKET_TEXT: Record<string, string> = {
  confirmed:   "text-emerald-800",
  maybe:       "text-amber-800",
  declined:    "text-red-800",
  no_response: "text-stone-500",
};
const RSVP_BUCKET_LABEL: Record<string, string> = {
  confirmed:   "Confirmed",
  maybe:       "Maybe",
  declined:    "Declined",
  no_response: "No response",
};

// Scorecard strip — letter grade tile color.
const GRADE_TILE: Record<string, string> = {
  "A+": "bg-emerald-700 text-white",
  "A":  "bg-emerald-600 text-white",
  "A-": "bg-emerald-500 text-white",
  "B+": "bg-blue-600 text-white",
  "B":  "bg-blue-500 text-white",
  "B-": "bg-blue-400 text-white",
  "C+": "bg-amber-500 text-white",
  "C":  "bg-amber-400 text-stone-900",
  "C-": "bg-amber-300 text-stone-900",
  "D+": "bg-orange-500 text-white",
  "D":  "bg-orange-400 text-white",
  "D-": "bg-orange-300 text-stone-900",
  "F":  "bg-red-600 text-white",
  "INC": "bg-stone-200 text-stone-500",
};

function legislatorPhotoUrl(bioguide: string): string {
  // Server proxies & caches member photos. The endpoint redirects to
  // the storage CDN once cached, falls back to a 404 we render as
  // initials. See src/index.ts ensureMemberPhotoCached.
  return `/api/v1/photos/members/${encodeURIComponent(bioguide)}.jpg`;
}

function legislatorInitials(name: string): string {
  const parts = name.replace(/^Sen\.\s*|^Rep\.\s*|^Dr\.\s*/, "").trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const SLIDE_THEME_TITLE: Record<string, string> = {
  default: "text-stone-900",
  dark: "text-white",
  primary: "text-white",
  accent: "text-white",
};

const SLIDE_THEME_SUBTITLE: Record<string, string> = {
  default: "text-stone-500",
  dark: "text-stone-300",
  primary: "text-white/75",
  accent: "text-accent",
};

/**
 * Panel specs are authored by AI clients, so one malformed node (an object
 * where text belongs, say) must not take down the page around it. Each node
 * renders inside its own boundary that falls back to a small notice.
 */
class PanelNodeBoundary extends React.Component<{ kind?: string; resetKey: unknown; children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error: unknown) { console.error(`Panel node "${this.props.kind ?? "unknown"}" failed to render:`, error); }
  componentDidUpdate(previous: Readonly<{ resetKey: unknown }>) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) this.setState({ failed: false });
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return <div role="status" className="rounded border border-dashed border-stone-300 bg-stone-50 px-3 py-2 text-xs text-stone-500">This {this.props.kind ? `${this.props.kind.replace(/_/g, " ")} ` : ""}panel couldn't be displayed.</div>;
  }
}

/** Text for a label-like spec field: AI clients sometimes send a source badge ({source_name}) or other object. */
function panelText(value: unknown): string | null {
  if (value == null || value === false) return null;
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object" && !Array.isArray(value)) {
    const v = value as Record<string, unknown>;
    for (const key of ["source_name", "name", "label", "title", "text"]) if (typeof v[key] === "string") return v[key] as string;
  }
  return null;
}

function PanelNode(props: PanelNodeProps): JSX.Element {
  return <PanelNodeBoundary kind={(props.spec as { kind?: string } | undefined)?.kind} resetKey={props.spec}><PanelNodeInner {...props} /></PanelNodeBoundary>;
}

/**
 * Living reports mark blocks that moved since the viewer last looked with
 * `change: { lines }` (src/lib/report-diff.ts). Any kind can carry it, so
 * the highlight wraps the node rather than living in each primitive.
 */
function PanelNodeInner(props: PanelNodeProps): JSX.Element | null {
  const change = (props.spec as { change?: { lines?: unknown } } | undefined)?.change;
  const lines = Array.isArray(change?.lines) ? change.lines.filter((l): l is string => typeof l === "string").slice(0, 6) : [];
  if (lines.length === 0) return <PanelNodeBody {...props} />;
  return (
    <div className="relative rounded-xl ring-2 ring-teal-500/60 ring-offset-2 ring-offset-white" data-report-change="">
      <div className="mb-2 rounded-lg bg-teal-50 px-3 py-2 text-xs text-teal-900">
        <span className="font-semibold">Changed since you last looked</span>
        <ul className="mt-1 space-y-0.5">
          {lines.map((line, i) => <li key={i}>{line}</li>)}
        </ul>
      </div>
      <PanelNodeBody {...props} />
    </div>
  );
}

function PanelNodeBody({ spec, workspaceId, resolveWidget, fullscreen, buildDeckHref, deckInstanceId }: PanelNodeProps): JSX.Element | null {
  // Universal cycle hook — runs once per PanelNode. Length is 0 for
  // primitives that don't opt into cycling, so the interval is a no-op.
  // Honors workspace view_preferences.rotation_enabled globally. In TV
  // mode every cycle-capable primitive cycles by default and the
  // display-wide pause (Space) stops them all.
  const tv = useTvMode();
  const cycleSpec = effectiveCycle(spec.kind, (spec as { cycle?: { interval_ms?: number } }).cycle, tv);
  const cycleLength: number = (() => {
    if (!cycleSpec) return 0;
    switch (spec.kind) {
      case "list":        return spec.items.length;
      case "news_feed":   return spec.items.length;
      default:            return 0;
    }
  })();
  const { index: cycleIndex } = useCycle(cycleLength, {
    enabled: !!cycleSpec && cycleLength > 1,
    interval_ms: cycleSpec?.interval_ms,
    paused: tv.active && tv.paused,
    workspace_id: workspaceId,
  });
  switch (spec.kind) {
    case "header": {
      const level = spec.level ?? 2;
      const sizeClass =
        level === 1 ? "text-2xl font-bold tracking-tight" :
        level === 2 ? "text-lg font-semibold" :
        "text-sm font-semibold uppercase tracking-wider text-stone-500";
      return (
        <div>
          <div className={sizeClass + " text-stone-900"}>{spec.text}</div>
          {spec.subtitle && <div className="text-sm text-stone-500 mt-0.5">{spec.subtitle}</div>}
        </div>
      );
    }

    case "stat_card": {
      const theme = spec.theme ?? "default";
      return (
        <div className={`border rounded-lg p-3 ${THEME_BG[theme]}`}>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">
            {spec.label}
          </div>
          <div className={`text-2xl font-bold tabular-nums mt-0.5 ${THEME_TEXT[theme]}`}>
            {spec.value}
          </div>
          {(spec.delta || spec.comparison) && (
            <div className="text-xs text-stone-500 mt-0.5">
              {spec.delta && <span className="font-semibold">{spec.delta}</span>}
              {spec.delta && spec.comparison && " · "}
              {spec.comparison}
            </div>
          )}
          {spec.sparkline && spec.sparkline.length > 0 && (
            <div className="mt-2 text-stone-400">
              <InlineSparkline values={spec.sparkline} />
            </div>
          )}
        </div>
      );
    }

    case "hero": {
      const theme = spec.theme ?? "default";
      return (
        <div className={`border rounded-xl ${spec.value == null ? "p-6 sm:p-8" : "p-5"} ${THEME_BG[theme]}`}>
          <h2 className={`${spec.value == null ? "text-2xl sm:text-3xl leading-tight tracking-tight" : "text-sm opacity-80"} font-semibold ${THEME_TEXT[theme]}`}>{spec.title}</h2>
          {spec.value != null && (
            <div className={`text-4xl font-bold tabular-nums mt-1 ${THEME_TEXT[theme]}`}>
              {spec.value}
            </div>
          )}
          {spec.subtitle && <div className="text-sm text-stone-600 mt-1">{spec.subtitle}</div>}
          {spec.children && spec.children.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {spec.children.map((c, i) => (
                <PanelNode key={i} spec={c} workspaceId={workspaceId} resolveWidget={resolveWidget} fullscreen={fullscreen} buildDeckHref={buildDeckHref} deckInstanceId={deckInstanceId} />
              ))}
            </div>
          )}
        </div>
      );
    }

    case "prose_card":
      return (
        <div className="border border-stone-200 rounded-lg p-4 bg-white">
          {spec.title && (
            <div className="text-sm font-semibold text-stone-900 mb-2">{spec.title}</div>
          )}
          <SafeMarkdown text={spec.markdown} />
        </div>
      );

    case "list": {
      // When cycle is set, render only the current item (one at a time).
      const itemsToRender = cycleSpec && spec.items.length > 0
        ? [spec.items[cycleIndex % spec.items.length]]
        : spec.items;
      const inner = (
        <ul className="divide-y divide-stone-100">
          {itemsToRender.map((it, i) => {
            // Auto-derive a click-through URL in priority order:
            //   1. explicit it.href — always wins
            //   2. structured entity {kind, id} — preferred substrate-
            //      shaped path; maps cleanly via entityHref
            //   3. bill_ref pattern in title/subtitle (HR 7640, S 1748)
            //   4. bioguide pattern in title/subtitle (M001177)
            // Result: every AI-composed bill OR member row becomes
            // tappable on mobile without the AI having to remember to
            // set href on every item.
            const entityHrefVal = entityRefHref(it.entity);
            const detectedBillRef =
              detectBillRefHref(it.title) ?? detectBillRefHref(it.subtitle);
            const detectedBioguide =
              detectBioguideHref(it.title) ??
              detectBioguideHref(it.subtitle);
            const effectiveHref =
              it.href ?? entityHrefVal ?? detectedBillRef ?? detectedBioguide ?? null;
            const content = (
              <div className="flex items-center gap-3 px-3 py-2 hover:bg-stone-50">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-stone-900 truncate">{it.title}</div>
                  {it.subtitle && <div className="text-xs text-stone-500 truncate">{it.subtitle}</div>}
                </div>
                {it.trailing && <div className="text-xs text-stone-500 font-mono tabular-nums">{it.trailing}</div>}
              </div>
            );
            return (
              <li key={i}>
                {effectiveHref ? (
                  <a href={effectiveHref} className="block text-stone-900 no-underline">{content}</a>
                ) : content}
              </li>
            );
          })}
        </ul>
      );
      if (spec.flush) return inner;
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {spec.title && (
            <div className="px-3 py-2 border-b border-stone-100 text-sm font-semibold text-stone-900">
              {spec.title}
            </div>
          )}
          {inner}
        </div>
      );
    }

    case "badge_row":
      return (
        <div className="flex flex-wrap gap-2">
          {spec.badges.map((b, i) => (
            <span
              key={i}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${BADGE_COLOR[b.color ?? "default"]}`}
            >
              <span>{b.label}</span>
              {b.value != null && <span className="font-mono font-semibold tabular-nums">{b.value}</span>}
            </span>
          ))}
        </div>
      );

    case "divider":
      if (spec.label) {
        return (
          <div className="flex items-center gap-3">
            <div className="flex-1 border-t border-stone-200" />
            <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">{spec.label}</div>
            <div className="flex-1 border-t border-stone-200" />
          </div>
        );
      }
      return <div className="border-t border-stone-200" />;

    case "row": {
      const gap = GAP_CLASS[spec.gap ?? 3] ?? "gap-3";
      const align =
        spec.align === "center" ? "items-center" :
        spec.align === "stretch" ? "items-stretch" : "items-start";
      // basis 240px makes children request that minimum before flex-grow
      // kicks in. On narrow viewports (mobile) the row reflows vertically
      // instead of squeezing children to unreadable widths. Was min-w-0
      // which allowed infinite shrinkage and produced the "stat cards
      // overlapping the sidebar" mobile bug.
      return (
        <div className={`flex flex-wrap ${gap} ${align}`}>
          {spec.children.map((c, i) => (
            <div key={i} className="flex-[1_1_240px] min-w-0">
              <PanelNode spec={c} workspaceId={workspaceId} resolveWidget={resolveWidget} fullscreen={fullscreen} buildDeckHref={buildDeckHref} deckInstanceId={deckInstanceId} />
            </div>
          ))}
        </div>
      );
    }

    case "column": {
      const gap = GAP_CLASS[spec.gap ?? 3] ?? "gap-3";
      return (
        <div className={`flex flex-col ${gap}`}>
          {spec.children.map((c, i) => (
            <PanelNode key={i} spec={c} workspaceId={workspaceId} resolveWidget={resolveWidget} fullscreen={fullscreen} buildDeckHref={buildDeckHref} deckInstanceId={deckInstanceId} />
          ))}
        </div>
      );
    }

    case "grid": {
      const cols = COLS_CLASS[spec.cols ?? 3] ?? "grid-cols-3";
      const gap = GAP_CLASS[spec.gap ?? 3] ?? "gap-3";
      return (
        <div className={`grid ${cols} ${gap}`}>
          {spec.children.map((c, i) => (
            <PanelNode key={i} spec={c} workspaceId={workspaceId} resolveWidget={resolveWidget} fullscreen={fullscreen} buildDeckHref={buildDeckHref} deckInstanceId={deckInstanceId} />
          ))}
        </div>
      );
    }

    case "renderer":
      return <RendererSlot tool={spec.tool} input={spec.input} />;

    case "widget": {
      // Delegate to the host-supplied widget resolver. The framework
      // boundary rule means we can't import the host's WIDGET_REGISTRY
      // directly — but the host app passes a resolver via the
      // resolveWidget prop and we render whatever it returns. Without
      // a resolver, fall back to a dim placeholder card so the
      // composition still validates.
      if (!resolveWidget || !workspaceId) {
        return (
          <div className="border border-stone-200 rounded-lg p-3 bg-white">
            <div className="text-xs text-stone-400 italic">
              Widget: {spec.widget_id}{spec.instance_id ? ` / ${spec.instance_id}` : ""}
            </div>
          </div>
        );
      }
      const WidgetComp = resolveWidget(spec.widget_id);
      if (!WidgetComp) {
        return (
          <div className="border border-stone-200 rounded-lg p-3 bg-amber-50">
            <div className="text-xs text-amber-700 italic">
              Unknown widget kind: <code className="font-mono">{spec.widget_id}</code>
            </div>
          </div>
        );
      }
      return (
        <div className="border border-stone-200 rounded-lg p-3 bg-white">
          <WidgetComp
            spec={{
              widget_id: spec.widget_id,
              instance_id: spec.instance_id,
              params: spec.params,
            }}
            workspaceId={workspaceId}
          />
        </div>
      );
    }

    case "slide": {
      const theme = spec.theme ?? "default";
      const layout = spec.layout ?? "full";
      const titleClass = SLIDE_THEME_TITLE[theme];
      const subtitleClass = SLIDE_THEME_SUBTITLE[theme];

      // Fullscreen mode (presentation player) — slide fills the viewport.
      if (fullscreen) {
        const isTitle = layout === "title";
        const isCentered = layout === "centered" || isTitle;
        return (
          <div className={`flex-1 self-stretch flex flex-col ${SLIDE_THEME_BG[theme]}`}>
            <div className={`flex-1 flex flex-col ${isCentered ? "items-center justify-center text-center" : "items-stretch"} px-12 py-10 overflow-auto`}>
              <div className={isCentered ? "max-w-3xl" : "w-full max-w-6xl mx-auto"}>
                <h2 className={`${isTitle ? "text-5xl" : "text-3xl"} font-bold tracking-tight ${titleClass}`}>{spec.title}</h2>
                {spec.subtitle && (
                  <div className={`${isTitle ? "text-xl" : "text-base"} mt-3 ${subtitleClass}`}>{spec.subtitle}</div>
                )}
                {spec.body && spec.body.length > 0 && (
                  <div className="mt-8">
                    {spec.body.map((c, i) => (
                      <div key={i} className="mb-4">
                        <PanelNode spec={c} workspaceId={workspaceId} resolveWidget={resolveWidget} fullscreen={fullscreen} buildDeckHref={buildDeckHref} deckInstanceId={deckInstanceId} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }

      // Inline preview mode — bordered card showing the slide content
      // at a smaller scale, suitable for embedding inside a non-
      // presentation page or in a contact-sheet view.
      return (
        <div className={`border rounded-xl p-5 ${SLIDE_THEME_BG[theme]}`}>
          <div className={`text-xs uppercase tracking-wider font-semibold ${subtitleClass} mb-2`}>Slide</div>
          <div className={`text-xl font-bold ${titleClass}`}>{spec.title}</div>
          {spec.subtitle && (
            <div className={`text-sm mt-1 ${subtitleClass}`}>{spec.subtitle}</div>
          )}
          {spec.body && spec.body.length > 0 && (
            <div className="mt-4 flex flex-col gap-3">
              {spec.body.map((c, i) => (
                <PanelNode key={i} spec={c} workspaceId={workspaceId} resolveWidget={resolveWidget} fullscreen={false} buildDeckHref={buildDeckHref} deckInstanceId={deckInstanceId} />
              ))}
            </div>
          )}
        </div>
      );
    }

    case "deck": {
      // In fullscreen mode the player handles its own slide-by-slide
      // rendering; this branch is the embedded preview path. Render a
      // contact sheet of slide thumbnails with a "play deck" CTA.
      const href = buildDeckHref && deckInstanceId ? buildDeckHref(deckInstanceId) : null;
      const slideCount = spec.slides.length;
      return (
        <div className="border border-stone-200 rounded-xl bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-100 flex items-baseline justify-between gap-3">
            <div className="min-w-0">
              <div className="text-base font-semibold text-stone-900 truncate">{spec.title}</div>
              {spec.subtitle && (
                <div className="text-xs text-stone-500 truncate">{spec.subtitle}</div>
              )}
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-xs text-stone-500 font-mono tabular-nums">{slideCount} slide{slideCount === 1 ? "" : "s"}</span>
              {href && (
                <a href={href} className="text-xs font-semibold px-3 py-1.5 rounded-md bg-action text-white hover:bg-action-hover transition-colors no-underline inline-flex items-center gap-1.5">
                  Play <Play size={11} fill="currentColor" aria-hidden />
                </a>
              )}
            </div>
          </div>
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {spec.slides.slice(0, 6).map((slide, i) => {
              const slideTheme = slide.theme ?? spec.theme ?? "default";
              return (
                <div key={i} className={`border rounded-md p-3 aspect-[16/9] flex flex-col justify-between overflow-hidden ${SLIDE_THEME_BG[slideTheme]}`}>
                  <div>
                    <div className={`text-[10px] uppercase tracking-wider font-mono ${SLIDE_THEME_SUBTITLE[slideTheme]}`}>
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className={`text-sm font-semibold mt-1 line-clamp-3 ${SLIDE_THEME_TITLE[slideTheme]}`}>
                      {slide.title}
                    </div>
                  </div>
                  {slide.subtitle && (
                    <div className={`text-[11px] line-clamp-2 ${SLIDE_THEME_SUBTITLE[slideTheme]}`}>
                      {slide.subtitle}
                    </div>
                  )}
                </div>
              );
            })}
            {slideCount > 6 && (
              <div className="border border-dashed border-stone-300 rounded-md p-3 aspect-[16/9] flex items-center justify-center text-xs text-stone-500">
                +{slideCount - 6} more
              </div>
            )}
          </div>
          {spec.footer && (
            <div className="px-4 py-2 border-t border-stone-100 text-[11px] text-stone-500 italic">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "callout": {
      const tone = spec.tone ?? "tip";
      // Explicit spec.icon override (lucide name or legacy emoji, mapped
      // via SpecIcon) wins; otherwise render the Lucide variant for the tone.
      const ToneIcon = resolveSpecIcon(spec.icon) ?? CALLOUT_LUCIDE[tone] ?? Lightbulb;
      return (
        <div className={`border-l-4 rounded-r-md ${CALLOUT_BG[tone]} ${CALLOUT_TEXT[tone]} px-3 py-2.5`}>
          <div className="flex items-start gap-2">
            <ToneIcon size={15} strokeWidth={1.75} className="flex-shrink-0 mt-0.5" aria-hidden />
            <div className="flex-1 min-w-0">
              {spec.title && (
                <div className="font-semibold text-sm">{spec.title}</div>
              )}
              <div className={`text-sm leading-relaxed whitespace-pre-wrap ${spec.title ? "mt-0.5" : ""}`}>
                {spec.body}
              </div>
            </div>
          </div>
        </div>
      );
    }

    case "progress_bar": {
      if (!Number.isFinite(spec.max) || spec.max <= 0 || !Number.isFinite(spec.value) || spec.target != null && !Number.isFinite(spec.target)) {
        return <div role="status" className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600">{spec.label}: progress is unavailable because the recorded value or positive maximum is missing.</div>;
      }
      const max = spec.max;
      const value = spec.value;
      const tone = spec.tone ?? (spec.target && value >= spec.target ? "success" : "default");
      const fillPct = Math.max(0, Math.min(1, value / max)) * 100;
      const targetPct = spec.target != null ? (Math.max(0, Math.min(spec.target, max)) / max) * 100 : null;
      const showFraction = spec.show_fraction !== false;
      const aboveTarget = spec.target != null && value >= spec.target;
      return (
        <div className="border border-stone-200 rounded-lg p-3 bg-white">
          <div className="flex items-baseline gap-2">
            <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 flex-1">
              {spec.label}
            </div>
            <div className="text-base font-bold tabular-nums text-stone-900">
              {showFraction && spec.target != null ? `${value} / ${spec.target}` : showFraction ? `${value} / ${max}` : value}
              {aboveTarget && spec.target != null && (
                <Check size={14} strokeWidth={2.5} className="inline text-emerald-600 ml-1 -mt-0.5" aria-label="threshold reached" />
              )}
            </div>
          </div>
          <div className="mt-2 relative h-2.5 rounded-full bg-stone-100 overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 ${PROGRESS_FILL[tone]} transition-all`}
              style={{ width: `${fillPct}%` }}
            />
            {targetPct != null && targetPct > 0 && targetPct < 100 && (
              <div
                className="absolute inset-y-0 w-0.5 bg-stone-800"
                style={{ left: `${targetPct}%` }}
                aria-label={`Target threshold at ${spec.target}`}
              />
            )}
          </div>
          {spec.caption && (
            <div className="text-xs text-stone-500 mt-1.5">{spec.caption}</div>
          )}
        </div>
      );
    }

    case "legislator_chip": {
      const compact = !!spec.compact;
      const photoSize = compact ? "w-7 h-7" : "w-9 h-9";
      const partyKey = (spec.party ?? "").toUpperCase();
      const partyClass = PARTY_BADGE[partyKey] ?? "bg-stone-100 text-stone-700";
      const initials = legislatorInitials(spec.name);
      const Inner = (
        <div className={`flex items-center gap-2.5 ${compact ? "py-0.5" : "py-1"}`}>
          <div className={`${photoSize} rounded-full bg-stone-200 overflow-hidden flex-shrink-0 relative`}>
            <img
              src={legislatorPhotoUrl(spec.bioguide)}
              alt={spec.name}
              loading="lazy"
              className="w-full h-full object-cover"
              onError={(e) => {
                // Hide the broken image so initials show through
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
            <div className={`absolute inset-0 flex items-center justify-center text-stone-500 ${compact ? "text-[9px]" : "text-[10px]"} font-bold tracking-wider pointer-events-none`}>
              {initials}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <EntityRef
                kind="member"
                id={spec.bioguide}
                name={spec.name}
                workspace_id={workspaceId}
                no_link={!!spec.href}
                className={`${compact ? "text-xs" : "text-sm"} font-semibold text-stone-900 truncate`}
              />
              {spec.party && (
                <span className={`text-[10px] px-1 py-0.5 rounded font-bold ${partyClass}`}>
                  {partyKey}
                </span>
              )}
              {spec.state && (
                <span className="text-[10px] text-stone-500 font-mono tabular-nums">
                  {spec.state.toUpperCase()}
                </span>
              )}
            </div>
            {!compact && spec.subtitle && (
              <div className="text-xs text-stone-500 truncate mt-0.5">{spec.subtitle}</div>
            )}
          </div>
        </div>
      );
      if (spec.href) {
        return (
          <a href={spec.href} className="block hover:bg-stone-50 rounded-md px-1 -mx-1 no-underline">
            {Inner}
          </a>
        );
      }
      return <div className="px-1">{Inner}</div>;
    }

    case "carousel":
      return (
        <CarouselNode
          spec={spec}
          workspaceId={workspaceId}
          resolveWidget={resolveWidget}
          fullscreen={fullscreen}
          buildDeckHref={buildDeckHref}
          deckInstanceId={deckInstanceId}
        />
      );

    case "member_card": {
      const partyKey = (spec.party ?? "").toUpperCase();
      const partyClass = PARTY_BADGE[partyKey] ?? "bg-stone-100 text-stone-700";
      const initials = legislatorInitials(spec.name);
      const tierClass = TIER_DOT[spec.tier ?? "cold"] ?? TIER_DOT.cold;
      const Inner = (
        <div className="border border-stone-200 rounded-xl bg-white p-3 flex items-start gap-3">
          <div className="w-12 h-12 rounded-full bg-stone-200 overflow-hidden flex-shrink-0 relative">
            <img
              src={legislatorPhotoUrl(spec.bioguide)}
              alt={spec.name}
              loading="lazy"
              className="w-full h-full object-cover"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
            />
            <div className="absolute inset-0 flex items-center justify-center text-stone-500 text-xs font-bold tracking-wider pointer-events-none">
              {initials}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <EntityRef
                kind="member"
                id={spec.bioguide}
                name={spec.name}
                workspace_id={workspaceId}
                no_link={!!spec.href}
                className="text-base font-semibold text-stone-900 truncate"
              />
              {spec.party && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${partyClass}`}>
                  {partyKey}
                </span>
              )}
              {spec.state && spec.district != null ? (
                <EntityRef
                  kind="district"
                  id={`${spec.state.toUpperCase()}-${String(spec.district).padStart(2, "0")}`}
                  name={`${spec.state.toUpperCase()}-${spec.district === 0 ? "AL" : spec.district}`}
                  workspace_id={workspaceId}
                  className="text-xs text-stone-500 font-mono"
                />
              ) : spec.state ? (
                <EntityRef
                  kind="state"
                  id={spec.state.toUpperCase()}
                  name={spec.state.toUpperCase()}
                  workspace_id={workspaceId}
                  className="text-xs text-stone-500 font-mono"
                />
              ) : null}
            </div>
            {spec.headline && (
              <div className="text-sm text-stone-700 mt-0.5 truncate">{spec.headline}</div>
            )}
            <div className="flex items-center gap-3 mt-1.5 text-xs text-stone-500">
              {spec.tier && (
                <span className="inline-flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${tierClass}`} aria-hidden />
                  <span className="capitalize">{spec.tier}</span>
                  {typeof spec.score === "number" && (
                    <span className="font-mono tabular-nums">({spec.score})</span>
                  )}
                </span>
              )}
              {spec.last_touch && <span>{spec.last_touch}</span>}
            </div>
            {spec.stances && spec.stances.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {spec.stances.map((s, i) => (
                  <span
                    key={i}
                    className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STANCE_TONE[s.tone ?? "default"]}`}
                  >
                    {s.label}
                  </span>
                ))}
              </div>
            )}
            {spec.facts && spec.facts.length > 0 && (
              <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[11px]">
                {spec.facts.map((f, i) => (
                  <Fragment key={i}>
                    <dt className="font-semibold text-stone-600 whitespace-nowrap">{f.label}:</dt>
                    <dd className={`tabular-nums ${STANCE_TONE[f.tone ?? "default"]?.replace(/bg-\S+\s*/g, "") ?? "text-stone-700"}`}>
                      {f.value}
                    </dd>
                  </Fragment>
                ))}
              </dl>
            )}
          </div>
        </div>
      );
      if (spec.href) {
        return (
          <a href={spec.href} className="block no-underline hover:opacity-90">
            {Inner}
          </a>
        );
      }
      return Inner;
    }

    case "heatmap": {
      const cellSize = spec.cell_size ?? 28;
      const colCount = spec.columns.length;
      const rowLabelWidth = 140;
      const colHeaderHeight = 60;
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {spec.title && (
            <div className="px-4 py-2 border-b border-stone-100 text-sm font-semibold text-stone-900">
              {spec.title}
            </div>
          )}
          <div className="overflow-auto p-3">
            <div style={{ minWidth: rowLabelWidth + colCount * cellSize + 8 }}>
              {/* Column headers */}
              <div className="flex" style={{ marginLeft: rowLabelWidth, height: colHeaderHeight, alignItems: "flex-end" }}>
                {spec.columns.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-end justify-center text-[10px] text-stone-600"
                    style={{ width: cellSize, height: colHeaderHeight, paddingBottom: 4 }}
                    title={c.sublabel ? `${c.label} — ${c.sublabel}` : c.label}
                  >
                    <span style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
                      {c.label}
                    </span>
                  </div>
                ))}
              </div>
              {/* Rows */}
              {spec.rows.map((r) => (
                <div key={r.id} className="flex items-center">
                  <div
                    className="text-xs text-stone-700 truncate pr-2 text-right"
                    style={{ width: rowLabelWidth }}
                    title={r.sublabel ? `${r.label} — ${r.sublabel}` : r.label}
                  >
                    <span className="font-medium">{r.label}</span>
                    {r.sublabel && <span className="text-[10px] text-stone-400 ml-1">{r.sublabel}</span>}
                  </div>
                  {spec.columns.map((c) => {
                    const cell = spec.cells[`${r.id}:${c.id}`];
                    return (
                      <div
                        key={c.id}
                        className="border border-white"
                        style={{
                          width: cellSize,
                          height: cellSize,
                          backgroundColor: cell?.fill ?? "#f5f5f4",
                        }}
                        title={cell?.label ?? `${r.label} · ${c.label}${cell?.value != null ? ` · ${cell.value}` : ""}`}
                      >
                        {cell?.value != null && (
                          <div className="w-full h-full flex items-center justify-center text-[9px] font-mono tabular-nums text-stone-700/80">
                            {cell.value}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          {spec.legend && spec.legend.length > 0 && (
            <div className="px-4 py-2 border-t border-stone-100 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-stone-600">
              {spec.legend.map((l, i) => (
                <span key={i} className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: l.color }} aria-hidden />
                  <span>{l.label}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      );
    }

    case "calendar": {
      return <CalendarNode spec={spec} />;
    }

    case "article_summary": {
      const sent = spec.sentiment;
      const hitClass = spec.hit_role ? HIT_ROLE_BADGE[spec.hit_role] : null;
      const leanClass = spec.outlet_lean ? OUTLET_LEAN_TINT[spec.outlet_lean] : null;
      return (
        <article className="border border-stone-200 rounded-xl bg-white overflow-hidden">
          <div className="p-4">
            <div className="flex items-baseline gap-2 flex-wrap mb-1">
              {spec.outlet && (
                <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${leanClass ?? "bg-stone-100 text-stone-700"}`}>
                  {spec.outlet}
                </span>
              )}
              {spec.outlet_classification && (
                <span className="text-[10px] uppercase tracking-wider text-stone-500">
                  {spec.outlet_classification.replace(/_/g, " ")}
                </span>
              )}
              {spec.date && (
                <span className="text-[11px] text-stone-500 font-mono ml-auto">{spec.date}</span>
              )}
            </div>
            <div className="flex items-start gap-2 mt-1">
              {sent && (
                <span
                  className={`w-2 h-2 rounded-full shrink-0 mt-2 ${SENTIMENT_DOT[sent]}`}
                  title={`Sentiment: ${sent}`}
                  aria-hidden
                />
              )}
              <div className="flex-1 min-w-0">
                {spec.href ? (
                  <a href={spec.href} target="_blank" rel="noopener noreferrer" className="text-base font-semibold text-stone-900 hover:text-action hover:underline no-underline">
                    {spec.headline}
                  </a>
                ) : (
                  <span className="text-base font-semibold text-stone-900">{spec.headline}</span>
                )}
                {hitClass && spec.hit_role && (
                  <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${hitClass}`}>
                    {spec.hit_role.replace(/_/g, " ")}
                  </span>
                )}
                {spec.byline && (
                  <div className="text-xs text-stone-500 mt-0.5">{spec.byline}</div>
                )}
              </div>
            </div>
            {spec.summary && (
              <div className="text-sm text-stone-700 leading-relaxed mt-3 whitespace-pre-wrap">{spec.summary}</div>
            )}
            {spec.pullquote && (
              <blockquote className="border-l-4 border-action bg-accent/15 px-4 py-3 mt-4 rounded-r-md">
                <div className="flex items-start gap-3">
                  {spec.pullquote.speaker_bioguide && (
                    <div className="w-9 h-9 rounded-full bg-stone-200 overflow-hidden flex-shrink-0 relative">
                      <img
                        src={legislatorPhotoUrl(spec.pullquote.speaker_bioguide)}
                        alt={spec.pullquote.attribution ?? "speaker"}
                        loading="lazy"
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center text-stone-500 text-[10px] font-bold tracking-wider pointer-events-none">
                        {legislatorInitials(spec.pullquote.attribution ?? "?")}
                      </div>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-stone-800 italic leading-relaxed">"{spec.pullquote.text}"</div>
                    {spec.pullquote.attribution && (
                      <div className="text-xs text-stone-500 mt-1.5">— {spec.pullquote.attribution}</div>
                    )}
                  </div>
                </div>
              </blockquote>
            )}
            {spec.mentioned_legislators && spec.mentioned_legislators.length > 0 && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Mentioned</span>
                <div className="flex -space-x-2">
                  {spec.mentioned_legislators.slice(0, 6).map((m, i) => (
                    <EntityRef
                      key={i}
                      kind="member"
                      id={m.bioguide}
                      name={legislatorInitials(m.name)}
                      workspace_id={workspaceId}
                      className="w-7 h-7 rounded-full bg-stone-200 border-2 border-white overflow-hidden flex items-center justify-center text-[9px] text-stone-500 font-bold align-middle"
                    />
                  ))}
                  {spec.mentioned_legislators.length > 6 && (
                    <div className="w-7 h-7 rounded-full bg-stone-100 border-2 border-white flex items-center justify-center text-[10px] text-stone-600 font-semibold">
                      +{spec.mentioned_legislators.length - 6}
                    </div>
                  )}
                </div>
                <div className="text-xs text-stone-500 truncate flex-1">
                  {spec.mentioned_legislators.slice(0, 3).map((m) => m.name).join(", ")}
                  {spec.mentioned_legislators.length > 3 && ` and ${spec.mentioned_legislators.length - 3} more`}
                </div>
              </div>
            )}
          </div>
        </article>
      );
    }

    case "pullquote": {
      const themeClass = PULLQUOTE_THEME_BORDER[spec.theme ?? "default"];
      const initials = legislatorInitials(spec.speaker_name ?? spec.attribution ?? "?");
      return (
        <blockquote className={`border-l-4 ${themeClass} bg-white border-y border-r border-stone-200 rounded-r-lg px-5 py-4`}>
          <div className="flex items-start gap-4">
            {spec.speaker_bioguide && (
              <div className="w-12 h-12 rounded-full bg-stone-200 overflow-hidden flex-shrink-0 relative">
                <img
                  src={legislatorPhotoUrl(spec.speaker_bioguide)}
                  alt={spec.speaker_name ?? spec.attribution ?? "speaker"}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                />
                <div className="absolute inset-0 flex items-center justify-center text-stone-500 text-xs font-bold tracking-wider pointer-events-none">
                  {initials}
                </div>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-lg leading-snug text-stone-900 italic">
                <span className="text-stone-300 mr-1">“</span>
                {spec.text}
                <span className="text-stone-300 ml-1">”</span>
              </div>
              {(spec.attribution || spec.source_label) && (
                <div className="text-xs text-stone-500 mt-2">
                  {spec.attribution && (
                    <span className="font-semibold">
                      — {spec.speaker_bioguide ? (
                        <EntityRef
                          kind="member"
                          id={spec.speaker_bioguide}
                          name={spec.attribution}
                          workspace_id={workspaceId}
                        />
                      ) : (
                        spec.attribution
                      )}
                    </span>
                  )}
                  {spec.attribution && spec.source_label && <span className="mx-1">·</span>}
                  {spec.source_label && (
                    spec.source_href ? (
                      <a href={spec.source_href} target="_blank" rel="noopener noreferrer" className="text-action hover:underline">
                        {spec.source_label}
                      </a>
                    ) : (
                      <span>{spec.source_label}</span>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </blockquote>
      );
    }

    case "news_feed": {
      const allItems = spec.items ?? [];
      // Cycle mode: show one at a time on rotation.
      const items = cycleSpec && allItems.length > 0
        ? [allItems[cycleIndex % allItems.length]]
        : allItems;
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {spec.title && (
            <div className="px-4 py-2 border-b border-stone-100 text-sm font-semibold text-stone-900 flex items-baseline gap-2">
              <span>{spec.title}</span>
              {items.length > 0 && (
                <span className="text-stone-400 font-normal">({items.length})</span>
              )}
            </div>
          )}
          {items.length === 0 ? (
            <div className="px-4 py-3 text-sm text-stone-500 italic">{spec.empty_label ?? "No mentions in this window."}</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {items.map((it) => {
                const inner = (
                  <div className="px-3 py-2.5 hover:bg-stone-50">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      {it.sentiment && (
                        <span
                          className={`w-2 h-2 rounded-full shrink-0 ${SENTIMENT_DOT[it.sentiment]}`}
                          title={`Sentiment: ${it.sentiment}`}
                          aria-hidden
                        />
                      )}
                      <span className="text-sm font-semibold text-stone-900 flex-1 truncate">{it.headline}</span>
                      {it.hit_role && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider flex-shrink-0 ${HIT_ROLE_BADGE[it.hit_role]}`}>
                          {it.hit_role.replace(/_/g, " ")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-stone-500 mt-0.5 flex items-baseline gap-2 flex-wrap">
                      {it.outlet && <span className="font-semibold text-stone-700">{it.outlet}</span>}
                      {it.date && <span className="font-mono">{it.date}</span>}
                    </div>
                    {it.summary && (
                      <div className="text-xs text-stone-700 mt-1 line-clamp-2">{it.summary}</div>
                    )}
                    {it.mentioned_legislators && it.mentioned_legislators.length > 0 && (
                      <div className="flex -space-x-1.5 mt-1.5">
                        {it.mentioned_legislators.slice(0, 5).map((m, i) => (
                          <EntityRef
                            key={i}
                            kind="member"
                            id={m.bioguide}
                            name={legislatorInitials(m.name)}
                            workspace_id={workspaceId}
                            className="w-5 h-5 rounded-full bg-stone-200 border border-white overflow-hidden flex items-center justify-center text-[7px] text-stone-500 font-bold align-middle"
                          />
                        ))}
                        {it.mentioned_legislators.length > 5 && (
                          <div className="w-5 h-5 rounded-full bg-stone-100 border border-white flex items-center justify-center text-[8px] text-stone-600 font-semibold">
                            +{it.mentioned_legislators.length - 5}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
                return (
                  <li key={it.id}>
                    {it.href ? (
                      <a href={it.href} target="_blank" rel="noopener noreferrer" className="block no-underline text-stone-900">{inner}</a>
                    ) : inner}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      );
    }

    case "vote_record_strip": {
      const votes = spec.votes ?? [];
      const partyKey = (spec.party ?? "").toUpperCase();
      const partyClass = PARTY_BADGE[partyKey] ?? "bg-stone-100 text-stone-700";
      const initials = legislatorInitials(spec.member_name ?? spec.title ?? "?");
      const totals = spec.totals;
      const Inner = (
        <div className="border border-stone-200 rounded-lg bg-white p-3">
          <div className="flex items-center gap-3">
            {spec.bioguide && (
              <div className="w-9 h-9 rounded-full bg-stone-200 overflow-hidden flex-shrink-0 relative">
                <img
                  src={legislatorPhotoUrl(spec.bioguide)}
                  alt={spec.member_name ?? spec.bioguide}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                />
                <div className="absolute inset-0 flex items-center justify-center text-stone-500 text-[10px] font-bold tracking-wider pointer-events-none">
                  {initials}
                </div>
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                {spec.bioguide ? (
                  <EntityRef
                    kind="member"
                    id={spec.bioguide}
                    name={spec.member_name ?? spec.title ?? "(member)"}
                    workspace_id={workspaceId}
                    className="text-sm font-semibold text-stone-900 truncate"
                  />
                ) : (
                  <span className="text-sm font-semibold text-stone-900 truncate">
                    {spec.member_name ?? spec.title ?? "(member)"}
                  </span>
                )}
                {spec.party && (
                  <span className={`text-[10px] px-1 py-0.5 rounded font-bold ${partyClass}`}>{partyKey}</span>
                )}
                {spec.state && (
                  <span className="text-[10px] text-stone-500 font-mono">{spec.state.toUpperCase()}</span>
                )}
              </div>
            </div>
            <div className="flex-1 flex items-center gap-1 ml-3 overflow-x-auto">
              {votes.map((v, i) => (
                <div
                  key={i}
                  className={`w-5 h-7 rounded-sm flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 ${VOTE_FILL[v.vote] ?? VOTE_FILL.missed}`}
                  title={`${v.vote.replace(/_/g, " ")}${v.label ? `: ${v.label}` : ""}${v.date ? ` (${v.date})` : ""}`}
                >
                  {VOTE_LABEL[v.vote] ?? "?"}
                </div>
              ))}
            </div>
            {totals && (
              <div className="flex items-center gap-1.5 text-[11px] font-mono tabular-nums text-stone-600 ml-3 flex-shrink-0">
                {totals.yea != null && <span className="text-emerald-700">Y {totals.yea}</span>}
                {totals.nay != null && <span className="text-red-700">N {totals.nay}</span>}
                {totals.missed != null && <span className="text-stone-500">— {totals.missed}</span>}
                {totals.present != null && <span className="text-amber-600">P {totals.present}</span>}
              </div>
            )}
          </div>
        </div>
      );
      if (spec.href) {
        return <a href={spec.href} className="block no-underline">{Inner}</a>;
      }
      return Inner;
    }

    case "empty_state": {
      const tone = spec.tone ?? "default";
      return (
        <div className={`border-2 border-dashed rounded-xl p-8 text-center ${EMPTY_STATE_TONE[tone]}`}>
          {resolveSpecIcon(spec.icon) && (
            <div className="w-11 h-11 mx-auto mb-3 rounded-full bg-stone-100 text-stone-500 flex items-center justify-center" aria-hidden>
              <SpecIcon icon={spec.icon} size={20} />
            </div>
          )}
          <div className="text-lg font-semibold text-stone-900">{spec.headline}</div>
          {spec.description && (
            <div className="text-sm text-stone-600 mt-2 max-w-md mx-auto">{spec.description}</div>
          )}
          {spec.hint && (
            <div className="text-xs text-stone-500 italic mt-4 max-w-md mx-auto">
              <ArrowRight size={12} className="inline text-stone-400 -mt-0.5" aria-hidden /> {spec.hint}
            </div>
          )}
        </div>
      );
    }

    case "signal_card": {
      const dirMeta = SIGNAL_DIRECTION_TONE[spec.direction] ?? SIGNAL_DIRECTION_TONE.neutral;
      const initials = legislatorInitials(spec.member_name ?? "?");
      return (
        <div className="border border-stone-200 rounded-xl bg-white overflow-hidden">
          <div className={`px-4 py-2 border-b ${dirMeta.fill} flex items-baseline gap-2`}>
            <span className="text-sm font-semibold">{dirMeta.label}</span>
            {spec.subject && <span className="text-xs opacity-70 truncate">on {spec.subject}</span>}
            {spec.status && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ml-auto ${SIGNAL_STATUS_BADGE[spec.status]}`}>
                {spec.status}
              </span>
            )}
          </div>
          <div className="p-4">
            <div className="flex items-start gap-3">
              {spec.member_bioguide && (
                <div className="w-10 h-10 rounded-full bg-stone-200 overflow-hidden flex-shrink-0 relative">
                  <img
                    src={legislatorPhotoUrl(spec.member_bioguide)}
                    alt={spec.member_name ?? spec.member_bioguide}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center text-stone-500 text-[10px] font-bold tracking-wider pointer-events-none">
                    {initials}
                  </div>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  {spec.member_name && (
                    spec.member_bioguide ? (
                      <EntityRef
                        kind="member"
                        id={spec.member_bioguide}
                        name={spec.member_name}
                        workspace_id={workspaceId}
                        className="text-sm font-semibold text-stone-900"
                      />
                    ) : (
                      <span className="text-sm font-semibold text-stone-900">{spec.member_name}</span>
                    )
                  )}
                  {panelText(spec.source) && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-stone-100 text-stone-600 font-semibold">
                      {panelText(spec.source)}
                    </span>
                  )}
                  {spec.date && (
                    <span className="text-[11px] text-stone-500 font-mono ml-auto">{spec.date}</span>
                  )}
                </div>
                {spec.context && (
                  <div className="text-sm text-stone-700 mt-1.5 leading-relaxed line-clamp-3">{spec.context}</div>
                )}
                {typeof spec.confidence === "number" && (
                  <div className="flex items-center gap-2 mt-2.5">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">Confidence</span>
                    <div className="relative h-1.5 w-24 rounded-full bg-stone-100 overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 bg-action"
                        style={{ width: `${Math.max(0, Math.min(1, spec.confidence)) * 100}%` }}
                      />
                    </div>
                    <span className="text-[11px] font-mono tabular-nums text-stone-600">
                      {Math.round(Math.max(0, Math.min(1, spec.confidence)) * 100)}%
                    </span>
                  </div>
                )}
                {spec.source_href && (
                  <div className="mt-2.5">
                    <a href={spec.source_href} target="_blank" rel="noopener noreferrer" className="text-xs text-action hover:underline">
                      View source →
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    case "bill_text_excerpt": {
      const startLine = spec.start_line ?? 1;
      const highlights = spec.highlights ?? [];
      const lineHighlight = (idx: number): string | null => {
        for (const h of highlights) {
          if (idx >= h.from && idx <= h.to) return HIGHLIGHT_TONE[h.tone ?? "default"];
        }
        return null;
      };
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.bill_ref || spec.section_label) && (
            <div className="px-4 py-2 border-b border-stone-100 flex items-baseline gap-2">
              {spec.title && (
                <div className="text-sm font-semibold text-stone-900">{spec.title}</div>
              )}
              {spec.bill_ref && (
                <span className="text-xs font-mono text-stone-500">{spec.bill_ref}</span>
              )}
              {spec.section_label && (
                <span className="text-xs text-stone-500">· {spec.section_label}</span>
              )}
              {spec.source_href && (
                <a href={spec.source_href} target="_blank" rel="noopener noreferrer" className="text-xs text-action hover:underline ml-auto">
                  View full →
                </a>
              )}
            </div>
          )}
          <div className="p-3">
            <pre className="text-xs font-mono text-stone-800 leading-relaxed whitespace-pre-wrap m-0">
              {(spec.lines ?? []).map((line, i) => {
                const highlightClass = lineHighlight(i);
                return (
                  <div key={i} className={`flex ${highlightClass ?? ""}`}>
                    <span className="select-none text-stone-400 pr-3 text-right" style={{ minWidth: "3ch" }}>
                      {startLine + i}
                    </span>
                    <span className="flex-1">{line || " "}</span>
                  </div>
                );
              })}
            </pre>
          </div>
        </div>
      );
    }

    case "event_card": {
      const tone = spec.tone ?? "default";
      const statusClass = spec.status ? EVENT_STATUS_BADGE[spec.status] : null;
      const Inner = (
        <div className={`border border-stone-200 border-l-4 ${EVENT_TONE_BORDER[tone]} rounded-lg bg-white p-4`}>
          <div className="flex items-start gap-3">
            {resolveSpecIcon(spec.icon) && (
              <div className="w-9 h-9 rounded-lg bg-stone-100 text-stone-600 flex items-center justify-center flex-shrink-0" aria-hidden>
                <SpecIcon icon={spec.icon} size={18} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-base font-semibold text-stone-900">{spec.title}</span>
                {statusClass && spec.status && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${statusClass}`}>
                    {spec.status.replace(/_/g, " ")}
                  </span>
                )}
              </div>
              <div className="text-xs text-stone-600 mt-1 flex items-baseline gap-2 flex-wrap">
                {spec.date && <span className="font-mono">{spec.date}</span>}
                {spec.location && <span>· {spec.location}</span>}
                {spec.attendees && <span>· {spec.attendees}</span>}
              </div>
              {spec.description && (
                <div className="text-sm text-stone-700 mt-2 leading-relaxed">{spec.description}</div>
              )}
            </div>
          </div>
        </div>
      );
      if (spec.href) {
        return <a href={spec.href} className="block no-underline">{Inner}</a>;
      }
      return Inner;
    }

    case "tag_cloud": {
      const tags = (spec.tags ?? []).slice(0, spec.max ?? 30);
      // Compute size scale based on count.
      const counts = tags.map((t) => Math.max(1, t.count));
      const minCount = counts.length ? Math.min(...counts) : 1;
      const maxCount = counts.length ? Math.max(...counts) : 1;
      const scale = (n: number): number => {
        if (maxCount === minCount) return 1;
        return (n - minCount) / (maxCount - minCount);
      };
      // Tier mapping: 0–0.2 → small, 0.2–0.5 → medium, 0.5–0.8 → large, 0.8–1 → xlarge
      const sizeClassFor = (n: number): string => {
        const s = scale(n);
        if (s >= 0.8) return "text-base px-3 py-1.5";
        if (s >= 0.5) return "text-sm px-2.5 py-1";
        if (s >= 0.2) return "text-xs px-2 py-0.5";
        return "text-[11px] px-1.5 py-0.5";
      };
      return (
        <div className="border border-stone-200 rounded-lg bg-white p-4">
          {spec.title && (
            <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-3">{spec.title}</div>
          )}
          <div className="flex flex-wrap gap-2 items-center">
            {tags.map((t, i) => {
              const sizeClass = sizeClassFor(t.count);
              const toneClass = TAG_TONE_CHIP[t.tone ?? "default"];
              const inner = (
                <span className={`inline-flex items-center gap-1 rounded font-medium ${sizeClass} ${toneClass}`}>
                  <span>{t.label}</span>
                  <span className="font-mono tabular-nums opacity-70">{t.count}</span>
                </span>
              );
              return t.href ? (
                <a key={i} href={t.href} className="no-underline">{inner}</a>
              ) : (
                <span key={i}>{inner}</span>
              );
            })}
          </div>
        </div>
      );
    }

    case "breadcrumb": {
      const crumbs = spec.crumbs ?? [];
      return (
        <nav className="text-xs text-stone-500" aria-label="Breadcrumb">
          {crumbs.map((c, i) => {
            const isLast = i === crumbs.length - 1;
            return (
              <span key={i}>
                {i > 0 && <span className="mx-1.5 text-stone-300">›</span>}
                {!isLast && c.href ? (
                  <a href={c.href} className="hover:text-stone-800 hover:underline">{c.label}</a>
                ) : (
                  <span className={isLast ? "text-stone-800 font-semibold" : ""}>{c.label}</span>
                )}
              </span>
            );
          })}
        </nav>
      );
    }

    case "avatar_stack": {
      const size = spec.size ?? 32;
      const maxVisible = spec.max_visible ?? 6;
      const visible = spec.members.slice(0, maxVisible);
      const overflow = spec.members.length - visible.length;
      const fontSize = Math.max(8, Math.floor(size / 3));
      return (
        <div className="inline-flex flex-col gap-1">
          {spec.title && (
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
              {spec.title}
            </div>
          )}
          <div className="flex" style={{ marginLeft: 0 }}>
            {visible.map((m, i) => {
              const inner = (
                <div
                  className="rounded-full bg-stone-200 border-2 border-white overflow-hidden relative"
                  style={{ width: size, height: size, marginLeft: i === 0 ? 0 : -size / 4 }}
                  title={m.name}
                >
                  <img
                    src={legislatorPhotoUrl(m.bioguide)}
                    alt={m.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                  />
                  <div
                    className="absolute inset-0 flex items-center justify-center text-stone-500 font-bold tracking-wider pointer-events-none"
                    style={{ fontSize }}
                  >
                    {legislatorInitials(m.name)}
                  </div>
                </div>
              );
              return m.href ? (
                <a key={i} href={m.href} title={m.name}>{inner}</a>
              ) : (
                <div key={i}>{inner}</div>
              );
            })}
            {overflow > 0 && (
              <div
                className="rounded-full bg-stone-100 border-2 border-white flex items-center justify-center text-stone-600 font-semibold"
                style={{
                  width: size,
                  height: size,
                  marginLeft: -size / 4,
                  fontSize: fontSize - 1,
                }}
              >
                +{overflow}
              </div>
            )}
          </div>
          {spec.caption && (
            <div className="text-xs text-stone-500 mt-0.5">{spec.caption}</div>
          )}
        </div>
      );
    }

    case "task_list": {
      const items = spec.items ?? [];
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {spec.title && (
            <div className="px-4 py-2 border-b border-stone-100 text-sm font-semibold text-stone-900">
              {spec.title}
              {items.length > 0 && (
                <span className="text-stone-400 font-normal ml-1">({items.length})</span>
              )}
            </div>
          )}
          {items.length === 0 ? (
            <div className="px-4 py-3 text-sm text-stone-500 italic">{spec.empty_label ?? "No items."}</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {items.map((it) => {
                const status = it.status ?? "open";
                const inner = (
                  <div className={`flex items-start gap-3 px-3 py-2 hover:bg-stone-50 border-l-4 ${TASK_TONE[it.tone ?? "default"]}`}>
                    {(() => {
                      const StatusIcon = TASK_STATUS_ICON[status] ?? Circle;
                      return <StatusIcon size={16} strokeWidth={1.75} className={`mt-0.5 shrink-0 ${TASK_STATUS_COLOR[status]}`} aria-label={status.replace(/_/g, " ")} />;
                    })()}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${status === "done" ? "line-through text-stone-400" : "text-stone-900 font-medium"}`}>
                        {it.title}
                      </div>
                      {it.subtitle && (
                        <div className="text-xs text-stone-500 mt-0.5 line-clamp-2">{it.subtitle}</div>
                      )}
                      <div className="flex items-center gap-2 mt-1 text-[11px]">
                        {it.due && (
                          <span className={`px-1.5 py-0.5 rounded font-mono inline-flex items-center gap-1 ${it.overdue ? "bg-red-100 text-red-800" : "bg-stone-100 text-stone-600"}`}>
                            {it.overdue && <AlertTriangle size={10} aria-hidden />}Due {it.due}
                          </span>
                        )}
                        {it.assignee && (
                          <span className="text-stone-500">· {it.assignee}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
                return (
                  <li key={it.id}>
                    {it.href ? (
                      <a href={it.href} className="block no-underline text-stone-900">{inner}</a>
                    ) : inner}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      );
    }

    case "comparison_table": {
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {spec.title && (
            <div className="px-4 py-2 border-b border-stone-100 text-sm font-semibold text-stone-900">
              {spec.title}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr>
                  <th className="text-left px-3 py-2 border-b border-stone-200 bg-stone-50 font-semibold text-stone-500 uppercase text-[10px]"></th>
                  {spec.columns.map((c) => (
                    <th key={c.key} className="text-left px-3 py-2 border-b border-stone-200 bg-stone-50 align-bottom">
                      <div className="text-sm font-semibold text-stone-900">{c.label}</div>
                      {c.sublabel && <div className="text-[10px] text-stone-500 font-normal mt-0.5">{c.sublabel}</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {spec.rows.map((r) => (
                  <tr key={r.key} className="hover:bg-stone-50 border-b border-stone-100 last:border-b-0">
                    <td className="px-3 py-2 text-xs uppercase tracking-wider font-semibold text-stone-500 align-top">
                      <div>{r.label}</div>
                      {r.sublabel && <div className="text-[10px] font-normal text-stone-400 normal-case mt-0.5">{r.sublabel}</div>}
                    </td>
                    {spec.columns.map((c) => {
                      const cell = spec.cells[`${r.key}:${c.key}`];
                      return (
                        <td key={c.key} className="px-3 py-2 align-top">
                          <div className={`text-sm ${COMPARISON_TONE_TEXT[cell?.tone ?? "default"]}`}>
                            {cell?.text ?? <span className="text-stone-300">—</span>}
                          </div>
                          {cell?.badge && (
                            <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-medium mt-1 ${STANCE_TONE[cell.badge.tone ?? "default"]}`}>
                              {cell.badge.label}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    case "step_progress": {
      const steps = spec.steps ?? [];
      return (
        <div className="border border-stone-200 rounded-lg bg-white p-4">
          {spec.title && (
            <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-3">
              {spec.title}
            </div>
          )}
          <div className="flex items-start">
            {steps.map((s, i) => {
              const isLast = i === steps.length - 1;
              return (
                <div key={i} className="flex items-start flex-1 last:flex-none">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold ${STEP_STATUS_MARKER[s.status]}`}
                    >
                      {(() => {
                        const StepIcon = STEP_STATUS_SYMBOL[s.status] ?? Circle;
                        return <StepIcon size={13} strokeWidth={2.5} aria-hidden />;
                      })()}
                    </div>
                    <div className="text-center mt-1.5 max-w-[110px]">
                      <div className={`text-xs font-semibold ${s.status === "current" ? "text-action" : s.status === "done" ? "text-stone-900" : "text-stone-500"}`}>
                        {s.label}
                      </div>
                      {s.sublabel && <div className="text-[10px] text-stone-500 mt-0.5">{s.sublabel}</div>}
                      {s.date && <div className="text-[10px] text-stone-400 font-mono mt-0.5">{s.date}</div>}
                    </div>
                  </div>
                  {!isLast && (
                    <div className={`flex-1 h-0.5 mt-3.5 mx-1 ${STEP_STATUS_LINE[s.status]}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    case "stat_comparison": {
      const leftTone = spec.left.tone ?? "default";
      const rightTone = spec.right.tone ?? "default";
      return (
        <div className="border border-stone-200 rounded-lg bg-white p-3">
          {spec.title && (
            <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-2">
              {spec.title}
            </div>
          )}
          <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
                {spec.left.label}
              </div>
              <div className={`text-2xl font-bold tabular-nums mt-0.5 ${COMPARISON_TONE_TEXT[leftTone]}`}>
                {spec.left.value}
              </div>
              {spec.left.sublabel && (
                <div className="text-xs text-stone-500 mt-0.5">{spec.left.sublabel}</div>
              )}
            </div>
            <div className="flex flex-col items-center justify-center">
              <span className="text-stone-300 text-base" aria-hidden>→</span>
              {spec.delta && (
                <span className={`text-[10px] px-2 py-0.5 rounded font-semibold mt-1 ${COMPARISON_DELTA_TONE[spec.delta.tone ?? "default"]}`}>
                  {spec.delta.label}
                </span>
              )}
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
                {spec.right.label}
              </div>
              <div className={`text-2xl font-bold tabular-nums mt-0.5 ${COMPARISON_TONE_TEXT[rightTone]}`}>
                {spec.right.value}
              </div>
              {spec.right.sublabel && (
                <div className="text-xs text-stone-500 mt-0.5">{spec.right.sublabel}</div>
              )}
            </div>
          </div>
        </div>
      );
    }

    case "poll_card": {
      const maxPct = Math.max(0, ...spec.results.map((r) => r.pct));
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-stone-100 bg-stone-50">
            <div className="flex items-baseline justify-between gap-3">
              <div className="text-sm font-semibold text-stone-900 truncate">
                {spec.pollster}
                {spec.sponsor && (
                  <span className="font-normal text-stone-500"> / {spec.sponsor}</span>
                )}
              </div>
              {spec.date_range && (
                <div className="text-[11px] text-stone-500 font-mono tabular-nums flex-shrink-0">
                  {spec.date_range}
                </div>
              )}
            </div>
            {(spec.sample || spec.method || spec.margin_of_error) && (
              <div className="text-[11px] text-stone-500 mt-0.5 flex gap-2">
                {spec.sample && <span>{spec.sample}</span>}
                {spec.method && <span>· {spec.method}</span>}
                {spec.margin_of_error && <span>· MoE {spec.margin_of_error}</span>}
              </div>
            )}
          </div>
          {spec.question && (
            <div className="px-4 py-2 text-sm text-stone-700 italic border-b border-stone-100">
              &ldquo;{spec.question}&rdquo;
            </div>
          )}
          <div className="px-4 py-3 space-y-2">
            {spec.results.map((r, i) => {
              const widthPct = maxPct > 0 ? (r.pct / Math.max(maxPct, 1)) * 100 : 0;
              const fill = PARTY_BAR_FILL[r.party ?? ""] ?? "bg-stone-500";
              const tint = PARTY_BAR_TINT[r.party ?? ""] ?? "bg-stone-100";
              const photoSrc = r.photo_url ?? (r.bioguide ? legislatorPhotoUrl(r.bioguide) : null);
              return (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-stone-200 flex-shrink-0 overflow-hidden flex items-center justify-center text-[10px] text-stone-600 font-semibold">
                    {photoSrc ? (
                      <img
                        src={photoSrc}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      legislatorInitials(r.label)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-xs font-medium text-stone-900 truncate">
                        {r.label}
                        {r.party && (
                          <span className={`ml-1.5 text-[10px] font-bold ${PARTY_TEXT[r.party] ?? "text-stone-600"}`}>
                            ({r.party})
                          </span>
                        )}
                        {r.sublabel && (
                          <span className="ml-1.5 text-[10px] text-stone-500 font-normal">{r.sublabel}</span>
                        )}
                      </div>
                      <div className="text-sm font-bold tabular-nums text-stone-900 flex-shrink-0">
                        {r.pct.toFixed(0)}%
                      </div>
                    </div>
                    <div className={`h-1.5 rounded-full mt-0.5 ${tint} overflow-hidden`}>
                      <div className={`h-full ${fill}`} style={{ width: `${widthPct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {spec.source_href && (
            <div className="px-4 py-1.5 border-t border-stone-100 bg-stone-50">
              <a href={spec.source_href} target="_blank" rel="noreferrer" className="text-[11px] text-action hover:underline">
                Full crosstabs &rarr;
              </a>
            </div>
          )}
        </div>
      );
    }

    case "head_to_head": {
      const renderSide = (side: typeof spec.left, align: "left" | "right") => {
        const photoSrc = side.photo_url ?? (side.bioguide ? legislatorPhotoUrl(side.bioguide) : null);
        const partyText = PARTY_TEXT[side.party ?? ""] ?? "text-stone-700";
        const inner = (
          <div className={`flex flex-col items-center gap-1 ${align === "right" ? "text-right" : "text-left"}`}>
            <div className="w-16 h-16 rounded-full bg-stone-200 overflow-hidden flex items-center justify-center text-base text-stone-600 font-semibold border-2 border-white shadow-sm">
              {photoSrc ? (
                <img
                  src={photoSrc}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : (
                legislatorInitials(side.name)
              )}
            </div>
            <div className="text-sm font-semibold text-stone-900 text-center">
              {side.bioguide ? (
                <EntityRef
                  kind="member"
                  id={side.bioguide}
                  name={side.name}
                  workspace_id={workspaceId}
                  no_link={!!side.href}
                />
              ) : (
                side.name
              )}
            </div>
            {side.party && (
              <div className={`text-[10px] font-bold uppercase tracking-wider ${partyText}`}>
                {side.party === "D" ? "Democrat" : side.party === "R" ? "Republican" : side.party}
              </div>
            )}
            {side.sublabel && (
              <div className="text-[11px] text-stone-500 text-center">{side.sublabel}</div>
            )}
            {typeof side.pct === "number" && (
              <div className="text-2xl font-bold tabular-nums text-stone-900 mt-1">
                {side.pct.toFixed(0)}<span className="text-sm font-normal text-stone-500">%</span>
              </div>
            )}
          </div>
        );
        return side.href ? (
          <a href={side.href} className="block no-underline text-stone-900 hover:opacity-80">{inner}</a>
        ) : inner;
      };
      const leftFill = PARTY_BAR_FILL[spec.left.party ?? ""] ?? "bg-stone-500";
      const rightFill = PARTY_BAR_FILL[spec.right.party ?? ""] ?? "bg-stone-500";
      const leftPct = spec.left.pct ?? 50;
      const rightPct = spec.right.pct ?? 50;
      const totalPct = leftPct + rightPct || 100;
      const leftWidth = (leftPct / totalPct) * 100;
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.race_label) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50">
              {spec.title && <div className="text-sm font-semibold text-stone-900">{spec.title}</div>}
              {spec.race_label && <div className="text-[11px] text-stone-500">{spec.race_label}</div>}
              {spec.context && <div className="text-[11px] text-stone-400 mt-0.5">{spec.context}</div>}
            </div>
          )}
          <div className="px-4 py-4 grid grid-cols-[1fr_auto_1fr] gap-4 items-center">
            {renderSide(spec.left, "left")}
            {spec.spread ? (
              <div className={`text-xs font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${SPREAD_TONE[spec.spread.tone ?? "default"]}`}>
                {spec.spread.label}
              </div>
            ) : (
              <div className="text-xs text-stone-400">vs</div>
            )}
            {renderSide(spec.right, "right")}
          </div>
          <div className="px-4 pb-3">
            <div className="flex h-2 rounded-full overflow-hidden bg-stone-100">
              <div className={leftFill} style={{ width: `${leftWidth}%` }} />
              <div className={rightFill} style={{ width: `${100 - leftWidth}%` }} />
            </div>
          </div>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "candidate_card": {
      const photoSrc = spec.photo_url ?? (spec.bioguide ? legislatorPhotoUrl(spec.bioguide) : null);
      const partyText = PARTY_TEXT[spec.party ?? ""] ?? "text-stone-700";
      const partyBadge = PARTY_BADGE[spec.party ?? ""] ?? "bg-stone-100 text-stone-700";
      const inner = (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          <div className="px-4 py-3 flex gap-3 items-start">
            <div className="w-14 h-14 rounded-full bg-stone-200 overflow-hidden flex items-center justify-center text-sm text-stone-600 font-semibold flex-shrink-0">
              {photoSrc ? (
                <img
                  src={photoSrc}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : (
                legislatorInitials(spec.name)
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-sm font-semibold text-stone-900 truncate">
                  {spec.bioguide ? (
                    <EntityRef
                      kind="member"
                      id={spec.bioguide}
                      name={spec.name}
                      workspace_id={workspaceId}
                      no_link={!!spec.href}
                    />
                  ) : (
                    spec.name
                  )}
                </div>
                {spec.party && (
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${partyBadge}`}>{spec.party}</span>
                )}
                {spec.is_incumbent && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                    Incumbent
                  </span>
                )}
              </div>
              {spec.race && <div className={`text-[11px] mt-0.5 ${partyText}`}>{spec.race}</div>}
              {spec.headline && <div className="text-xs text-stone-600 mt-1">{spec.headline}</div>}
            </div>
          </div>
          {spec.stats && spec.stats.length > 0 && (
            <div className="grid border-t border-stone-100" style={{ gridTemplateColumns: `repeat(${spec.stats.length}, 1fr)` }}>
              {spec.stats.map((s, i) => (
                <div key={i} className={`px-3 py-2 ${i > 0 ? "border-l border-stone-100" : ""}`}>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">{s.label}</div>
                  <div className="text-sm font-bold tabular-nums text-stone-900">{s.value}</div>
                  {s.sublabel && <div className="text-[10px] text-stone-500">{s.sublabel}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      );
      return spec.href ? (
        <a href={spec.href} className="block no-underline text-stone-900 hover:opacity-90">{inner}</a>
      ) : inner;
    }

    case "partisan_lean_meter": {
      const max = spec.max ?? 30;
      if (!Number.isFinite(max) || max <= 0 || !Number.isFinite(spec.lean)) {
        return <div role="status" className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600">{spec.label ?? "Partisan lean"}: the recorded value or positive scale is unavailable.</div>;
      }
      const clamped = Math.max(-max, Math.min(max, spec.lean));
      // Map [-max, +max] to [0%, 100%]
      const markerPct = ((clamped / max + 1) / 2) * 100;
      const leanLabel =
        clamped === 0 ? "EVEN" :
        clamped < 0 ? `D+${Math.abs(clamped).toFixed(1)}` :
        `R+${clamped.toFixed(1)}`;
      const leanColor = clamped < 0 ? "text-blue-700" : clamped > 0 ? "text-red-700" : "text-stone-700";
      return (
        <div className="border border-stone-200 rounded-lg bg-white p-3">
          {spec.label && (
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-xs font-semibold text-stone-900">{spec.label}</div>
              <div className={`text-sm font-bold tabular-nums ${leanColor}`}>{leanLabel}</div>
            </div>
          )}
          <div className="relative h-3 rounded-full overflow-hidden" style={{
            background: "linear-gradient(to right, rgb(29 78 216) 0%, rgb(147 197 253) 35%, rgb(231 229 228) 50%, rgb(252 165 165) 65%, rgb(185 28 28) 100%)",
          }}>
            <div
              className="absolute top-0 h-full w-0.5 bg-stone-900"
              style={{ left: `calc(${markerPct}% - 1px)` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-stone-900 border-2 border-white shadow"
              style={{ left: `calc(${markerPct}% - 5px)` }}
            />
          </div>
          <div className="flex justify-between mt-1 text-[10px] font-mono text-stone-500 tabular-nums">
            <span>D+{max}</span>
            <span>EVEN</span>
            <span>R+{max}</span>
          </div>
          {spec.caption && (
            <div className="text-[11px] text-stone-500 mt-1.5">{spec.caption}</div>
          )}
        </div>
      );
    }

    case "favorability_split": {
      const total = Math.max(spec.favorable + spec.neutral + spec.unfavorable, 1);
      const favPct = (spec.favorable / total) * 100;
      const neuPct = (spec.neutral / total) * 100;
      const unfPct = (spec.unfavorable / total) * 100;
      const net = spec.favorable - spec.unfavorable;
      const netLabel = spec.net_label ?? (net === 0 ? "Net 0" : net > 0 ? `Net +${net}` : `Net ${net}`);
      const netColor = net > 0 ? "text-emerald-700" : net < 0 ? "text-red-700" : "text-stone-700";
      return (
        <div className="border border-stone-200 rounded-lg bg-white p-3">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-xs font-semibold text-stone-900">{spec.label ?? "Favorability"}</div>
            <div className={`text-sm font-bold tabular-nums ${netColor}`}>{netLabel}</div>
          </div>
          <div className="flex h-5 rounded overflow-hidden">
            <div className="bg-emerald-500 flex items-center justify-center text-[10px] font-bold text-white" style={{ width: `${favPct}%` }}>
              {favPct >= 12 ? `${spec.favorable}%` : ""}
            </div>
            <div className="bg-stone-300 flex items-center justify-center text-[10px] font-bold text-stone-700" style={{ width: `${neuPct}%` }}>
              {neuPct >= 12 ? `${spec.neutral}%` : ""}
            </div>
            <div className="bg-red-500 flex items-center justify-center text-[10px] font-bold text-white" style={{ width: `${unfPct}%` }}>
              {unfPct >= 12 ? `${spec.unfavorable}%` : ""}
            </div>
          </div>
          <div className="flex justify-between mt-1 text-[10px] text-stone-500">
            <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 align-middle mr-1" />Favorable {spec.favorable}%</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-stone-300 align-middle mr-1" />Neutral {spec.neutral}%</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-red-500 align-middle mr-1" />Unfavorable {spec.unfavorable}%</span>
          </div>
          {spec.footer && (
            <div className="text-[11px] text-stone-500 mt-1.5">{spec.footer}</div>
          )}
        </div>
      );
    }

    case "crosstab_table": {
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.question) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50">
              {spec.title && <div className="text-sm font-semibold text-stone-900">{spec.title}</div>}
              {spec.question && <div className="text-[11px] text-stone-500 italic mt-0.5">&ldquo;{spec.question}&rdquo;</div>}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-200">
                  <th className="text-left px-3 py-2 font-semibold text-stone-700">Group</th>
                  <th className="text-right px-2 py-2 font-semibold text-stone-500 text-[10px] uppercase tracking-wider">n</th>
                  {spec.columns.map((c, i) => (
                    <th key={i} className={`text-right px-3 py-2 font-semibold ${PARTY_TEXT[c.party ?? ""] ?? "text-stone-700"}`}>
                      {c.label}
                      {c.party && <span className="ml-1 text-[10px] font-normal opacity-70">({c.party})</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {spec.rows.map((r, i) => (
                  <tr key={i} className={`border-b border-stone-100 ${i % 2 === 1 ? "bg-stone-50/50" : ""}`}>
                    <td className="px-3 py-1.5 font-medium text-stone-900">{r.label}</td>
                    <td className="px-2 py-1.5 text-right text-stone-500 tabular-nums font-mono">
                      {r.n != null ? r.n : ""}
                    </td>
                    {r.pcts.map((pct, j) => {
                      const col = spec.columns[j];
                      const partyTint = PARTY_BAR_TINT[col?.party ?? ""];
                      // tint the cell with party color, intensity by pct
                      return (
                        <td
                          key={j}
                          className={`px-3 py-1.5 text-right tabular-nums font-mono font-semibold ${PARTY_TEXT[col?.party ?? ""] ?? "text-stone-900"}`}
                          style={partyTint ? { backgroundColor: `rgb(255 255 255 / ${1 - pct / 200})` } : undefined}
                        >
                          {pct.toFixed(0)}%
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">{spec.footer}</div>
          )}
        </div>
      );
    }

    case "electoral_map": {
      const threshold = spec.threshold ?? 270;
      // Compute totals if not provided.
      const computed = spec.states.reduce(
        (acc, s) => {
          if (s.status === "called_d") acc.d += s.electors;
          else if (s.status === "called_r") acc.r += s.electors;
          else if (s.status === "lean_d") acc.lean_d += s.electors;
          else if (s.status === "lean_r") acc.lean_r += s.electors;
          else if (s.status === "tossup") acc.tossup += s.electors;
          else acc.no_data += s.electors;
          return acc;
        },
        { d: 0, r: 0, lean_d: 0, lean_r: 0, tossup: 0, no_data: 0 },
      );
      const totalD = spec.totals?.d ?? computed.d;
      const totalR = spec.totals?.r ?? computed.r;
      const totalTossup = spec.totals?.tossup ?? (computed.tossup + computed.lean_d + computed.lean_r);
      const grandTotal = totalD + totalR + totalTossup + computed.no_data;
      const widthOf = (n: number) => grandTotal > 0 ? (n / grandTotal) * 100 : 0;
      // Sort states alphabetically for the grid.
      const sortedStates = [...spec.states].sort((a, b) => a.abbr.localeCompare(b.abbr));
      // Threshold marker position on the bar (relative to D side).
      const thresholdPct = grandTotal > 0 ? (threshold / grandTotal) * 100 : 50;
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.year) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50 flex items-baseline justify-between gap-2">
              <div className="text-sm font-semibold text-stone-900">
                {spec.title ?? "Electoral college"}
              </div>
              {spec.year && <div className="text-xs text-stone-500">{spec.year}</div>}
            </div>
          )}
          <div className="px-4 py-3">
            <div className="flex items-baseline justify-between mb-1.5">
              <div className="text-sm font-bold tabular-nums text-blue-700">{totalD}</div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500">{threshold} to win</div>
              <div className="text-sm font-bold tabular-nums text-red-700">{totalR}</div>
            </div>
            <div className="relative h-5 rounded-full overflow-hidden bg-stone-100 flex">
              <div className={EC_TALLY_FILL.d} style={{ width: `${widthOf(totalD)}%` }} />
              {computed.lean_d > 0 && (
                <div className={EC_TALLY_FILL.lean_d} style={{ width: `${widthOf(computed.lean_d)}%` }} />
              )}
              {computed.tossup > 0 && (
                <div className={EC_TALLY_FILL.tossup} style={{ width: `${widthOf(computed.tossup)}%` }} />
              )}
              {computed.lean_r > 0 && (
                <div className={EC_TALLY_FILL.lean_r} style={{ width: `${widthOf(computed.lean_r)}%` }} />
              )}
              <div className={EC_TALLY_FILL.r} style={{ width: `${widthOf(totalR)}%` }} />
              <div
                className="absolute top-0 h-full w-0.5 bg-stone-900"
                style={{ left: `calc(${thresholdPct}% - 1px)` }}
                aria-hidden
              />
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-stone-500">
              <span>D needs {Math.max(0, threshold - totalD)}</span>
              {totalTossup > 0 && <span>{totalTossup} tossup/leaning</span>}
              <span>R needs {Math.max(0, threshold - totalR)}</span>
            </div>
          </div>
          <div className="px-4 pb-3 grid grid-cols-9 gap-1">
            {sortedStates.map((s) => (
              <div
                key={s.abbr}
                className={`border rounded px-1 py-1 text-center ${EC_STATUS_FILL[s.status]}`}
                title={`${s.abbr}: ${s.electors} EC · ${s.status}${s.margin != null ? ` (margin ${s.margin > 0 ? "+" : ""}${s.margin})` : ""}`}
              >
                <div className="text-[10px] font-bold">{s.abbr}</div>
                <div className="text-[9px] tabular-nums opacity-80">{s.electors}</div>
              </div>
            ))}
          </div>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "race_rating_strip": {
      const activeIdx = RACE_RATING_ORDER.indexOf(spec.rating);
      const fromIdx = spec.delta ? RACE_RATING_ORDER.indexOf(spec.delta.from) : -1;
      const deltaTone = spec.delta?.tone ?? "default";
      const deltaToneClass =
        deltaTone === "primary" ? "text-action" :
        deltaTone === "warning" ? "text-amber-700" :
        deltaTone === "alert"   ? "text-red-700"   :
        "text-stone-600";
      const arrow = fromIdx < 0 ? "" : fromIdx < activeIdx ? " →R" : fromIdx > activeIdx ? " →D" : "";
      return (
        <div className="border border-stone-200 rounded-lg bg-white p-3">
          {(spec.title || panelText(spec.source) || spec.date) && (
            <div className="flex items-baseline justify-between mb-2 gap-2">
              {spec.title && <div className="text-sm font-semibold text-stone-900 truncate">{spec.title}</div>}
              <div className="text-[11px] text-stone-500 flex-shrink-0">
                {panelText(spec.source)}
                {panelText(spec.source) && spec.date && " · "}
                {spec.date}
              </div>
            </div>
          )}
          <div className="flex h-6 rounded overflow-hidden border border-stone-200">
            {RACE_RATING_ORDER.map((bucket, i) => (
              <div
                key={bucket}
                className={`flex-1 ${RACE_RATING_FILL[bucket]} ${i === activeIdx ? "ring-2 ring-stone-900 ring-inset" : ""} flex items-center justify-center text-[9px] font-bold ${i === 3 ? "text-stone-700" : "text-white"}`}
                title={RACE_RATING_LABEL[bucket]}
              >
                {i === activeIdx ? RACE_RATING_LABEL[bucket].toUpperCase() : ""}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-1 text-[9px] font-semibold uppercase tracking-wider text-stone-500">
            {RACE_RATING_ORDER.map((b) => (
              <span key={b} className="flex-1 text-center">{RACE_RATING_LABEL[b]}</span>
            ))}
          </div>
          {(spec.delta || spec.caption) && (
            <div className="mt-2 flex items-baseline justify-between gap-2 text-xs">
              {spec.caption && <div className="text-stone-600">{spec.caption}</div>}
              {spec.delta && (
                <div className={`text-[11px] font-semibold ${deltaToneClass} flex-shrink-0`}>
                  Moved from {RACE_RATING_LABEL[spec.delta.from]}{arrow}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    case "fundraising_race": {
      const fmt = (n: number) =>
        n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` :
        n >= 1_000 ?     `$${(n / 1_000).toFixed(0)}K` :
                         `$${n.toFixed(0)}`;
      const maxAmt = Math.max(
        1,
        ...spec.periods.flatMap((p) => [p.left_amount, p.right_amount]),
      );
      const leftFill = PARTY_BAR_FILL[spec.left.party ?? ""] ?? "bg-stone-500";
      const rightFill = PARTY_BAR_FILL[spec.right.party ?? ""] ?? "bg-stone-500";
      const renderHeadshot = (side: typeof spec.left) => {
        const photoSrc = side.photo_url ?? (side.bioguide ? legislatorPhotoUrl(side.bioguide) : null);
        return (
          <div className="w-9 h-9 rounded-full bg-stone-200 flex-shrink-0 overflow-hidden flex items-center justify-center text-[10px] text-stone-600 font-semibold border border-white shadow-sm">
            {photoSrc ? (
              <img
                src={photoSrc}
                alt=""
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              legislatorInitials(side.name)
            )}
          </div>
        );
      };
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.race_label) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50">
              {spec.title && <div className="text-sm font-semibold text-stone-900">{spec.title}</div>}
              {spec.race_label && <div className="text-[11px] text-stone-500">{spec.race_label}</div>}
            </div>
          )}
          <div className="px-4 py-3 flex items-center gap-3 border-b border-stone-100">
            {renderHeadshot(spec.left)}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-stone-900 truncate">{spec.left.name}</div>
              {spec.left.party && (
                <div className={`text-[10px] font-bold ${PARTY_TEXT[spec.left.party] ?? "text-stone-700"}`}>
                  {spec.left.party}
                </div>
              )}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-stone-400">vs</div>
            <div className="flex-1 min-w-0 text-right">
              <div className="text-xs font-semibold text-stone-900 truncate">{spec.right.name}</div>
              {spec.right.party && (
                <div className={`text-[10px] font-bold ${PARTY_TEXT[spec.right.party] ?? "text-stone-700"}`}>
                  {spec.right.party}
                </div>
              )}
            </div>
            {renderHeadshot(spec.right)}
          </div>
          <div className="px-4 py-2.5 space-y-1.5">
            {spec.periods.map((p, i) => {
              const leftWidth = (p.left_amount / maxAmt) * 50;
              const rightWidth = (p.right_amount / maxAmt) * 50;
              return (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <div className="flex-1 flex items-center justify-end gap-1.5">
                    <span className="font-mono tabular-nums text-stone-700 text-[11px]">{fmt(p.left_amount)}</span>
                    <div className={`h-3 ${leftFill} rounded-l`} style={{ width: `${leftWidth}%` }} />
                  </div>
                  <div className="w-20 text-center text-[10px] uppercase tracking-wider font-semibold text-stone-500 flex-shrink-0">
                    {p.label}
                  </div>
                  <div className="flex-1 flex items-center gap-1.5">
                    <div className={`h-3 ${rightFill} rounded-r`} style={{ width: `${rightWidth}%` }} />
                    <span className="font-mono tabular-nums text-stone-700 text-[11px]">{fmt(p.right_amount)}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {(spec.totals || spec.coh) && (
            <div className="px-4 py-2 border-t border-stone-100 bg-stone-50 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              {spec.totals && (
                <>
                  <div className="text-left">
                    <span className="text-[10px] uppercase tracking-wider text-stone-500">Cycle total </span>
                    <span className="font-bold tabular-nums text-stone-900">{fmt(spec.totals.left)}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold tabular-nums text-stone-900">{fmt(spec.totals.right)}</span>
                    <span className="text-[10px] uppercase tracking-wider text-stone-500"> cycle total</span>
                  </div>
                </>
              )}
              {spec.coh && (
                <>
                  <div className="text-left">
                    <span className="text-[10px] uppercase tracking-wider text-stone-500">COH </span>
                    <span className="font-bold tabular-nums text-stone-900">{fmt(spec.coh.left)}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold tabular-nums text-stone-900">{fmt(spec.coh.right)}</span>
                    <span className="text-[10px] uppercase tracking-wider text-stone-500"> COH</span>
                  </div>
                </>
              )}
            </div>
          )}
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "donor_breakdown": {
      const fmt = (n: number) =>
        n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` :
        n >= 1_000 ?     `$${(n / 1_000).toFixed(1)}K` :
                         `$${n.toFixed(0)}`;
      const total = spec.segments.reduce((s, x) => s + x.amount, 0);
      const radius = 60;
      const stroke = 22;
      const circ = 2 * Math.PI * radius;
      const SIZE = 160;
      let cumulative = 0;
      const arcs = spec.segments.map((seg, i) => {
        const frac = total > 0 ? seg.amount / total : 0;
        const len = frac * circ;
        const offset = -cumulative;
        cumulative += len;
        const fill = DONOR_SEGMENT_FILL[seg.tone ?? "default"];
        return (
          <circle
            key={i}
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={radius}
            fill="none"
            stroke={fill}
            strokeWidth={stroke}
            strokeDasharray={`${len} ${circ - len}`}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          />
        );
      });
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {spec.title && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50 text-sm font-semibold text-stone-900">
              {spec.title}
            </div>
          )}
          <div className="px-4 py-3 grid grid-cols-[auto_1fr] gap-4 items-center">
            <div className="relative" style={{ width: SIZE, height: SIZE }}>
              <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>{arcs}</svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                <div className="text-lg font-bold tabular-nums text-stone-900">
                  {spec.total_label ?? fmt(total)}
                </div>
                {spec.total_sublabel && (
                  <div className="text-[10px] text-stone-500 mt-0.5">{spec.total_sublabel}</div>
                )}
              </div>
            </div>
            <div className="space-y-1.5 min-w-0">
              {spec.segments.map((seg, i) => {
                const pct = total > 0 ? (seg.amount / total) * 100 : 0;
                return (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span
                      className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: DONOR_SEGMENT_FILL[seg.tone ?? "default"] }}
                    />
                    <span className="flex-1 text-stone-700 truncate">{seg.label}</span>
                    <span className="font-mono tabular-nums text-stone-900 font-semibold">{fmt(seg.amount)}</span>
                    <span className="font-mono tabular-nums text-stone-500 w-10 text-right">{pct.toFixed(0)}%</span>
                  </div>
                );
              })}
            </div>
          </div>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "whip_count_summary": {
      // Per-bucket count uses total_counts override if present, else falls
      // back to the member-sample length. Lets the AI pass "73 yes, here's
      // 6 representatives" instead of "6 yes" when only sampling.
      const bucketCount = (b: keyof typeof spec.buckets): number => {
        const override = spec.total_counts?.[b];
        if (typeof override === "number" && override >= 0) return override;
        return spec.buckets[b]?.length ?? 0;
      };
      const visibleBuckets = WHIP_BUCKET_ORDER.filter((b) => bucketCount(b) > 0);
      const counts: Record<string, number> = {};
      visibleBuckets.forEach((b) => { counts[b] = bucketCount(b); });
      const total = visibleBuckets.reduce((s, b) => s + counts[b], 0);
      const yesTotal = (counts.yes ?? 0) + (counts.lean_yes ?? 0);
      const MAX_AVATARS = 8;
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.bill_ref) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50 flex items-baseline justify-between gap-2">
              <div className="text-sm font-semibold text-stone-900">
                {spec.title ?? "Whip count"}
                {spec.bill_ref && <span className="font-normal text-stone-500 ml-2">{spec.bill_ref}</span>}
              </div>
              {spec.threshold != null && (
                <div className="text-[11px] text-stone-500 font-mono tabular-nums">
                  {yesTotal}/{spec.threshold}
                  {spec.threshold_label ? ` · ${spec.threshold_label}` : " to pass"}
                </div>
              )}
            </div>
          )}
          {spec.threshold != null && total > 0 && (
            <div className="px-4 pt-2.5">
              <div className="relative h-2 rounded-full overflow-hidden bg-stone-100 flex">
                {visibleBuckets.map((b) => (
                  <div
                    key={b}
                    className={WHIP_BUCKET_FILL[b]}
                    style={{ width: `${(counts[b] / total) * 100}%` }}
                  />
                ))}
                <div
                  className="absolute top-0 h-full w-0.5 bg-stone-900"
                  style={{ left: `calc(${Math.min(100, (spec.threshold / total) * 100)}% - 1px)` }}
                  aria-hidden
                />
              </div>
            </div>
          )}
          <div className={`px-4 py-3 grid gap-3`} style={{ gridTemplateColumns: `repeat(${visibleBuckets.length}, 1fr)` }}>
            {visibleBuckets.map((bucket) => {
              const members = spec.buckets[bucket] ?? [];
              const visible = members.slice(0, MAX_AVATARS);
              // Overflow pill reflects the TRUE bucket size (total_counts
              // override if set, else member-sample length), not just the
              // sample we got. So "73 yes, 6 sampled" → "+67" pill.
              const overflow = counts[bucket] - visible.length;
              return (
                <div key={bucket} className="min-w-0">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${WHIP_BUCKET_TEXT[bucket]}`}>
                      {WHIP_BUCKET_LABEL[bucket]}
                    </span>
                    <span className="text-sm font-bold tabular-nums text-stone-900">{counts[bucket]}</span>
                  </div>
                  <div className={`h-1 ${WHIP_BUCKET_FILL[bucket]} rounded-full mb-1.5`} />
                  <div className="flex flex-wrap gap-0.5">
                    {visible.map((m) => (
                      <EntityRef
                        key={m.bioguide}
                        kind="member"
                        id={m.bioguide}
                        name={legislatorInitials(m.name)}
                        workspace_id={workspaceId}
                        className="w-6 h-6 rounded-full bg-stone-200 overflow-hidden flex items-center justify-center text-[8px] text-stone-600 font-semibold border border-white align-middle"
                      />
                    ))}
                    {overflow > 0 && (
                      <div className="w-6 h-6 rounded-full bg-stone-100 flex items-center justify-center text-[9px] text-stone-600 font-semibold border border-white">
                        +{overflow}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "affiliation_list": {
      // Group entries by kind, preserving canonical order.
      const KIND_ORDER: Array<typeof spec.groups[number]["kind"]> =
        ["leadership", "committee", "subcommittee", "caucus", "working_group"];
      const grouped: Record<string, typeof spec.groups> = {};
      spec.groups.forEach((g) => {
        if (!grouped[g.kind]) grouped[g.kind] = [];
        grouped[g.kind].push(g);
      });
      const photoSrc = spec.bioguide ? legislatorPhotoUrl(spec.bioguide) : null;
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.member_name) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50 flex items-center gap-2">
              {photoSrc && (
                <div className="w-7 h-7 rounded-full bg-stone-200 overflow-hidden flex items-center justify-center text-[10px] text-stone-600 font-semibold flex-shrink-0">
                  <img
                    src={photoSrc}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const img = e.currentTarget as HTMLImageElement;
                      img.style.display = "none";
                      if (spec.member_name && img.parentElement) img.parentElement.textContent = legislatorInitials(spec.member_name);
                    }}
                  />
                </div>
              )}
              <div className="min-w-0">
                {spec.title && <div className="text-sm font-semibold text-stone-900 truncate">{spec.title}</div>}
                {spec.member_name && (
                  <div className="text-[11px] text-stone-500 truncate">
                    {spec.bioguide ? (
                      <EntityRef
                        kind="member"
                        id={spec.bioguide}
                        name={spec.member_name}
                        workspace_id={workspaceId}
                      />
                    ) : (
                      spec.member_name
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="divide-y divide-stone-100">
            {KIND_ORDER.filter((k) => grouped[k]?.length).map((kind) => (
              <div key={kind} className="px-4 py-2.5">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <span className="text-sm">{AFFILIATION_KIND_ICON[kind]}</span>
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">
                    {AFFILIATION_KIND_LABEL[kind]}
                  </span>
                  <span className="text-[10px] text-stone-400 font-mono tabular-nums">
                    {grouped[kind]!.length}
                  </span>
                </div>
                <ul className="space-y-1">
                  {grouped[kind]!.map((g, i) => {
                    const inner = (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="flex-1 text-stone-900 truncate">
                          {g.label}
                          {g.sublabel && (
                            <span className="text-stone-500 font-normal ml-1.5">{g.sublabel}</span>
                          )}
                        </span>
                        {g.role && (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${AFFILIATION_ROLE_BADGE[g.role]} flex-shrink-0`}>
                            {AFFILIATION_ROLE_LABEL[g.role]}
                          </span>
                        )}
                      </div>
                    );
                    return (
                      <li key={i}>
                        {g.href ? (
                          <a href={g.href} className="block hover:bg-stone-50 -mx-2 px-2 py-0.5 rounded no-underline text-stone-900">
                            {inner}
                          </a>
                        ) : inner}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 bg-stone-50 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "headline_strip": {
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {spec.title && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50 text-sm font-semibold text-stone-900">
              {spec.title}
            </div>
          )}
          <div className="overflow-x-auto">
            <div className="flex gap-2 px-4 py-3 min-w-min">
              {spec.headlines.map((h, i) => {
                const leanTint = OUTLET_LEAN_TINT[h.outlet_lean ?? "unknown"] ?? OUTLET_LEAN_TINT.unknown;
                const sentDot = HEADLINE_SENTIMENT_DOT[h.sentiment ?? "neutral"] ?? "bg-stone-300";
                const inner = (
                  <div className="border border-stone-200 rounded-lg p-2.5 bg-white w-64 flex-shrink-0 hover:shadow-sm transition-shadow">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${leanTint} truncate`}>
                        {h.outlet}
                      </span>
                      {h.sentiment && (
                        <span className={`w-2 h-2 rounded-full ${sentDot} flex-shrink-0`} title={h.sentiment} />
                      )}
                      {h.date && (
                        <span className="text-[10px] text-stone-400 font-mono ml-auto flex-shrink-0">{h.date}</span>
                      )}
                    </div>
                    <div className="text-xs text-stone-900 line-clamp-3 leading-snug">{h.headline}</div>
                  </div>
                );
                return h.href ? (
                  <a key={i} href={h.href} target="_blank" rel="noreferrer" className="no-underline text-stone-900">
                    {inner}
                  </a>
                ) : <div key={i}>{inner}</div>;
              })}
            </div>
          </div>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "meeting_prep_card": {
      const subj = spec.subject;
      const photoSrc = subj.photo_url ?? (subj.bioguide ? legislatorPhotoUrl(subj.bioguide) : null);
      const partyText = PARTY_TEXT[subj.party ?? ""] ?? "text-stone-700";
      const tempPill = spec.temperature ? TEMPERATURE_PILL[spec.temperature] : null;
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {spec.title && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50 text-sm font-semibold text-stone-900">
              {spec.title}
            </div>
          )}
          <div className="px-4 py-3 flex gap-3 items-start border-b border-stone-100">
            <div className="w-14 h-14 rounded-full bg-stone-200 overflow-hidden flex items-center justify-center text-sm text-stone-600 font-semibold flex-shrink-0">
              {photoSrc ? (
                <img
                  src={photoSrc}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const img = e.currentTarget as HTMLImageElement;
                    img.style.display = "none";
                    if (img.parentElement) img.parentElement.textContent = legislatorInitials(subj.name);
                  }}
                />
              ) : (
                legislatorInitials(subj.name)
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-sm font-semibold text-stone-900">
                  {subj.bioguide ? (
                    <EntityRef
                      kind="member"
                      id={subj.bioguide}
                      name={subj.name}
                      workspace_id={workspaceId}
                    />
                  ) : (
                    subj.name
                  )}
                </div>
                {subj.party && (
                  <span className={`text-[10px] font-bold ${partyText}`}>{subj.party}</span>
                )}
                {(subj.state || subj.district) && (
                  <span className="text-[11px] text-stone-500">
                    {subj.state}{subj.district ? `-${subj.district}` : ""}
                  </span>
                )}
                {tempPill && (
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${tempPill} ml-auto`}>
                    {TEMPERATURE_LABEL[spec.temperature!]}
                  </span>
                )}
              </div>
              {subj.role && <div className="text-[11px] text-stone-500 mt-0.5">{subj.role}</div>}
              {spec.meeting && (
                <div className="text-[11px] text-stone-600 mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  {spec.meeting.when && (
                    <span className="inline-flex items-center gap-1">
                      <Calendar size={11} strokeWidth={1.75} className="text-stone-400" aria-hidden />
                      {spec.meeting.when}
                    </span>
                  )}
                  {spec.meeting.where && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={11} strokeWidth={1.75} className="text-stone-400" aria-hidden />
                      {spec.meeting.where}
                    </span>
                  )}
                  {spec.meeting.duration && (
                    <span className="inline-flex items-center gap-1">
                      <Clock size={11} strokeWidth={1.75} className="text-stone-400" aria-hidden />
                      {spec.meeting.duration}
                    </span>
                  )}
                  {spec.meeting.format && <span className="text-stone-500">· {spec.meeting.format}</span>}
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 divide-x divide-stone-100">
            {spec.their_priorities && spec.their_priorities.length > 0 && (
              <div className="px-4 py-2.5">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1.5">
                  Their priorities
                </div>
                <ul className="text-xs space-y-1 text-stone-700">
                  {spec.their_priorities.map((p, i) => (
                    <li key={i} className="flex gap-1.5"><span className="text-stone-400">•</span><span>{p}</span></li>
                  ))}
                </ul>
              </div>
            )}
            {spec.your_asks && spec.your_asks.length > 0 && (
              <div className="px-4 py-2.5">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-action mb-1.5">
                  Your asks
                </div>
                <ul className="text-xs space-y-1 text-stone-700">
                  {spec.your_asks.map((p, i) => (
                    <li key={i} className="flex gap-1.5"><ArrowRight size={12} strokeWidth={2.25} className="text-action shrink-0 mt-0.5" aria-hidden /><span>{p}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {spec.recent_history && spec.recent_history.length > 0 && (
            <div className="px-4 py-2.5 border-t border-stone-100 bg-stone-50">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1.5">
                Recent history
              </div>
              <ul className="text-xs space-y-1 text-stone-700">
                {spec.recent_history.slice(0, 3).map((h, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="font-mono tabular-nums text-stone-400 flex-shrink-0">{h.date}</span>
                    <span className="flex-1">
                      {h.title}
                      {h.outcome && <span className="text-stone-500 italic"> — {h.outcome}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {spec.watch_out && (
            <div className="px-4 py-2 border-t border-amber-200 bg-amber-50">
              <div className="text-[10px] uppercase tracking-[0.12em] font-medium text-amber-800 mb-0.5 inline-flex items-center gap-1">
                <AlertTriangle size={11} strokeWidth={1.75} aria-hidden />
                Watch out
              </div>
              <div className="text-xs text-amber-900">{spec.watch_out}</div>
            </div>
          )}
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "vote_breakdown_chamber": {
      const ROW_KEYS: Array<keyof typeof spec.totals> = ["yea", "nay", "present", "not_voting"];
      const ROW_LABELS: Record<string, string> = {
        yea: "Yea",
        nay: "Nay",
        present: "Present",
        not_voting: "Not voting",
      };
      const ROW_TONE: Record<string, string> = {
        yea: "border-l-emerald-500",
        nay: "border-l-red-500",
        present: "border-l-amber-400",
        not_voting: "border-l-stone-300",
      };
      const sumRow = (r?: { d?: number; r?: number; i?: number }) =>
        ((r?.d ?? 0) + (r?.r ?? 0) + (r?.i ?? 0));
      const visibleRows = ROW_KEYS.filter((k) => spec.totals[k] && sumRow(spec.totals[k]) > 0);
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-stone-100 bg-stone-50 flex items-baseline justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-stone-900">
                {spec.title ?? "Recorded vote"}
                {spec.vote_ref && <span className="font-normal text-stone-500 ml-2">{spec.vote_ref}</span>}
              </div>
              {spec.date && <div className="text-[11px] text-stone-500">{spec.date}</div>}
            </div>
            {spec.outcome && (
              <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded border ${VOTE_OUTCOME_BADGE[spec.outcome]} flex-shrink-0`}>
                {spec.outcome_label ?? VOTE_OUTCOME_LABEL[spec.outcome]}
              </span>
            )}
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-stone-100 bg-stone-50">
                <th className="text-left px-3 py-1.5 font-semibold text-stone-500 text-[10px] uppercase tracking-wider">Bucket</th>
                <th className="text-right px-2 py-1.5 font-semibold text-blue-700 text-[10px] uppercase tracking-wider">D</th>
                <th className="text-right px-2 py-1.5 font-semibold text-red-700 text-[10px] uppercase tracking-wider">R</th>
                <th className="text-right px-2 py-1.5 font-semibold text-stone-600 text-[10px] uppercase tracking-wider">I</th>
                <th className="text-right px-3 py-1.5 font-semibold text-stone-700 text-[10px] uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((k) => {
                const r = spec.totals[k]!;
                const total = sumRow(r);
                return (
                  <tr key={k} className={`border-b border-stone-100 border-l-4 ${ROW_TONE[k]}`}>
                    <td className="px-3 py-1.5 font-semibold text-stone-900">{ROW_LABELS[k]}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-mono text-blue-700">{r.d ?? 0}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-mono text-red-700">{r.r ?? 0}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-mono text-stone-600">{r.i ?? 0}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-bold font-mono text-stone-900">{total}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {spec.threshold != null && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500 flex items-baseline justify-between">
              <span>{spec.threshold_label ?? "Threshold"}</span>
              <span className="font-mono tabular-nums font-semibold text-stone-700">{spec.threshold}</span>
            </div>
          )}
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "target_quadrant": {
      const W = 480, H = 320, PAD = 24;
      const xRange = spec.x_axis.max - spec.x_axis.min || 1;
      const yRange = spec.y_axis.max - spec.y_axis.min || 1;
      const px = (x: number) => PAD + ((x - spec.x_axis.min) / xRange) * (W - 2 * PAD);
      const py = (y: number) => H - PAD - ((y - spec.y_axis.min) / yRange) * (H - 2 * PAD);
      const midX = (spec.x_axis.min + spec.x_axis.max) / 2;
      const midY = (spec.y_axis.min + spec.y_axis.max) / 2;
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {spec.title && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50 text-sm font-semibold text-stone-900">
              {spec.title}
            </div>
          )}
          <div className="p-4">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 360 }}>
              <rect x={PAD} y={PAD} width={W - 2 * PAD} height={H - 2 * PAD} fill="#fafaf9" stroke="#e7e5e4" />
              <line x1={px(midX)} y1={PAD} x2={px(midX)} y2={H - PAD} stroke="#d6d3d1" strokeDasharray="3 3" />
              <line x1={PAD} y1={py(midY)} x2={W - PAD} y2={py(midY)} stroke="#d6d3d1" strokeDasharray="3 3" />
              {spec.quadrant_labels?.tl && (
                <text x={PAD + 8} y={PAD + 14} fontSize="10" fill="#a8a29e" fontWeight="600">
                  {spec.quadrant_labels.tl}
                </text>
              )}
              {spec.quadrant_labels?.tr && (
                <text x={W - PAD - 8} y={PAD + 14} fontSize="10" fill="#a8a29e" fontWeight="600" textAnchor="end">
                  {spec.quadrant_labels.tr}
                </text>
              )}
              {spec.quadrant_labels?.bl && (
                <text x={PAD + 8} y={H - PAD - 6} fontSize="10" fill="#a8a29e" fontWeight="600">
                  {spec.quadrant_labels.bl}
                </text>
              )}
              {spec.quadrant_labels?.br && (
                <text x={W - PAD - 8} y={H - PAD - 6} fontSize="10" fill="#a8a29e" fontWeight="600" textAnchor="end">
                  {spec.quadrant_labels.br}
                </text>
              )}
              {spec.points.map((p, i) => {
                const cx = px(p.x);
                const cy = py(p.y);
                const r = 4 + (p.size ? (p.size - 1) * 2 : 0);
                const partyColor =
                  p.party === "D" ? "#2563eb" :
                  p.party === "R" ? "#dc2626" :
                  p.party === "I" ? "#78716c" :
                  "#0ea5e9";
                return (
                  <g key={i}>
                    <circle cx={cx} cy={cy} r={r} fill={partyColor} fillOpacity="0.7" stroke={partyColor} strokeWidth="1.5" />
                    <text x={cx + r + 3} y={cy + 3} fontSize="10" fill="#1c1917" fontWeight="500">
                      {p.label}
                    </text>
                  </g>
                );
              })}
              <text x={PAD} y={H - 4} fontSize="9" fill="#78716c" fontWeight="600" textAnchor="start">
                {spec.x_axis.left_label ?? `${spec.x_axis.min}`}
              </text>
              <text x={W - PAD} y={H - 4} fontSize="9" fill="#78716c" fontWeight="600" textAnchor="end">
                {spec.x_axis.right_label ?? `${spec.x_axis.max}`}
              </text>
              <text x={W / 2} y={H - 4} fontSize="10" fill="#44403c" fontWeight="700" textAnchor="middle">
                ↔ {spec.x_axis.label}
              </text>
              <text x={6} y={py(midY)} fontSize="10" fill="#44403c" fontWeight="700" textAnchor="middle" transform={`rotate(-90 6 ${py(midY)})`}>
                ↕ {spec.y_axis.label}
              </text>
            </svg>
          </div>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "coalition_signon_card": {
      const orgs = spec.org_signers ?? [];
      const inds = spec.individual_signers ?? [];
      const MAX_VISIBLE = 16;
      const orgVisible = orgs.slice(0, MAX_VISIBLE);
      const orgOverflow = orgs.length - orgVisible.length;
      const indVisible = inds.slice(0, MAX_VISIBLE);
      const indOverflow = inds.length - indVisible.length;
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-stone-100 bg-stone-50">
            <div className="text-sm font-semibold text-stone-900">{spec.title}</div>
            <div className="text-[11px] text-stone-500 flex flex-wrap gap-x-2 mt-0.5">
              {spec.date && <span>{spec.date}</span>}
              {spec.to && <span>· {spec.to}</span>}
            </div>
          </div>
          {spec.body && (
            <div className="px-4 py-2.5 border-b border-stone-100 text-xs text-stone-700 italic leading-relaxed">
              &ldquo;{spec.body}&rdquo;
            </div>
          )}
          {orgs.length > 0 && (
            <div className="px-4 py-2.5 border-b border-stone-100">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">
                  Organization signers
                </span>
                <span className="text-xs font-bold tabular-nums text-stone-900">{orgs.length}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {orgVisible.map((o, i) => {
                  const inner = (
                    <div className="flex items-center gap-1.5 border border-stone-200 rounded-full pl-0.5 pr-2 py-0.5 hover:bg-stone-50">
                      <div className="w-5 h-5 rounded-full bg-stone-100 overflow-hidden flex items-center justify-center text-[8px] text-stone-500 font-bold">
                        {o.logo_url ? (
                          <img
                            src={o.logo_url}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const img = e.currentTarget as HTMLImageElement;
                              img.style.display = "none";
                              if (img.parentElement) img.parentElement.textContent = o.name.slice(0, 2).toUpperCase();
                            }}
                          />
                        ) : o.name.slice(0, 2).toUpperCase()}
                      </div>
                      <span className="text-[11px] text-stone-700 truncate max-w-[160px]">{o.name}</span>
                    </div>
                  );
                  return o.href ? (
                    <a key={i} href={o.href} className="no-underline">{inner}</a>
                  ) : <div key={i}>{inner}</div>;
                })}
                {orgOverflow > 0 && (
                  <div className="flex items-center gap-1.5 border border-stone-200 rounded-full px-2 py-0.5 bg-stone-50 text-[11px] text-stone-600 font-semibold">
                    +{orgOverflow} more
                  </div>
                )}
              </div>
            </div>
          )}
          {inds.length > 0 && (
            <div className="px-4 py-2.5">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">
                  Individual signers
                </span>
                <span className="text-xs font-bold tabular-nums text-stone-900">{inds.length}</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {indVisible.map((p, i) => {
                  if (p.bioguide) {
                    return (
                      <EntityRef
                        key={i}
                        kind="member"
                        id={p.bioguide}
                        name={legislatorInitials(p.name)}
                        workspace_id={workspaceId}
                        no_link={!!p.href}
                        className="w-7 h-7 rounded-full bg-stone-200 overflow-hidden flex items-center justify-center text-[9px] text-stone-600 font-semibold border border-white align-middle"
                      />
                    );
                  }
                  // No bioguide → fall back to a static circle.
                  const photoSrc = p.photo_url;
                  const inner = (
                    <div
                      className="w-7 h-7 rounded-full bg-stone-200 overflow-hidden flex items-center justify-center text-[9px] text-stone-600 font-semibold border border-white"
                      title={`${p.name}${p.sublabel ? ` · ${p.sublabel}` : ""}`}
                    >
                      {photoSrc ? (
                        <img
                          src={photoSrc}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const img = e.currentTarget as HTMLImageElement;
                            img.style.display = "none";
                            if (img.parentElement) img.parentElement.textContent = legislatorInitials(p.name);
                          }}
                        />
                      ) : (
                        legislatorInitials(p.name)
                      )}
                    </div>
                  );
                  return p.href ? (
                    <a key={i} href={p.href} className="no-underline">{inner}</a>
                  ) : <div key={i}>{inner}</div>;
                })}
                {indOverflow > 0 && (
                  <div className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center text-[10px] text-stone-600 font-semibold border border-white">
                    +{indOverflow}
                  </div>
                )}
              </div>
            </div>
          )}
          {(spec.source_href || spec.footer) && (
            <div className="px-4 py-1.5 border-t border-stone-100 bg-stone-50 flex items-baseline justify-between text-[11px] text-stone-500">
              <span>{spec.footer}</span>
              {spec.source_href && (
                <a href={spec.source_href} target="_blank" rel="noreferrer" className="text-action hover:underline">
                  Read full letter →
                </a>
              )}
            </div>
          )}
        </div>
      );
    }

    case "district_demo_card": {
      const fmtPop = (n: number) =>
        n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)}M` :
        n >= 1_000     ? `${(n / 1_000).toFixed(0)}K` :
                         `${n}`;
      const fmtMoney = (n: number) =>
        n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${n}`;
      const lean = spec.partisan_lean;
      const leanLabel =
        lean == null ? null :
        lean === 0 ? "EVEN" :
        lean < 0 ? `D+${Math.abs(lean).toFixed(1)}` :
        `R+${lean.toFixed(1)}`;
      const leanColor = lean == null ? "" : lean < 0 ? "text-blue-700" : lean > 0 ? "text-red-700" : "text-stone-700";
      const incPartyText = PARTY_TEXT[spec.incumbent?.party ?? ""] ?? "text-stone-700";
      const incPhotoSrc = spec.incumbent?.bioguide ? legislatorPhotoUrl(spec.incumbent.bioguide) : null;
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-stone-100 bg-stone-50 flex items-baseline justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-stone-900">{spec.label}</div>
              {spec.sublabel && <div className="text-[11px] text-stone-500">{spec.sublabel}</div>}
            </div>
            {leanLabel && (
              <div className={`text-sm font-bold tabular-nums ${leanColor} flex-shrink-0`}>{leanLabel}</div>
            )}
          </div>
          {(spec.population != null || spec.median_income != null || spec.bachelors_pct != null) && (
            <div className="grid grid-cols-3 border-b border-stone-100 divide-x divide-stone-100">
              {spec.population != null && (
                <div className="px-3 py-2 text-center">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">Population</div>
                  <div className="text-sm font-bold tabular-nums text-stone-900">{fmtPop(spec.population)}</div>
                </div>
              )}
              {spec.median_income != null && (
                <div className="px-3 py-2 text-center">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">Median income</div>
                  <div className="text-sm font-bold tabular-nums text-stone-900">{fmtMoney(spec.median_income)}</div>
                </div>
              )}
              {spec.bachelors_pct != null && (
                <div className="px-3 py-2 text-center">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">Bachelor's+</div>
                  <div className="text-sm font-bold tabular-nums text-stone-900">{spec.bachelors_pct.toFixed(0)}%</div>
                </div>
              )}
            </div>
          )}
          {spec.race && (
            <div className="px-3 py-2 border-b border-stone-100">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1">Race composition</div>
              <div className="flex h-3 rounded overflow-hidden border border-stone-100">
                {spec.race.white != null && spec.race.white > 0 && (
                  <div className="bg-stone-400" style={{ width: `${spec.race.white}%` }} title={`White ${spec.race.white}%`} />
                )}
                {spec.race.black != null && spec.race.black > 0 && (
                  <div className="bg-amber-700" style={{ width: `${spec.race.black}%` }} title={`Black ${spec.race.black}%`} />
                )}
                {spec.race.hispanic != null && spec.race.hispanic > 0 && (
                  <div className="bg-orange-500" style={{ width: `${spec.race.hispanic}%` }} title={`Hispanic ${spec.race.hispanic}%`} />
                )}
                {spec.race.asian != null && spec.race.asian > 0 && (
                  <div className="bg-emerald-500" style={{ width: `${spec.race.asian}%` }} title={`Asian ${spec.race.asian}%`} />
                )}
                {spec.race.other != null && spec.race.other > 0 && (
                  <div className="bg-purple-400" style={{ width: `${spec.race.other}%` }} title={`Other ${spec.race.other}%`} />
                )}
              </div>
              <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1 text-[10px] text-stone-600">
                {spec.race.white != null && <span><span className="inline-block w-2 h-2 bg-stone-400 align-middle rounded-sm mr-1" />White {spec.race.white}%</span>}
                {spec.race.black != null && <span><span className="inline-block w-2 h-2 bg-amber-700 align-middle rounded-sm mr-1" />Black {spec.race.black}%</span>}
                {spec.race.hispanic != null && <span><span className="inline-block w-2 h-2 bg-orange-500 align-middle rounded-sm mr-1" />Hispanic {spec.race.hispanic}%</span>}
                {spec.race.asian != null && <span><span className="inline-block w-2 h-2 bg-emerald-500 align-middle rounded-sm mr-1" />Asian {spec.race.asian}%</span>}
                {spec.race.other != null && <span><span className="inline-block w-2 h-2 bg-purple-400 align-middle rounded-sm mr-1" />Other {spec.race.other}%</span>}
              </div>
            </div>
          )}
          {spec.urbanicity && (
            <div className="px-3 py-2 border-b border-stone-100">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1">Urbanicity</div>
              <div className="flex h-3 rounded overflow-hidden border border-stone-100">
                {spec.urbanicity.urban != null && (
                  <div className="bg-blue-500" style={{ width: `${spec.urbanicity.urban}%` }} title={`Urban ${spec.urbanicity.urban}%`} />
                )}
                {spec.urbanicity.suburban != null && (
                  <div className="bg-blue-300" style={{ width: `${spec.urbanicity.suburban}%` }} title={`Suburban ${spec.urbanicity.suburban}%`} />
                )}
                {spec.urbanicity.rural != null && (
                  <div className="bg-amber-600" style={{ width: `${spec.urbanicity.rural}%` }} title={`Rural ${spec.urbanicity.rural}%`} />
                )}
              </div>
              <div className="flex flex-wrap gap-x-2 mt-1 text-[10px] text-stone-600">
                {spec.urbanicity.urban != null && <span><span className="inline-block w-2 h-2 bg-blue-500 align-middle rounded-sm mr-1" />Urban {spec.urbanicity.urban}%</span>}
                {spec.urbanicity.suburban != null && <span><span className="inline-block w-2 h-2 bg-blue-300 align-middle rounded-sm mr-1" />Suburban {spec.urbanicity.suburban}%</span>}
                {spec.urbanicity.rural != null && <span><span className="inline-block w-2 h-2 bg-amber-600 align-middle rounded-sm mr-1" />Rural {spec.urbanicity.rural}%</span>}
              </div>
            </div>
          )}
          {spec.incumbent && (
            <div className="px-3 py-2 border-b border-stone-100 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-stone-200 overflow-hidden flex items-center justify-center text-[10px] text-stone-600 font-semibold flex-shrink-0">
                {incPhotoSrc ? (
                  <img
                    src={incPhotoSrc}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const img = e.currentTarget as HTMLImageElement;
                      img.style.display = "none";
                      if (img.parentElement) img.parentElement.textContent = legislatorInitials(spec.incumbent!.name);
                    }}
                  />
                ) : (
                  legislatorInitials(spec.incumbent.name)
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">Incumbent</div>
                <div className="text-xs font-semibold text-stone-900 truncate">
                  {spec.incumbent.name}
                  {spec.incumbent.party && (
                    <span className={`ml-1.5 text-[10px] font-bold ${incPartyText}`}>({spec.incumbent.party})</span>
                  )}
                </div>
              </div>
              {spec.incumbent.last_margin != null && (
                <div className="text-[11px] font-mono tabular-nums text-stone-600 flex-shrink-0">
                  Last margin {spec.incumbent.last_margin > 0 ? "+" : ""}{spec.incumbent.last_margin.toFixed(1)}
                </div>
              )}
            </div>
          )}
          {spec.footer && (
            <div className="px-4 py-1.5 text-[11px] text-stone-500">{spec.footer}</div>
          )}
        </div>
      );
    }

    case "historical_results_strip": {
      const maxAbs = spec.max ?? Math.max(1, ...spec.cycles.map((c) => Math.abs(c.margin)));
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.sublabel) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50">
              {spec.title && <div className="text-sm font-semibold text-stone-900">{spec.title}</div>}
              {spec.sublabel && <div className="text-[11px] text-stone-500">{spec.sublabel}</div>}
            </div>
          )}
          <div className="px-4 py-3 space-y-1.5">
            {spec.cycles.map((c, i) => {
              const fillWidth = (Math.abs(c.margin) / maxAbs) * 50;
              const isD = c.margin < 0;
              const fill = isD ? "bg-blue-600" : c.margin > 0 ? "bg-red-600" : "bg-stone-400";
              const label = c.margin === 0 ? "EVEN" : isD ? `D+${Math.abs(c.margin).toFixed(1)}` : `R+${c.margin.toFixed(1)}`;
              const labelColor = isD ? "text-blue-700" : c.margin > 0 ? "text-red-700" : "text-stone-600";
              return (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <div className="w-14 text-stone-700 font-mono tabular-nums text-[11px] flex-shrink-0">{c.label}</div>
                  <div className="flex-1 flex items-center justify-center relative">
                    <div className="w-full h-3 bg-stone-50 rounded overflow-hidden relative flex">
                      <div className="w-1/2 flex justify-end">
                        {isD && <div className={`h-full ${fill}`} style={{ width: `${(fillWidth / 50) * 100}%` }} />}
                      </div>
                      <div className="w-1/2 flex justify-start">
                        {!isD && c.margin !== 0 && <div className={`h-full ${fill}`} style={{ width: `${(fillWidth / 50) * 100}%` }} />}
                      </div>
                      <div className="absolute top-0 left-1/2 -translate-x-px h-full w-px bg-stone-400" />
                    </div>
                  </div>
                  <div className={`w-16 text-right font-mono tabular-nums font-bold ${labelColor} flex-shrink-0`}>{label}</div>
                  {c.winner && (
                    <div className="w-20 text-[10px] text-stone-500 truncate flex-shrink-0">{c.winner}</div>
                  )}
                </div>
              );
            })}
          </div>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "talking_points_card": {
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.context) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50">
              {spec.title && <div className="text-sm font-semibold text-stone-900">{spec.title}</div>}
              {spec.context && <div className="text-[11px] text-stone-500">{spec.context}</div>}
            </div>
          )}
          <ol className="divide-y divide-stone-100">
            {spec.points.map((p, i) => {
              const bullet = TALKING_POINT_BULLET[p.tone ?? "default"] ?? TALKING_POINT_BULLET.default;
              return (
                <li key={i} className="px-4 py-2.5">
                  <div className="flex gap-2.5 items-start">
                    <div className={`w-5 h-5 rounded-full ${bullet} flex-shrink-0 flex items-center justify-center text-white text-[10px] font-bold tabular-nums mt-0.5`}>
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-stone-900 leading-snug">{p.point}</div>
                      {p.evidence && (
                        <div className="mt-1 text-xs text-stone-600 border-l-2 border-stone-200 pl-2 italic">
                          <BarChart3 size={11} className="inline text-stone-400 mr-1 -mt-0.5" aria-hidden />{p.evidence}
                        </div>
                      )}
                      {p.if_asked && (
                        <div className="mt-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                          <span className="font-semibold">If asked:</span> {p.if_asked}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 bg-stone-50 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "bill_amendment_tree": {
      const renderNode = (node: AmendmentTreeNode, depth: number, key: string) => {
        const partyText = PARTY_TEXT[node.sponsor_party ?? ""] ?? "text-stone-700";
        const inner = (
          <div className="py-1.5 border-b border-stone-100 last:border-b-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-mono font-bold tabular-nums text-stone-700">{node.amendment_ref}</span>
              {node.kind_tag && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">
                  {node.kind_tag}
                </span>
              )}
              {node.status && (
                <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${AMEND_STATUS_BADGE[node.status]}`}>
                  {AMEND_STATUS_LABEL[node.status]}
                </span>
              )}
              {node.vote_outcome && (
                <span className="text-[10px] font-mono tabular-nums text-stone-500">{node.vote_outcome}</span>
              )}
              {node.date && (
                <span className="text-[10px] text-stone-400 font-mono ml-auto">{node.date}</span>
              )}
            </div>
            {node.title && (
              <div className="text-xs text-stone-700 mt-0.5">{node.title}</div>
            )}
            {node.sponsor && (
              <div className="text-[11px] text-stone-500 mt-0.5">
                Sponsor: {node.sponsor_bioguide ? (
                  <EntityRef
                    kind="member"
                    id={node.sponsor_bioguide}
                    name={node.sponsor}
                    workspace_id={workspaceId}
                    className={`font-medium ${partyText}`}
                  />
                ) : (
                  <span className={`font-medium ${partyText}`}>{node.sponsor}</span>
                )}
                {node.sponsor_party && <span className={`ml-1 ${partyText}`}>({node.sponsor_party})</span>}
              </div>
            )}
          </div>
        );
        return (
          <div key={key} style={{ marginLeft: depth * 20 }} className={depth > 0 ? "border-l-2 border-stone-200 pl-3" : ""}>
            {node.href ? (
              <a href={node.href} className="block hover:bg-stone-50 -mx-2 px-2 rounded no-underline text-stone-900">{inner}</a>
            ) : inner}
            {node.children?.map((child, i) => renderNode(child, depth + 1, `${key}-${i}`))}
          </div>
        );
      };
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.bill_ref) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50 flex items-baseline gap-2">
              {spec.title && <div className="text-sm font-semibold text-stone-900">{spec.title}</div>}
              {spec.bill_ref && <div className="text-[11px] text-stone-500 font-mono">{spec.bill_ref}</div>}
            </div>
          )}
          <div className="px-4 py-2">
            {spec.amendments.map((a, i) => renderNode(a, 0, `a${i}`))}
          </div>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 bg-stone-50 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "kpi_grid": {
      const cols = spec.cols ?? 4;
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.context) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50 flex items-baseline justify-between gap-2">
              {spec.title && <div className="text-sm font-semibold text-stone-900">{spec.title}</div>}
              {spec.context && <div className="text-[11px] text-stone-500">{spec.context}</div>}
            </div>
          )}
          <div className="grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
            {spec.kpis.map((k, i) => {
              const tone = k.tone ?? "default";
              const inner = (
                <div className={`px-3 py-2.5 ${KPI_TONE_BG[tone]} h-full`}>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 truncate">
                    {k.label}
                  </div>
                  <div className="text-xl font-bold tabular-nums text-stone-900 mt-0.5">{k.value}</div>
                  {(k.delta || k.comparison) && (
                    <div className="text-[11px] mt-0.5 flex items-baseline gap-1">
                      {k.delta && (
                        <span className={`font-semibold ${KPI_DELTA_TONE[k.delta_tone ?? "neutral"]}`}>{k.delta}</span>
                      )}
                      {k.comparison && (
                        <span className="text-stone-500">{k.comparison}</span>
                      )}
                    </div>
                  )}
                  {k.sparkline && k.sparkline.length > 0 && (() => {
                    const min = Math.min(...k.sparkline);
                    const max = Math.max(...k.sparkline);
                    const range = max - min || 1;
                    const points = k.sparkline.map((v, idx) => {
                      const x = (idx / Math.max(1, k.sparkline!.length - 1)) * 80;
                      const y = 16 - ((v - min) / range) * 16;
                      return `${x.toFixed(1)},${y.toFixed(1)}`;
                    }).join(" ");
                    return (
                      <svg viewBox="0 0 80 16" className="w-full h-4 mt-1" preserveAspectRatio="none">
                        <polyline fill="none" stroke="currentColor" strokeWidth="1" points={points} className="text-stone-400" />
                      </svg>
                    );
                  })()}
                </div>
              );
              return (
                <div key={i} className={`border-stone-100 ${i % cols !== cols - 1 ? "border-r" : ""} ${i >= cols ? "border-t" : ""}`}>
                  {k.href ? (
                    <a href={k.href} className="block no-underline text-stone-900 hover:bg-stone-50">{inner}</a>
                  ) : inner}
                </div>
              );
            })}
          </div>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "gap_chart": {
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.sublabel) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50">
              {spec.title && <div className="text-sm font-semibold text-stone-900">{spec.title}</div>}
              {spec.sublabel && <div className="text-[11px] text-stone-500">{spec.sublabel}</div>}
            </div>
          )}
          <div className="px-4 py-3 space-y-2.5">
            {spec.goals.map((g, i) => {
              const tone = g.tone ?? (g.current >= g.target ? "success" : g.current >= g.target * 0.75 ? "primary" : g.current >= g.target * 0.5 ? "warning" : "alert");
              const fill = GAP_BAR_FILL[tone];
              const tint = GAP_BAR_TINT[tone];
              const max = Math.max(g.target, g.current);
              const fillPct = max > 0 ? (g.current / max) * 100 : 0;
              const targetPct = max > 0 ? (g.target / max) * 100 : 0;
              const pctOfTarget = g.target > 0 ? (g.current / g.target) * 100 : 0;
              const fmt = (n: number) => n >= 1000 ? n.toLocaleString() : `${n}`;
              return (
                <div key={i}>
                  <div className="flex items-baseline justify-between mb-1 gap-2">
                    <span className="text-xs font-semibold text-stone-900 truncate">{g.label}</span>
                    <span className="text-[11px] font-mono tabular-nums text-stone-500 flex-shrink-0">
                      <span className="font-bold text-stone-900">{fmt(g.current)}{g.unit ?? ""}</span>
                      {" / "}
                      {fmt(g.target)}{g.unit ?? ""}
                      {" "}
                      <span className="text-stone-400">({pctOfTarget.toFixed(0)}%)</span>
                    </span>
                  </div>
                  <div className={`relative h-3 rounded ${tint} overflow-hidden`}>
                    <div className={`h-full ${fill}`} style={{ width: `${fillPct}%` }} />
                    <div
                      className="absolute top-0 h-full w-0.5 bg-stone-900"
                      style={{ left: `calc(${targetPct}% - 1px)` }}
                      aria-hidden
                    />
                  </div>
                  {g.eta && (
                    <div className="text-[10px] text-stone-500 mt-0.5">{g.eta}</div>
                  )}
                </div>
              );
            })}
          </div>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "engagement_funnel_card": {
      const max = Math.max(1, ...spec.stages.map((s) => s.count));
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.sublabel) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50">
              {spec.title && <div className="text-sm font-semibold text-stone-900">{spec.title}</div>}
              {spec.sublabel && <div className="text-[11px] text-stone-500">{spec.sublabel}</div>}
            </div>
          )}
          <div className="px-4 py-3 space-y-1.5">
            {spec.stages.map((stage, i) => {
              const tone = stage.tone ?? "primary";
              const fill = GAP_BAR_FILL[tone];
              const widthPct = (stage.count / max) * 100;
              const conv = i === 0 ? null
                : stage.conversion_pct != null ? stage.conversion_pct
                : spec.stages[i - 1].count > 0 ? (stage.count / spec.stages[i - 1].count) * 100
                : null;
              const inner = (
                <div className="flex items-center gap-2">
                  <div className="w-32 text-xs font-medium text-stone-900 truncate flex-shrink-0">{stage.label}</div>
                  <div className="flex-1 h-6 bg-stone-50 rounded relative overflow-hidden">
                    <div
                      className={`h-full ${fill} flex items-center pl-2 text-white text-xs font-bold`}
                      style={{ width: `${widthPct}%`, minWidth: 28 }}
                    >
                      {stage.count.toLocaleString()}
                    </div>
                  </div>
                  {conv != null && (
                    <div className="w-16 text-right text-[11px] text-stone-500 tabular-nums flex-shrink-0">
                      → {conv.toFixed(0)}%
                    </div>
                  )}
                </div>
              );
              return (
                <div key={i}>
                  {stage.href ? (
                    <a href={stage.href} className="block no-underline text-stone-900 hover:bg-stone-50 rounded -mx-2 px-2">
                      {inner}
                    </a>
                  ) : inner}
                </div>
              );
            })}
          </div>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "press_release_card": {
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-100 bg-stone-50 flex items-baseline justify-between gap-2">
            {spec.tag && (
              <div className="text-[10px] font-bold uppercase tracking-widest text-stone-700">
                {spec.tag}
              </div>
            )}
            {spec.status && (
              <span className={`text-[9px] font-bold tracking-wider px-2 py-0.5 rounded ${PR_STATUS_PILL[spec.status]}`}>
                {PR_STATUS_LABEL[spec.status]}
              </span>
            )}
          </div>
          <div className="px-6 py-4">
            <h2 className="text-lg font-bold text-stone-900 leading-tight">{spec.headline}</h2>
            {spec.subheadline && (
              <h3 className="text-sm text-stone-600 mt-1 leading-snug">{spec.subheadline}</h3>
            )}
            {spec.dateline && (
              <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 mt-2">
                {spec.dateline}
              </div>
            )}
            <p className="text-sm text-stone-800 mt-3 leading-relaxed font-medium">
              {spec.lede}
            </p>
            {spec.body?.map((p, i) => (
              <p key={i} className="text-sm text-stone-700 mt-2 leading-relaxed">{p}</p>
            ))}
            {spec.quotes?.map((q, i) => (
              <blockquote key={i} className="mt-3 border-l-4 border-stone-300 pl-3">
                <p className="text-sm text-stone-700 italic">&ldquo;{q.quote}&rdquo;</p>
                <footer className="text-[11px] text-stone-500 mt-1">
                  — <span className="font-semibold text-stone-700">{q.speaker}</span>
                  {q.role && <span>, {q.role}</span>}
                </footer>
              </blockquote>
            ))}
          </div>
          {(spec.boilerplate || spec.contact) && (
            <div className="px-6 py-3 border-t border-stone-100 bg-stone-50 space-y-2">
              {spec.boilerplate && (
                <p className="text-[11px] text-stone-500 italic leading-relaxed">{spec.boilerplate}</p>
              )}
              {spec.contact && (
                <div className="text-[11px] text-stone-600">
                  <span className="font-semibold text-stone-700 uppercase tracking-wider">Contact:</span>{" "}
                  {spec.contact.name}
                  {spec.contact.title && <span>, {spec.contact.title}</span>}
                  {spec.contact.email && <span> · {spec.contact.email}</span>}
                  {spec.contact.phone && <span> · {spec.contact.phone}</span>}
                </div>
              )}
            </div>
          )}
          {spec.footer && (
            <div className="px-5 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "decision_matrix": {
      const totalWeight = spec.criteria.reduce((s, c) => s + (c.weight ?? 1), 0);
      const totals = spec.options.map((o) =>
        o.scores.reduce((s, score, i) => s + score * (spec.criteria[i]?.weight ?? 1), 0)
      );
      const recIdx = spec.recommended_index ?? totals.indexOf(Math.max(...totals));
      // Heat color for cell intensity
      const heat = (s: number) => {
        if (s >= 8) return "bg-emerald-100 text-emerald-900";
        if (s >= 6) return "bg-emerald-50 text-emerald-800";
        if (s >= 4) return "bg-stone-50 text-stone-700";
        if (s >= 2) return "bg-amber-50 text-amber-800";
        return "bg-red-50 text-red-800";
      };
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.question) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50">
              {spec.title && <div className="text-sm font-semibold text-stone-900">{spec.title}</div>}
              {spec.question && <div className="text-[11px] text-stone-500 italic mt-0.5">&ldquo;{spec.question}&rdquo;</div>}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-200">
                  <th className="text-left px-3 py-2 font-semibold text-stone-700 sticky left-0 bg-stone-50">Option</th>
                  {spec.criteria.map((c, i) => (
                    <th key={i} className="text-center px-2 py-2 font-semibold text-stone-700">
                      <div>{c.label}</div>
                      {c.weight != null && c.weight !== 1 && (
                        <div className="text-[9px] text-stone-500 font-normal">×{c.weight}</div>
                      )}
                    </th>
                  ))}
                  <th className="text-right px-3 py-2 font-semibold text-stone-700">Weighted</th>
                </tr>
              </thead>
              <tbody>
                {spec.options.map((o, i) => (
                  <tr key={i} className={`border-b border-stone-100 ${i === recIdx ? "bg-emerald-50/30" : ""}`}>
                    <td className="px-3 py-2 sticky left-0 bg-white">
                      <div className="flex items-center gap-1.5">
                        {i === recIdx && <Star size={12} fill="currentColor" className="text-amber-500 shrink-0" aria-label="Recommended" />}
                        <div className="min-w-0">
                          <div className="font-semibold text-stone-900">{o.label}</div>
                          {o.sublabel && <div className="text-[10px] text-stone-500">{o.sublabel}</div>}
                        </div>
                      </div>
                    </td>
                    {o.scores.map((score, j) => (
                      <td key={j} className={`text-center px-2 py-2 tabular-nums font-mono font-semibold ${heat(score)}`}>
                        {score}
                      </td>
                    ))}
                    <td className="text-right px-3 py-2 tabular-nums font-mono font-bold text-stone-900">
                      {totals[i].toFixed(1)}
                      <span className="text-[9px] text-stone-400 font-normal ml-1">/ {(totalWeight * 10).toFixed(0)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {spec.options.some((o) => o.note) && (
            <div className="px-4 py-2 border-t border-stone-100 bg-stone-50 space-y-1 text-[11px] text-stone-600">
              {spec.options.map((o, i) => o.note ? (
                <div key={i}>
                  <span className="font-semibold text-stone-700">{o.label}:</span> {o.note}
                </div>
              ) : null)}
            </div>
          )}
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "donor_card": {
      const fmt = (n: number) =>
        n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` :
        n >= 1_000 ?     `$${(n / 1_000).toFixed(1)}K` :
                         `$${n.toFixed(0)}`;
      const initials = spec.name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0])
        .join("")
        .toUpperCase();
      const tier = spec.tier_tone ?? "default";
      const inner = (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          <div className="px-4 py-3 flex gap-3 items-start">
            <div className="w-12 h-12 rounded-full bg-stone-200 overflow-hidden flex items-center justify-center text-sm text-stone-600 font-semibold flex-shrink-0">
              {spec.photo_url ? (
                <img
                  src={spec.photo_url}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const img = e.currentTarget as HTMLImageElement;
                    img.style.display = "none";
                    if (img.parentElement) img.parentElement.textContent = initials;
                  }}
                />
              ) : initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-sm font-semibold text-stone-900 truncate">{spec.name}</div>
                {spec.tier && (
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${DONOR_TIER_BADGE[tier]}`}>
                    {spec.tier}
                  </span>
                )}
                {spec.party && (
                  <span className={`text-[10px] font-bold ${PARTY_TEXT[spec.party] ?? "text-stone-700"}`}>{spec.party}</span>
                )}
              </div>
              {(spec.occupation || spec.employer) && (
                <div className="text-[11px] text-stone-600 mt-0.5">
                  {spec.occupation}
                  {spec.occupation && spec.employer && " · "}
                  {spec.employer}
                </div>
              )}
              {spec.location && (
                <div className="text-[11px] text-stone-500 flex items-center gap-1"><MapPin size={11} className="shrink-0" aria-hidden />{spec.location}</div>
              )}
            </div>
          </div>
          {(spec.lifetime_total != null || spec.cycle_total != null || spec.last_gift) && (
            <div className="grid grid-cols-3 border-t border-stone-100 divide-x divide-stone-100">
              {spec.lifetime_total != null && (
                <div className="px-3 py-2 text-center">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">Lifetime</div>
                  <div className="text-sm font-bold tabular-nums text-stone-900">{fmt(spec.lifetime_total)}</div>
                </div>
              )}
              {spec.cycle_total != null && (
                <div className="px-3 py-2 text-center">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">This cycle</div>
                  <div className="text-sm font-bold tabular-nums text-stone-900">{fmt(spec.cycle_total)}</div>
                </div>
              )}
              {spec.last_gift && (
                <div className="px-3 py-2 text-center">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">Last gift</div>
                  <div className="text-sm font-bold tabular-nums text-stone-900">{fmt(spec.last_gift.amount)}</div>
                  <div className="text-[10px] text-stone-500">{spec.last_gift.date}</div>
                </div>
              )}
            </div>
          )}
          {spec.giving_history && spec.giving_history.length > 1 && (() => {
            const min = Math.min(...spec.giving_history);
            const max = Math.max(...spec.giving_history);
            const range = max - min || 1;
            const points = spec.giving_history.map((v, idx) => {
              const x = (idx / Math.max(1, spec.giving_history!.length - 1)) * 200;
              const y = 28 - ((v - min) / range) * 28;
              return `${x.toFixed(1)},${y.toFixed(1)}`;
            }).join(" ");
            return (
              <div className="px-4 py-2 border-t border-stone-100">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1">Giving history</div>
                <svg viewBox="0 0 200 28" className="w-full h-7" preserveAspectRatio="none">
                  <polyline fill="none" stroke="#10b981" strokeWidth="1.5" points={points} />
                </svg>
              </div>
            );
          })()}
          {spec.suggested_ask && (
            <div className="px-4 py-2 border-t border-stone-100 bg-accent/20">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-action">Suggested ask</div>
              <div className="text-sm font-bold tabular-nums text-ink">{fmt(spec.suggested_ask.amount)}</div>
              {spec.suggested_ask.reason && (
                <div className="text-[11px] text-action/80 italic">{spec.suggested_ask.reason}</div>
              )}
            </div>
          )}
          {spec.notes && (
            <div className="px-4 py-2 border-t border-stone-100 text-[11px] text-stone-600 italic">
              {spec.notes}
            </div>
          )}
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 bg-stone-50 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
      return spec.href ? <a href={spec.href} className="block no-underline text-stone-900 hover:opacity-90">{inner}</a> : inner;
    }

    case "delegation_map_card": {
      const senators = spec.senators ?? [];
      const reps = [...(spec.representatives ?? [])].sort((a, b) => {
        const an = parseInt(a.district, 10);
        const bn = parseInt(b.district, 10);
        if (isNaN(an) && isNaN(bn)) return a.district.localeCompare(b.district);
        if (isNaN(an)) return 1;
        if (isNaN(bn)) return -1;
        return an - bn;
      });
      const renderMember = (m: { bioguide: string; name: string; party?: string; role?: string; sublabel?: string; href?: string; district?: string }) => {
        const photoSrc = legislatorPhotoUrl(m.bioguide);
        const partyBorder = PARTY_BORDER[m.party ?? ""] ?? "border-l-stone-300";
        const partyText = PARTY_TEXT[m.party ?? ""] ?? "text-stone-700";
        const roleBadge = m.role ? DELEGATION_ROLE_BADGE[m.role] : null;
        const inner = (
          <div className={`border-l-4 ${partyBorder} bg-white border-y border-r border-stone-200 rounded-r p-2 hover:bg-stone-50 transition-colors`}>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-stone-200 overflow-hidden flex items-center justify-center text-[10px] text-stone-600 font-semibold flex-shrink-0">
                <img
                  src={photoSrc}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const img = e.currentTarget as HTMLImageElement;
                    img.style.display = "none";
                    if (img.parentElement) img.parentElement.textContent = legislatorInitials(m.name);
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 flex-wrap">
                  {m.district != null && (
                    <span className="text-[10px] font-mono font-bold tabular-nums text-stone-500">
                      {m.district}
                    </span>
                  )}
                  <EntityRef
                    kind="member"
                    id={m.bioguide}
                    name={m.name}
                    workspace_id={workspaceId}
                    no_link={!!m.href}
                    className="text-xs font-semibold text-stone-900 truncate"
                  />
                  {m.party && (
                    <span className={`text-[10px] font-bold ${partyText}`}>{m.party}</span>
                  )}
                </div>
                {m.role && roleBadge && (
                  <span className={`inline-block text-[9px] font-semibold uppercase tracking-wider px-1 py-0 rounded ${roleBadge} mt-0.5`}>
                    {DELEGATION_ROLE_LABEL[m.role]}
                  </span>
                )}
                {m.sublabel && (
                  <div className="text-[10px] text-stone-500 truncate mt-0.5">{m.sublabel}</div>
                )}
              </div>
            </div>
          </div>
        );
        return m.href ? (
          <a key={m.bioguide} href={m.href} className="no-underline text-stone-900">{inner}</a>
        ) : <div key={m.bioguide}>{inner}</div>;
      };
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          <div className="px-4 py-2 border-b border-stone-100 bg-stone-50 flex items-baseline justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-stone-900 truncate">
                {spec.title ?? `${spec.state} delegation`}
              </div>
              {spec.state_label && <div className="text-[11px] text-stone-500">{spec.state_label}</div>}
            </div>
            {spec.party_split && (
              <div className="text-[11px] font-mono tabular-nums flex gap-1.5 flex-shrink-0">
                <span className="text-blue-700 font-semibold">D {spec.party_split.d}</span>
                <span className="text-red-700 font-semibold">R {spec.party_split.r}</span>
                {spec.party_split.i > 0 && <span className="text-stone-700 font-semibold">I {spec.party_split.i}</span>}
              </div>
            )}
          </div>
          {senators.length > 0 && (
            <div className="px-3 py-3 border-b border-stone-100">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1.5">
                Senators
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {senators.map(renderMember)}
              </div>
            </div>
          )}
          {reps.length > 0 && (
            <div className="px-3 py-3">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1.5">
                Representatives ({reps.length})
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5">
                {reps.map(renderMember)}
              </div>
            </div>
          )}
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 bg-stone-50 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "cosponsor_growth_chart": {
      const W = 480, H = 140, PAD_L = 36, PAD_R = 12, PAD_T = 12, PAD_B = 22;
      const series = spec.series;
      if (series.length === 0) {
        return <div className="border border-stone-200 rounded-lg bg-white p-3 text-xs text-stone-500">No data</div>;
      }
      // Total cosponsor count per snapshot. Party composition lives on
      // every cosponsor's chip/card elsewhere — encoding it twice as a
      // colored stacked area was chartjunk (and the legend was prone to
      // inverting against the visual area ordering). One neutral curve
      // makes the accumulation shape itself the load-bearing signal.
      const totals = series.map((s) => s.d_count + s.r_count + (s.i_count ?? 0));
      const lastTotal = totals[totals.length - 1];
      const maxTotal = Math.max(spec.threshold ?? 0, ...totals);
      const xAt = (i: number) =>
        PAD_L + (i / Math.max(1, series.length - 1)) * (W - PAD_L - PAD_R);
      const yAt = (v: number) =>
        H - PAD_B - (v / Math.max(1, maxTotal)) * (H - PAD_T - PAD_B);

      // Single neutral area + crisp line on top.
      const linePoints = series
        .map((_, i) => `${xAt(i)},${yAt(totals[i])}`)
        .join(" ");
      const areaPath =
        `M ${xAt(0)},${H - PAD_B} ` +
        series.map((_, i) => `L ${xAt(i)},${yAt(totals[i])}`).join(" ") +
        ` L ${xAt(series.length - 1)},${H - PAD_B} Z`;

      // Day-since-introduction axis labels. Parse the series dates as
      // ISO timestamps; if they parse, compute days from the first
      // point. Falls back to raw date strings when dates aren't
      // parseable (legacy / pre-formatted labels).
      const DAY_MS = 86400000;
      const firstMs = Date.parse(series[0].date);
      const useDayAxis = Number.isFinite(firstMs);
      const dayLabel = (idx: number): string => {
        if (useDayAxis) {
          const ms = Date.parse(series[idx].date);
          if (Number.isFinite(ms)) {
            const d = Math.round((ms - firstMs) / DAY_MS);
            return `Day ${d}`;
          }
        }
        return series[idx].date;
      };
      const annoToneColor: Record<string, string> = {
        default: "#a8a29e",
        primary: "#2563eb",
        warning: "#d97706",
        alert:   "#dc2626",
      };
      // ── Annotation layout pass — collision-avoiding placement ───────
      //
      // The naive y = PAD_T + 8 + (i % 3) * 11 placement caused:
      //   1. Labels on close-date annotations stacking on top of each
      //      other (same x, only y differs across band cycle).
      //   2. Labels near the right edge clipped by the viewBox
      //      ("Saturation" rendered as "Sa…").
      //   3. Annotation labels overlapping the threshold ("cloture
      //      floor") label rendered at the right edge.
      //
      // Layout strategy:
      //   - Right-half annotations anchor to "end" so the label extends
      //     leftward from the vertical line, never past the right edge.
      //   - For each annotation, try y-bands top-down; first band whose
      //     bounding rect doesn't overlap any previously placed rect
      //     (annotation labels OR the threshold label) is taken.
      //   - Up to 6 bands; if all collide, fall through to the bottom
      //     band rather than dropping the label entirely.
      const ANNO_FS = 9;
      const ANNO_CHAR_W = 5.4; // approximate average glyph width at fs=9
      const ANNO_LINE_H = 11;
      const ANNO_MAX_BANDS = 6;
      const ANNO_TOP_Y = PAD_T + 8;
      const chartInnerW = W - PAD_L - PAD_R;
      type PlacedRect = { x1: number; y1: number; x2: number; y2: number };
      const placedRects: PlacedRect[] = [];
      // Seed with the threshold label rect (anchored to the right edge)
      // so annotation placement avoids it.
      if (spec.threshold != null) {
        const thLabel = spec.threshold_label ?? `${spec.threshold} threshold`;
        const thY = yAt(spec.threshold);
        const thW = thLabel.length * ANNO_CHAR_W;
        placedRects.push({
          x1: W - PAD_R - thW,
          y1: thY - 3 - ANNO_FS,
          x2: W - PAD_R,
          y2: thY - 3,
        });
      }
      const annoLayouts: Array<{
        a: NonNullable<typeof spec.annotations>[number];
        x: number;
        y: number;
        anchor: "start" | "end";
      }> = [];
      // Process in chronological order so left-side gets first dibs on
      // the top bands and right-side annotations work around them.
      const annosForLayout = (spec.annotations ?? [])
        .map((a) => ({ a, idx: series.findIndex((s) => s.date === a.date) }))
        .filter((p) => p.idx >= 0)
        .sort((p, q) => p.idx - q.idx);
      for (const { a, idx } of annosForLayout) {
        const x = xAt(idx);
        const labelW = a.label.length * ANNO_CHAR_W;
        const useEndAnchor = x - PAD_L > chartInnerW * 0.55;
        const candidateRect = (y: number): PlacedRect => ({
          x1: useEndAnchor ? x - 2 - labelW : x + 2,
          y1: y - ANNO_FS,
          x2: useEndAnchor ? x - 2 : x + 2 + labelW,
          y2: y,
        });
        const collides = (r: PlacedRect) =>
          placedRects.some(
            (p) =>
              !(r.x2 <= p.x1 || r.x1 >= p.x2 || r.y2 <= p.y1 || r.y1 >= p.y2),
          );
        let chosenY = ANNO_TOP_Y + (ANNO_MAX_BANDS - 1) * ANNO_LINE_H;
        for (let band = 0; band < ANNO_MAX_BANDS; band++) {
          const y = ANNO_TOP_Y + band * ANNO_LINE_H;
          const r = candidateRect(y);
          if (!collides(r)) {
            chosenY = y;
            placedRects.push(r);
            break;
          }
        }
        annoLayouts.push({
          a,
          x,
          y: chosenY,
          anchor: useEndAnchor ? "end" : "start",
        });
      }
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.bill_ref) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50 flex items-baseline justify-between gap-2">
              <div className="min-w-0">
                {spec.title && <div className="text-sm font-semibold text-stone-900">{spec.title}</div>}
                {spec.bill_ref && <div className="text-[11px] text-stone-500 font-mono">{spec.bill_ref}</div>}
              </div>
              <div className="text-[11px] font-mono tabular-nums text-stone-700 font-bold flex-shrink-0">
                {lastTotal} cosponsors
              </div>
            </div>
          )}
          <div className="px-2 py-2">
            <svg
              viewBox={`0 0 ${W} ${H}`}
              style={{ width: "100%", height: "auto", maxHeight: 220, display: "block" }}
            >
              {/* Y-axis label */}
              <text x={4} y={PAD_T + 8} fontSize="9" fill="#a8a29e" fontWeight="600">{maxTotal}</text>
              <text x={4} y={H - PAD_B + 4} fontSize="9" fill="#a8a29e" fontWeight="600">0</text>
              {/* Neutral filled area + crisp curve. Party composition
                  is intentionally not encoded here — see chips/cards. */}
              <path d={areaPath} fill="#78716c" fillOpacity="0.15" />
              <polyline
                points={linePoints}
                fill="none"
                stroke="#44403c"
                strokeWidth="1.5"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {/* Threshold line */}
              {spec.threshold != null && (
                <>
                  <line
                    x1={PAD_L} x2={W - PAD_R}
                    y1={yAt(spec.threshold)} y2={yAt(spec.threshold)}
                    stroke="#1c1917" strokeWidth="1.5" strokeDasharray="4 3"
                  />
                  <text x={W - PAD_R} y={yAt(spec.threshold) - 3} fontSize="9" fill="#1c1917" fontWeight="700" textAnchor="end">
                    {spec.threshold_label ?? `${spec.threshold} threshold`}
                  </text>
                </>
              )}
              {/* Annotations — vertical lines + collision-placed labels */}
              {annoLayouts.map((layout, i) => {
                const color = annoToneColor[layout.a.tone ?? "default"];
                const tx = layout.anchor === "end" ? layout.x - 2 : layout.x + 2;
                return (
                  <g key={i}>
                    <line
                      x1={layout.x}
                      x2={layout.x}
                      y1={PAD_T}
                      y2={H - PAD_B}
                      stroke={color}
                      strokeWidth="1"
                      strokeDasharray="2 2"
                    />
                    <text
                      x={tx}
                      y={layout.y}
                      fontSize="9"
                      fill={color}
                      fontWeight="700"
                      textAnchor={layout.anchor}
                    >
                      {layout.a.label}
                    </text>
                  </g>
                );
              })}
              {/* X-axis: days-since-introduction when dates parse,
                  else raw date strings. Cross-bill comparison only
                  works on a normalized day axis — a bill at day 362
                  isn't visually comparable to one at day 477 if both
                  use calendar dates. */}
              {[...new Set([0, Math.floor(series.length / 2), series.length - 1])].map((idx) => (
                series[idx] ? (
                  <text key={idx} x={xAt(idx)} y={H - 6} fontSize="9" fill="#78716c" textAnchor={idx === 0 ? "start" : idx === series.length - 1 ? "end" : "middle"}>
                    {dayLabel(idx)}
                  </text>
                ) : null
              ))}
            </svg>
          </div>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "mission_alignment_radar": {
      const SIZE = 280;
      const CX = SIZE / 2, CY = SIZE / 2;
      const R = SIZE / 2 - 40;
      const scale = spec.scale ?? 100;
      const N = spec.dimensions.length;
      if (!N || !Number.isFinite(scale) || scale <= 0 || spec.dimensions.some((dimension) => !Number.isFinite(dimension.value) || dimension.target != null && !Number.isFinite(dimension.target))) {
        return <div role="status" className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600">{spec.title ?? spec.subject ?? "Mission alignment"}: recorded dimensions and a positive scale are required to display this comparison.</div>;
      }
      if (spec.dimensions.some((dimension) => !Number.isFinite(dimension.value / scale * R) || dimension.target != null && !Number.isFinite(dimension.target / scale * R))) {
        return <div role="status" className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600">{spec.title ?? spec.subject ?? "Mission alignment"}: the recorded values exceed the available display scale.</div>;
      }
      // Compute axis endpoints
      const angle = (i: number) => -Math.PI / 2 + (i / N) * 2 * Math.PI;
      const point = (i: number, v: number) => {
        const r = (v / scale) * R;
        return [CX + r * Math.cos(angle(i)), CY + r * Math.sin(angle(i))];
      };
      const valuePolygon = spec.dimensions.map((d, i) => point(i, d.value).join(",")).join(" ");
      const hasTargets = spec.dimensions.some((d) => d.target != null);
      const targetPolygon = hasTargets
        ? spec.dimensions.map((d, i) => point(i, d.target ?? d.value).join(",")).join(" ")
        : null;
      // Concentric grid circles (4 rings: 25%, 50%, 75%, 100%)
      const rings = [0.25, 0.5, 0.75, 1.0];
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.subject) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50 flex items-baseline justify-between gap-2">
              {spec.title && <div className="text-sm font-semibold text-stone-900">{spec.title}</div>}
              {spec.subject && <div className="text-[11px] text-stone-500">{spec.subject}</div>}
            </div>
          )}
          <div className="px-4 py-3 flex justify-center">
            <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-full max-w-md" style={{ maxHeight: 320 }}>
              {/* Grid rings */}
              {rings.map((frac, i) => (
                <polygon
                  key={i}
                  points={spec.dimensions.map((_, j) => point(j, scale * frac).join(",")).join(" ")}
                  fill="none"
                  stroke="#e7e5e4"
                  strokeWidth="1"
                />
              ))}
              {/* Axes */}
              {spec.dimensions.map((_, i) => {
                const [x, y] = point(i, scale);
                return (
                  <line
                    key={i}
                    x1={CX} y1={CY} x2={x} y2={y}
                    stroke="#d6d3d1"
                    strokeWidth="1"
                  />
                );
              })}
              {/* Target polygon (overlay) */}
              {targetPolygon && (
                <polygon
                  points={targetPolygon}
                  fill="#94a3b8"
                  fillOpacity="0.15"
                  stroke="#64748b"
                  strokeWidth="1.5"
                  strokeDasharray="3 3"
                />
              )}
              {/* Value polygon */}
              <polygon
                points={valuePolygon}
                fill="#2563eb"
                fillOpacity="0.3"
                stroke="#2563eb"
                strokeWidth="2"
              />
              {/* Value points */}
              {spec.dimensions.map((d, i) => {
                const [x, y] = point(i, d.value);
                const color =
                  d.tone === "alert" ? "#dc2626" :
                  d.tone === "primary" ? "#2563eb" :
                  "#1c1917";
                return <circle key={i} cx={x} cy={y} r={3.5} fill={color} stroke="white" strokeWidth="1.5" />;
              })}
              {/* Axis labels */}
              {spec.dimensions.map((d, i) => {
                const [x, y] = point(i, scale * 1.18);
                const a = angle(i);
                const align = Math.cos(a) > 0.3 ? "start" : Math.cos(a) < -0.3 ? "end" : "middle";
                return (
                  <g key={i}>
                    <text x={x} y={y} fontSize="11" fill="#1c1917" fontWeight="600" textAnchor={align} dominantBaseline="middle">
                      {d.label}
                    </text>
                    <text x={x} y={y + 12} fontSize="10" fill="#78716c" fontWeight="500" textAnchor={align} dominantBaseline="middle">
                      {d.value}{d.target != null && ` / ${d.target}`}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
          {(hasTargets || spec.footer) && (
            <div className="px-4 py-1.5 border-t border-stone-100 bg-stone-50 text-[11px] text-stone-500 flex items-baseline justify-between gap-2">
              <div>
                {hasTargets && (
                  <span>
                    <span className="inline-block w-3 h-3 align-middle mr-1 bg-blue-600 rounded-sm" />Actual
                    <span className="inline-block w-3 h-3 align-middle ml-2 mr-1 border-2 border-stone-500 border-dashed rounded-sm" />Target
                  </span>
                )}
              </div>
              {spec.footer && <div>{spec.footer}</div>}
            </div>
          )}
        </div>
      );
    }

    case "member_position_diff": {
      const partyText = PARTY_TEXT[spec.member.party ?? ""] ?? "text-stone-700";
      const photoSrc = spec.member.bioguide ? legislatorPhotoUrl(spec.member.bioguide) : null;
      // Detect inconsistencies — if not all rows are the same direction
      const directions = spec.rows.map((r) =>
        r.value === "support" || r.value === "lean_support" ? "for" :
        r.value === "oppose" || r.value === "lean_oppose"   ? "against" :
        "neither"
      );
      const conflict = directions.includes("for") && directions.includes("against");
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-stone-100 bg-stone-50 flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-stone-200 overflow-hidden flex items-center justify-center text-[10px] text-stone-600 font-semibold flex-shrink-0">
              {photoSrc ? (
                <img
                  src={photoSrc}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const img = e.currentTarget as HTMLImageElement;
                    img.style.display = "none";
                    if (img.parentElement) img.parentElement.textContent = legislatorInitials(spec.member.name);
                  }}
                />
              ) : (
                legislatorInitials(spec.member.name)
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {spec.title && <div className="text-sm font-semibold text-stone-900 truncate">{spec.title}</div>}
                {spec.bill_ref && <div className="text-[11px] text-stone-500 font-mono">{spec.bill_ref}</div>}
              </div>
              <div className="text-[11px]">
                {spec.member.bioguide ? (
                  <EntityRef
                    kind="member"
                    id={spec.member.bioguide}
                    name={spec.member.name}
                    workspace_id={workspaceId}
                    className="font-semibold text-stone-900"
                  />
                ) : (
                  <span className="font-semibold text-stone-900">{spec.member.name}</span>
                )}
                {spec.member.party && <span className={`ml-1 font-bold ${partyText}`}>({spec.member.party})</span>}
                {spec.member.state && <span className="text-stone-500"> · {spec.member.state}</span>}
              </div>
            </div>
            {conflict && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 flex-shrink-0 inline-flex items-center gap-1">
                <AlertTriangle size={10} aria-hidden /> Conflict
              </span>
            )}
          </div>
          <div className="divide-y divide-stone-100">
            {spec.rows.map((row, i) => {
              const inner = (
                <div className="px-4 py-2 flex items-start gap-2.5">
                  <div className="w-32 flex-shrink-0">
                    <div className="text-[11px] font-semibold text-stone-700">{row.label}</div>
                    {row.date && <div className="text-[10px] text-stone-400 font-mono">{row.date}</div>}
                  </div>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${POSITION_CHIP[row.value]} flex-shrink-0`}>
                    {POSITION_LABEL[row.value]}
                  </span>
                  {row.detail && (
                    <div className="text-xs text-stone-600 flex-1 italic">{row.detail}</div>
                  )}
                </div>
              );
              return row.source_href ? (
                <a key={i} href={row.source_href} target="_blank" rel="noreferrer" className="block hover:bg-stone-50 no-underline text-stone-900">{inner}</a>
              ) : <div key={i}>{inner}</div>;
            })}
          </div>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 bg-stone-50 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "endorsement_card": {
      const endorseePhoto = spec.endorsee.photo_url ?? (spec.endorsee.bioguide ? legislatorPhotoUrl(spec.endorsee.bioguide) : null);
      const endorsePartyText = PARTY_TEXT[spec.endorsee.party ?? ""] ?? "text-stone-700";
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.status) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50 flex items-baseline justify-between gap-2">
              {spec.title && <div className="text-sm font-semibold text-stone-900">{spec.title}</div>}
              {spec.status && (
                <span className={`text-[9px] font-bold tracking-wider px-2 py-0.5 rounded ${ENDORSEMENT_STATUS_PILL[spec.status]}`}>
                  {ENDORSEMENT_STATUS_LABEL[spec.status]}
                </span>
              )}
            </div>
          )}
          <div className="px-4 py-3 grid grid-cols-[auto_auto_1fr] gap-3 items-center">
            <div className="flex flex-col items-center gap-1">
              <div className="w-14 h-14 rounded-full bg-stone-100 overflow-hidden flex items-center justify-center text-xs text-stone-600 font-semibold border border-stone-200">
                {spec.endorser.logo_url ? (
                  <img
                    src={spec.endorser.logo_url}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const img = e.currentTarget as HTMLImageElement;
                      img.style.display = "none";
                      if (img.parentElement) img.parentElement.textContent = spec.endorser.name.slice(0, 2).toUpperCase();
                    }}
                  />
                ) : (
                  spec.endorser.name.slice(0, 2).toUpperCase()
                )}
              </div>
              <div className="text-[10px] text-stone-500 font-semibold uppercase tracking-wider">
                {spec.endorser.type ?? "endorser"}
              </div>
            </div>
            <div className="flex flex-col items-center text-stone-400 text-2xl font-light">→</div>
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-14 h-14 rounded-full bg-stone-200 overflow-hidden flex items-center justify-center text-xs text-stone-600 font-semibold flex-shrink-0">
                {endorseePhoto ? (
                  <img
                    src={endorseePhoto}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const img = e.currentTarget as HTMLImageElement;
                      img.style.display = "none";
                      if (img.parentElement) img.parentElement.textContent = legislatorInitials(spec.endorsee.name);
                    }}
                  />
                ) : (
                  legislatorInitials(spec.endorsee.name)
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-stone-900 truncate">
                  {spec.endorsee.bioguide ? (
                    <EntityRef
                      kind="member"
                      id={spec.endorsee.bioguide}
                      name={spec.endorsee.name}
                      workspace_id={workspaceId}
                    />
                  ) : (
                    spec.endorsee.name
                  )}
                  {spec.endorsee.party && <span className={`ml-1.5 text-[10px] font-bold ${endorsePartyText}`}>({spec.endorsee.party})</span>}
                </div>
                {spec.endorsee.sublabel && <div className="text-[11px] text-stone-500 truncate">{spec.endorsee.sublabel}</div>}
              </div>
            </div>
          </div>
          <div className="px-4 pb-1">
            <div className="text-[12px] font-semibold text-stone-700">{spec.endorser.name}</div>
            {spec.endorser.role && <div className="text-[10px] text-stone-500 -mt-0.5">{spec.endorser.role}</div>}
          </div>
          {spec.statement && (
            <div className="px-4 py-2 border-t border-stone-100">
              <p className="text-sm text-stone-700 italic leading-relaxed">&ldquo;{spec.statement}&rdquo;</p>
              {spec.date && <div className="text-[11px] text-stone-400 mt-1">{spec.date}</div>}
            </div>
          )}
          {spec.media_coverage && spec.media_coverage.length > 0 && (
            <div className="px-4 py-2 border-t border-stone-100 bg-stone-50">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1">
                Media coverage
              </div>
              <ul className="text-xs space-y-0.5">
                {spec.media_coverage.map((c, i) => {
                  const inner = (
                    <span>
                      <span className="font-semibold text-stone-700">{c.outlet}</span>
                      <span className="text-stone-400 mx-1">·</span>
                      <span className="text-stone-700">{c.headline}</span>
                    </span>
                  );
                  return (
                    <li key={i}>
                      {c.href ? (
                        <a href={c.href} target="_blank" rel="noreferrer" className="hover:underline text-stone-900">{inner}</a>
                      ) : inner}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {(spec.source_href || spec.footer) && (
            <div className="px-4 py-1.5 border-t border-stone-100 bg-stone-50 text-[11px] text-stone-500 flex items-baseline justify-between gap-2">
              <span>{spec.footer}</span>
              {spec.source_href && (
                <a href={spec.source_href} target="_blank" rel="noreferrer" className="text-action hover:underline">
                  Read full endorsement →
                </a>
              )}
            </div>
          )}
        </div>
      );
    }

    case "event_attendance_card": {
      const BUCKET_ORDER: ReadonlyArray<keyof typeof spec.buckets> = ["confirmed", "maybe", "declined", "no_response"];
      const counts: Record<string, number> = {};
      BUCKET_ORDER.forEach((b) => { counts[b] = (spec.buckets[b] ?? []).length; });
      const total = BUCKET_ORDER.reduce((s, b) => s + counts[b], 0);
      const MAX_AVATARS = 8;
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-stone-100 bg-stone-50 flex items-baseline justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-stone-900">
                {spec.title ?? "Event RSVP"}
                {spec.event_label && <span className="font-normal text-stone-500 ml-2">{spec.event_label}</span>}
              </div>
              {spec.date && <div className="text-[11px] text-stone-500">{spec.date}</div>}
            </div>
            {spec.capacity != null && (
              <div className="text-[11px] font-mono tabular-nums text-stone-600 flex-shrink-0">
                <span className="font-bold text-stone-900">{counts.confirmed ?? 0}</span>
                <span className="text-stone-400"> / {spec.capacity}</span>
                <span className="text-[10px] uppercase tracking-wider text-stone-500 ml-1">capacity</span>
              </div>
            )}
          </div>
          {total > 0 && spec.capacity != null && (
            <div className="px-4 pt-2.5">
              <div className="relative h-2 rounded-full overflow-hidden bg-stone-100 flex">
                {BUCKET_ORDER.filter((b) => counts[b] > 0).map((b) => (
                  <div key={b} className={RSVP_BUCKET_FILL[b]} style={{ width: `${(counts[b] / Math.max(total, spec.capacity!)) * 100}%` }} />
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 px-4 py-3">
            {BUCKET_ORDER.map((bucket) => {
              const members = spec.buckets[bucket] ?? [];
              const visible = members.slice(0, MAX_AVATARS);
              const overflow = members.length - visible.length;
              return (
                <div key={bucket} className="min-w-0">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${RSVP_BUCKET_TEXT[bucket]}`}>
                      {RSVP_BUCKET_LABEL[bucket]}
                    </span>
                    <span className="text-sm font-bold tabular-nums text-stone-900">{counts[bucket]}</span>
                  </div>
                  <div className={`h-1 ${RSVP_BUCKET_FILL[bucket]} rounded-full mb-1.5`} />
                  <div className="flex flex-wrap gap-0.5">
                    {visible.map((m, i) => {
                      if (m.bioguide) {
                        return (
                          <EntityRef
                            key={i}
                            kind="member"
                            id={m.bioguide}
                            name={legislatorInitials(m.name)}
                            workspace_id={workspaceId}
                            className="w-6 h-6 rounded-full bg-stone-200 overflow-hidden flex items-center justify-center text-[8px] text-stone-600 font-semibold border border-white align-middle"
                          />
                        );
                      }
                      const photoSrc = m.photo_url;
                      return (
                        <div
                          key={i}
                          className="w-6 h-6 rounded-full bg-stone-200 overflow-hidden flex items-center justify-center text-[8px] text-stone-600 font-semibold border border-white"
                          title={m.name}
                        >
                          {photoSrc ? (
                            <img
                              src={photoSrc}
                              alt=""
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const img = e.currentTarget as HTMLImageElement;
                                img.style.display = "none";
                                if (img.parentElement) img.parentElement.textContent = legislatorInitials(m.name);
                              }}
                            />
                          ) : (
                            legislatorInitials(m.name)
                          )}
                        </div>
                      );
                    })}
                    {overflow > 0 && (
                      <div className="w-6 h-6 rounded-full bg-stone-100 flex items-center justify-center text-[9px] text-stone-600 font-semibold border border-white">
                        +{overflow}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "scorecard_strip": {
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.scorecard_label) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50">
              {spec.title && <div className="text-sm font-semibold text-stone-900">{spec.title}</div>}
              {spec.scorecard_label && <div className="text-[11px] text-stone-500">{spec.scorecard_label}</div>}
            </div>
          )}
          <ul className="divide-y divide-stone-100">
            {spec.members.map((m, i) => {
              const photoSrc = legislatorPhotoUrl(m.bioguide);
              const partyText = PARTY_TEXT[m.party ?? ""] ?? "text-stone-700";
              const tile = GRADE_TILE[m.grade] ?? GRADE_TILE.INC;
              const inner = (
                <div className="px-4 py-2 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-stone-200 overflow-hidden flex items-center justify-center text-[10px] text-stone-600 font-semibold flex-shrink-0">
                    <img
                      src={photoSrc}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const img = e.currentTarget as HTMLImageElement;
                        img.style.display = "none";
                        if (img.parentElement) img.parentElement.textContent = legislatorInitials(m.name);
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-stone-900 truncate">
                      <EntityRef
                        kind="member"
                        id={m.bioguide}
                        name={m.name}
                        workspace_id={workspaceId}
                        no_link={!!m.href}
                      />
                    </div>
                    <div className="text-[11px] text-stone-500">
                      {m.party && <span className={`font-bold ${partyText}`}>{m.party}</span>}
                      {m.state && <span className="ml-1">{m.state}{m.district ? `-${m.district}` : ""}</span>}
                      {m.sublabel && <span className="ml-1">· {m.sublabel}</span>}
                    </div>
                  </div>
                  {m.score != null && (
                    <div className="text-[11px] font-mono tabular-nums text-stone-600 flex-shrink-0">
                      {m.score}/100
                    </div>
                  )}
                  <div className={`w-12 h-12 rounded-md flex items-center justify-center font-bold text-lg ${tile} flex-shrink-0`}>
                    {m.grade}
                  </div>
                </div>
              );
              return (
                <li key={i}>
                  {m.href ? (
                    <a href={m.href} className="block hover:bg-stone-50 no-underline text-stone-900">{inner}</a>
                  ) : inner}
                </li>
              );
            })}
          </ul>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 bg-stone-50 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "data": {
      return (
        <div className="rounded-lg border border-dashed border-stone-300 bg-stone-50 px-4 py-6 text-center text-xs text-stone-500">
          {spec.title ? <div className="mb-1 font-medium text-stone-700">{spec.title}</div> : null}
          Live data appears when this page is opened in the workspace.
        </div>
      );
    }

    case "recipe": {
      // Defensive: recipes are expanded server-side at compose_panel
      // time and should never reach the renderer. If one does, render
      // a visible marker rather than failing silently.
      return (
        <div className="border-2 border-dashed border-amber-300 rounded-lg bg-amber-50 px-3 py-2 text-xs">
          <div className="font-bold text-amber-800 uppercase tracking-wider flex items-center gap-1"><AlertTriangle size={12} aria-hidden /> Unexpanded recipe</div>
          <div className="font-mono text-amber-900 mt-0.5">{spec.recipe_name}</div>
          {spec.params && Object.keys(spec.params).length > 0 && (
            <div className="text-[11px] text-amber-700 mt-0.5">
              params: {JSON.stringify(spec.params)}
            </div>
          )}
        </div>
      );
    }

    case "cosponsor_influence_map":
      return <CosponsorInfluenceMap spec={spec} workspaceId={workspaceId ?? undefined} />;

    case "cosponsor_avalanche_bench":
      return <CosponsorAvalancheBench spec={spec} workspaceId={workspaceId ?? undefined} />;

    case "bill_cosponsor_avalanche":
      return <BillCosponsorAvalanche spec={spec} workspaceId={workspaceId ?? undefined} />;

    case "bill_reactivation_plan":
      return <BillReactivationPlan spec={spec} workspaceId={workspaceId ?? undefined} />;

    case "committee_markup_summary":
      return <CommitteeMarkupSummary spec={spec} workspaceId={workspaceId ?? undefined} />;

    case "bill_cascade_simulator":
      return <BillCascadeSimulator spec={spec} workspaceId={workspaceId ?? undefined} />;

    case "bill_obstacle_network":
      return <BillObstacleNetwork spec={spec} workspaceId={workspaceId ?? undefined} />;

    case "precedent_anchor": {
      const precedents = spec.precedents ?? [];
      const toneRowClass = (t?: string) =>
        t === "alert"
          ? "border-l-2 border-red-300"
          : t === "warning"
            ? "border-l-2 border-amber-300"
            : t === "success"
              ? "border-l-2 border-emerald-300"
              : "border-l-2 border-stone-200";
      const toneOutcomeClass = (t?: string) =>
        t === "alert"
          ? "text-red-700"
          : t === "warning"
            ? "text-amber-700"
            : t === "success"
              ? "text-emerald-700"
              : "text-stone-700";
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          <div className="px-4 py-2 border-b border-stone-100 bg-stone-50/60">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">
              {spec.caption ?? "Procedural precedent"}
            </div>
          </div>
          <div className="px-4 py-3 flex flex-col gap-2">
            {precedents.map((p, i) => (
              <div key={i} className={`pl-2.5 ${toneRowClass(p.tone)}`}>
                <div className="flex items-baseline gap-2 flex-wrap text-[12px]">
                  {p.name ? (
                    <span className="font-semibold text-stone-900">{p.name}</span>
                  ) : null}
                  <span className="font-mono text-[10px] text-stone-500">
                    {p.bill_ref}
                  </span>
                  {p.days_at_stage != null && (
                    <span className="text-stone-600 tabular-nums">
                      · sat {p.days_at_stage}d
                    </span>
                  )}
                  <span className={`font-medium ${toneOutcomeClass(p.tone)}`}>
                    · {p.outcome}
                  </span>
                </div>
                {p.rationale && (
                  <div className="text-[11px] text-stone-500 mt-0.5">
                    {p.rationale}
                  </div>
                )}
              </div>
            ))}
          </div>
          {spec.implication && (
            <div className="px-4 py-2 border-t border-stone-100 bg-stone-50/40 text-[11px] text-stone-700 italic">
              {spec.implication}
            </div>
          )}
        </div>
      );
    }

    case "leverage_points": {
      const points = spec.points ?? [];
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          <div className="px-4 py-2 border-b border-stone-100 bg-amber-50/40">
            <div className="text-[10px] uppercase tracking-wider text-amber-900 font-bold flex items-center gap-1">
              <Star size={11} aria-hidden /> {spec.title ?? "Leverage points"}
            </div>
          </div>
          <ol className="px-4 py-3 flex flex-col gap-2.5">
            {points.map((p, i) => {
              const priClass =
                p.priority === "p1"
                  ? "bg-amber-200 text-amber-900"
                  : p.priority === "p2"
                    ? "bg-stone-200 text-stone-700"
                    : "bg-stone-100 text-stone-500";
              return (
                <li key={i} className="flex items-start gap-2.5">
                  <span
                    className={`flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold tabular-nums ${priClass}`}
                    aria-label={`item ${i + 1}`}
                  >
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] text-stone-900 leading-snug">
                      {p.action}
                      {p.target && (
                        <>
                          {" "}
                          <span className="text-stone-400">·</span>{" "}
                          <EntityRef
                            kind={p.target.kind as any}
                            id={p.target.id}
                            name={p.target.name ?? p.target.id}
                            workspace_id={workspaceId}
                            className="text-stone-700 font-medium"
                          />
                        </>
                      )}
                    </div>
                    {p.rationale && (
                      <div className="text-[11px] text-stone-500 mt-0.5 leading-snug">
                        {p.rationale}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
          {spec.footer && (
            <div className="px-4 py-1.5 border-t border-stone-100 text-[11px] text-stone-500">
              {spec.footer}
            </div>
          )}
        </div>
      );
    }

    case "parliament_chart": {
      const seats = spec.seats ?? [];
      const N = seats.length;
      if (N === 0) {
        return (
          <div className="border border-stone-200 rounded-lg bg-white p-4 text-xs text-stone-500">
            No seats to render
          </div>
        );
      }
      // Canvas geometry. ViewBox is wide enough that the half-disk fits
      // with a comfortable baseline margin for the center label.
      const W = 600;
      const H = 320;
      const CX = W / 2;
      const CY = H - 28;
      const INNER_R = 100;
      const OUTER_R = 270;

      // Row count by chamber size. Senate = 4, House = 8, custom auto.
      const rowCount =
        spec.chamber === "senate"
          ? 4
          : spec.chamber === "house"
            ? 8
            : Math.max(3, Math.min(8, Math.ceil(Math.sqrt(N))));

      // Seat radius scales with chamber size so dots stay distinguishable.
      const seatR = N <= 25 ? 8 : N <= 100 ? 6 : N <= 200 ? 4 : 3;

      // Row radii — innermost to outermost, evenly spaced.
      const radii = Array.from({ length: rowCount }, (_, r) =>
        rowCount === 1
          ? (INNER_R + OUTER_R) / 2
          : INNER_R + (r / (rowCount - 1)) * (OUTER_R - INNER_R),
      );

      // Seats per row proportional to row radius so outer rows hold more.
      const radSum = radii.reduce((a, b) => a + b, 0);
      const rowSeats = radii.map((r) => Math.round((r / radSum) * N));
      let diff = N - rowSeats.reduce((a, b) => a + b, 0);
      let adj = 0;
      while (diff > 0) {
        rowSeats[adj % rowCount]++;
        diff--;
        adj++;
      }
      while (diff < 0) {
        rowSeats[adj % rowCount]--;
        diff++;
        adj++;
      }

      // Build every (row, angle) cell position, then globally sort by
      // angle so left-to-right in the array maps to left-to-right in
      // the chart REGARDLESS of row. This is what makes a sorted-by-
      // party seats array produce a clean party wedge (D-left → R-right)
      // across all rows simultaneously.
      type Cell = { angle: number; row: number; x: number; y: number };
      const cells: Cell[] = [];
      for (let r = 0; r < rowCount; r++) {
        const n = rowSeats[r];
        const rad = radii[r];
        for (let s = 0; s < n; s++) {
          const angle =
            n === 1 ? Math.PI / 2 : Math.PI - (s / (n - 1)) * Math.PI;
          cells.push({
            angle,
            row: r,
            x: rad * Math.cos(angle),
            y: -rad * Math.sin(angle),
          });
        }
      }
      cells.sort((a, b) => b.angle - a.angle);

      // Palettes
      const STATUS_FILL: Record<string, string> = {
        yes: "#059669",
        lean_y: "#a7f3d0",
        undecided: "#a8a29e",
        lean_n: "#fda4af",
        no: "#dc2626",
        absent: "#e7e5e4",
        vacant: "#ffffff",
      };
      const PARTY_FILL: Record<string, string> = {
        D: "#2563eb",
        R: "#dc2626",
        I: "#78716c",
      };
      const HIGHLIGHT_RING: Record<string, string> = {
        holdout: "#f59e0b",
        champion: "#10b981",
        target: "#6366f1",
        leader: "#8b5cf6",
      };

      const fillFor = (seat: (typeof seats)[number]) => {
        if (seat.status && STATUS_FILL[seat.status]) return STATUS_FILL[seat.status];
        if (seat.party && PARTY_FILL[seat.party]) return PARTY_FILL[seat.party];
        return "#a8a29e";
      };
      const strokeFor = (seat: (typeof seats)[number]) => {
        if (seat.highlight && HIGHLIGHT_RING[seat.highlight])
          return HIGHLIGHT_RING[seat.highlight];
        if (seat.status === "vacant") return "#d6d3d1";
        return "none";
      };
      const strokeWidthFor = (seat: (typeof seats)[number]) => {
        if (seat.highlight) return 2;
        if (seat.status === "vacant") return 1.5;
        return 0;
      };

      // Legend: only show entries actually present.
      const statusesPresent = new Set(
        seats.map((s) => s.status).filter((x): x is NonNullable<typeof x> => !!x),
      );
      const partiesPresent = new Set(
        seats.map((s) => s.party).filter((x): x is NonNullable<typeof x> => !!x),
      );
      const highlightsPresent = new Set(
        seats
          .map((s) => s.highlight)
          .filter((x): x is NonNullable<typeof x> => !!x),
      );
      const showStatusLegend = statusesPresent.size > 0;
      const showPartyLegend = partiesPresent.size > 0;
      // When status drives fill, party-color swatches would lie (the
      // dots aren't actually painted by party). Drop the swatch and
      // signal "position encodes this" with a directional cue instead.
      const partySwatchHonest = !showStatusLegend;
      const STATUS_ORDER = [
        "yes",
        "lean_y",
        "undecided",
        "lean_n",
        "no",
        "absent",
        "vacant",
      ] as const;
      const PARTY_ORDER = ["D", "R", "I"] as const;
      const HIGHLIGHT_ORDER = [
        "holdout",
        "champion",
        "target",
        "leader",
      ] as const;
      const partyLabel: Record<string, string> = {
        D: "Democrat",
        R: "Republican",
        I: "Independent",
      };

      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {(spec.title || spec.subtitle) && (
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50">
              {spec.title && (
                <div className="text-sm font-semibold text-stone-900">
                  {spec.title}
                </div>
              )}
              {spec.subtitle && (
                <div className="text-[11px] text-stone-600 mt-0.5">
                  {spec.subtitle}
                </div>
              )}
            </div>
          )}
          <div className="px-3 py-3 flex flex-col items-center">
            <svg
              viewBox={`0 0 ${W} ${H}`}
              style={{ width: "100%", height: "auto", maxHeight: 380, display: "block" }}
            >
              {cells.map((cell, i) => {
                const seat = seats[i];
                if (!seat) return null;
                const title = [
                  seat.name ?? seat.bioguide ?? "(unnamed)",
                  seat.party ? `(${seat.party})` : null,
                  seat.status ? `· ${seat.status.replace(/_/g, " ")}` : null,
                  seat.highlight ? `· ${seat.highlight}` : null,
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <circle
                    key={i}
                    cx={CX + cell.x}
                    cy={CY + cell.y}
                    r={seatR}
                    fill={fillFor(seat)}
                    stroke={strokeFor(seat)}
                    strokeWidth={strokeWidthFor(seat)}
                  >
                    <title>{title}</title>
                  </circle>
                );
              })}
              {/* Center label — total seat count */}
              <text
                x={CX}
                y={CY - 16}
                fontSize="22"
                fontWeight="700"
                fill="#1c1917"
                textAnchor="middle"
              >
                {N}
              </text>
              <text
                x={CX}
                y={CY - 2}
                fontSize="9"
                fontWeight="700"
                letterSpacing="0.1em"
                fill="#78716c"
                textAnchor="middle"
              >
                SEATS
              </text>
            </svg>

            {(showStatusLegend || showPartyLegend || highlightsPresent.size > 0) && (
              <div className="mt-3 flex items-baseline gap-x-4 gap-y-1 flex-wrap text-[10px] text-stone-600 justify-center">
                {showStatusLegend &&
                  STATUS_ORDER.filter((s) => statusesPresent.has(s)).map((s) => {
                    const count = seats.filter((x) => x.status === s).length;
                    return (
                      <span key={`s-${s}`} className="inline-flex items-center gap-1">
                        <span
                          className="inline-block rounded-full"
                          style={{
                            width: 8,
                            height: 8,
                            backgroundColor: STATUS_FILL[s],
                            border: s === "vacant" ? "1px solid #d6d3d1" : "none",
                          }}
                        />
                        <span className="font-mono tabular-nums">{count}</span>
                        <span>{s.replace(/_/g, " ")}</span>
                      </span>
                    );
                  })}
                {showPartyLegend && partySwatchHonest &&
                  PARTY_ORDER.filter((p) => partiesPresent.has(p)).map((p) => {
                    const count = seats.filter((x) => x.party === p).length;
                    return (
                      <span key={`p-${p}`} className="inline-flex items-center gap-1">
                        <span
                          className="inline-block rounded-full"
                          style={{
                            width: 8,
                            height: 8,
                            backgroundColor: PARTY_FILL[p],
                          }}
                        />
                        <span className="font-mono tabular-nums">{count}</span>
                        <span>{partyLabel[p]}</span>
                      </span>
                    );
                  })}
                {showPartyLegend && !partySwatchHonest && (
                  <span className="inline-flex items-baseline gap-1 text-stone-500 italic">
                    position →{" "}
                    {PARTY_ORDER.filter((p) => partiesPresent.has(p)).map(
                      (p, i, arr) => {
                        const count = seats.filter((x) => x.party === p).length;
                        const positional =
                          p === "D" ? "left" : p === "R" ? "right" : "center";
                        return (
                          <span key={`pp-${p}`}>
                            <span className="font-mono tabular-nums">{count}</span>{" "}
                            {p} ({positional})
                            {i < arr.length - 1 ? " · " : ""}
                          </span>
                        );
                      },
                    )}
                  </span>
                )}
                {HIGHLIGHT_ORDER.filter((h) => highlightsPresent.has(h)).map((h) => {
                  const count = seats.filter((x) => x.highlight === h).length;
                  return (
                    <span key={`h-${h}`} className="inline-flex items-center gap-1">
                      <span
                        className="inline-block rounded-full"
                        style={{
                          width: 8,
                          height: 8,
                          border: `2px solid ${HIGHLIGHT_RING[h]}`,
                          backgroundColor: "white",
                        }}
                      />
                      <span className="font-mono tabular-nums">{count}</span>
                      <span>{h}</span>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          {spec.caption && (
            <div className="px-4 py-2 border-t border-stone-100 text-[11px] text-stone-500 italic">
              {spec.caption}
            </div>
          )}
        </div>
      );
    }

    case "diagnosis_panel": {
      const tone = spec.tone ?? "alert";
      const toneRing =
        tone === "alert"
          ? "border-red-200 bg-gradient-to-br from-red-50/70 to-white"
          : tone === "warning"
            ? "border-amber-200 bg-gradient-to-br from-amber-50/60 to-white"
            : tone === "primary"
              ? "border-accent bg-gradient-to-br from-accent/20 to-white"
              : "border-stone-200 bg-stone-50/40";
      const punchClass =
        tone === "alert"
          ? "text-red-700"
          : tone === "warning"
            ? "text-amber-700"
            : tone === "primary"
              ? "text-action"
              : "text-stone-900";
      const observationClass =
        tone === "alert"
          ? "text-red-600/90"
          : tone === "warning"
            ? "text-amber-700/90"
            : tone === "primary"
              ? "text-action/90"
              : "text-stone-700";
      const captionClass =
        tone === "alert"
          ? "text-red-700"
          : tone === "warning"
            ? "text-amber-800"
            : tone === "primary"
              ? "text-action"
              : "text-stone-600";
      const factPillClass = (factTone?: string) => {
        const t = factTone ?? "default";
        return t === "alert"
          ? "bg-red-100 text-red-800"
          : t === "warning"
            ? "bg-amber-100 text-amber-800"
            : t === "primary"
              ? "bg-accent/60 text-ink"
              : "bg-stone-100 text-stone-700";
      };
      return (
        <div className={`border rounded-xl p-5 ${toneRing}`}>
          {spec.caption && (
            <div
              className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${captionClass}`}
            >
              {spec.caption}
            </div>
          )}
          <div className={`text-sm font-medium leading-snug ${observationClass}`}>
            {spec.observation}
          </div>
          <div
            className={`mt-2 text-3xl font-bold leading-tight tracking-tight ${punchClass}`}
          >
            {spec.punch}
          </div>
          {spec.body && (
            <div className="mt-3 text-[12px] leading-relaxed text-stone-700">
              {spec.body}
            </div>
          )}
          {spec.facts && spec.facts.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {spec.facts.map((f, i) => (
                <span
                  key={i}
                  className={`inline-flex items-baseline gap-1 text-[11px] font-medium px-2 py-0.5 rounded ${factPillClass(f.tone)}`}
                >
                  <span>{f.label}</span>
                  {f.value && (
                    <>
                      <span className="opacity-50">·</span>
                      <span className="font-semibold">{f.value}</span>
                    </>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      );
    }

    case "procedural_state_strip": {
      const days = spec.days_since_introduction;
      const autoTone: "default" | "warning" | "alert" =
        days == null
          ? "default"
          : days >= 180
            ? "alert"
            : days >= 60
              ? "warning"
              : "default";
      const tone = spec.tone ?? autoTone;
      const stageClass =
        tone === "alert"
          ? "text-red-700"
          : tone === "warning"
            ? "text-amber-700"
            : "text-stone-900";
      const daysClass =
        tone === "alert"
          ? "text-red-700"
          : tone === "warning"
            ? "text-amber-700"
            : "text-stone-700";
      const stallLabel =
        tone === "alert"
          ? "long-stalled"
          : tone === "warning"
            ? "stalled"
            : null;
      const breakdown = spec.action_breakdown ?? [];
      const hasSecondary =
        !!spec.locus || breakdown.length > 0;
      return (
        <div className="border-y border-stone-200 py-3 px-4 bg-stone-50/40">
          <div className="flex items-baseline gap-2 flex-wrap">
            {spec.bill_ref && (
              <span className="text-[10px] uppercase tracking-wider font-mono text-stone-500">
                {spec.bill_ref}
              </span>
            )}
            <span className={`text-sm font-semibold ${stageClass}`}>
              {spec.stage}
            </span>
            {days != null && (
              <>
                <span className="text-stone-400">·</span>
                <span className={`text-sm tabular-nums ${daysClass}`}>
                  {days}d
                  {stallLabel && (
                    <span className="text-[11px] ml-1 text-stone-500 font-normal">
                      ({stallLabel})
                    </span>
                  )}
                </span>
              </>
            )}
            {spec.total_actions != null && (
              <>
                <span className="text-stone-400">·</span>
                <span className="text-sm text-stone-700 tabular-nums">
                  {spec.total_actions} action{spec.total_actions === 1 ? "" : "s"} total
                </span>
              </>
            )}
          </div>
          {hasSecondary && (
            <div className="text-[11px] text-stone-500 mt-1 flex items-baseline gap-1.5 flex-wrap">
              {spec.locus && <span>{spec.locus}</span>}
              {spec.locus && breakdown.length > 0 && (
                <span className="text-stone-400">·</span>
              )}
              {breakdown.map((b, i) => (
                <span key={i}>
                  {i > 0 && <span className="text-stone-400"> · </span>}
                  {b.count != null ? `${b.count} ${b.label}` : b.label}
                </span>
              ))}
            </div>
          )}
        </div>
      );
    }

    case "timeline": {
      const compact = spec.density === "compact";
      const items = spec.items;
      return (
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          {spec.title && (
            <div className="px-4 py-2 border-b border-stone-100 text-sm font-semibold text-stone-900">
              {spec.title}
            </div>
          )}
          <ol className={`relative ${compact ? "py-2" : "py-3"} pl-3 pr-4`}>
            {/* Continuous vertical line behind the dots */}
            <div className="absolute top-3 bottom-3 w-px bg-stone-200" style={{ left: "5.5rem" }} aria-hidden />
            {items.map((it, i) => {
              const dotClass = TIMELINE_DOT[it.tone ?? "default"];
              const inner = (
                <div className={`flex items-start gap-3 ${compact ? "py-1" : "py-1.5"}`}>
                  <div className="w-16 flex-shrink-0 text-[11px] text-stone-500 font-mono tabular-nums text-right pt-0.5">
                    {it.date ?? ""}
                  </div>
                  <div className="relative flex-shrink-0 w-5 flex items-center justify-center pt-1">
                    <div className={`w-3 h-3 rounded-full border-2 ${dotClass} flex items-center justify-center`}>
                      {it.icon && <SpecIcon icon={it.icon} size={11} className="absolute -top-1.5 -right-1.5 text-stone-500 bg-white rounded-full" />}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 pt-0">
                    <div className="text-sm font-medium text-stone-900 truncate">{it.title}</div>
                    {!compact && it.subtitle && (
                      <div className="text-xs text-stone-500 line-clamp-2 mt-0.5">{it.subtitle}</div>
                    )}
                  </div>
                </div>
              );
              return (
                <li key={i}>
                  {it.href ? (
                    <a href={it.href} className="block hover:bg-stone-50 -mx-3 px-3 no-underline text-stone-900">{inner}</a>
                  ) : inner}
                </li>
              );
            })}
          </ol>
        </div>
      );
    }

    default: {
      // exhaustiveness check for the switch
      const _exhaustive: never = spec;
      void _exhaustive;
      return null;
    }
  }
}

/**
 * CalendarNode — week or month grid with events keyed by ISO date.
 * Pure JS date math (no date library) — Sunday-start month grid that
 * pads with leading/trailing days so each row has 7 cells.
 */
function CalendarNode({ spec }: { spec: CalendarSpec }): JSX.Element {
  const view = spec.view ?? "month";
  const today = new Date();
  const todayKey = today.toISOString().slice(0, 10);
  const anchor = spec.anchor_date ? new Date(spec.anchor_date + "T00:00:00") : today;
  if (Number.isNaN(anchor.getTime())) anchor.setTime(today.getTime());

  const dayKey = (d: Date): string => d.toISOString().slice(0, 10);
  const cellsForView = (): Date[] => {
    if (view === "week") {
      const start = new Date(anchor);
      start.setDate(start.getDate() - start.getDay()); // back to Sunday
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        return d;
      });
    }
    // Month view: pad to full weeks
    const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const lastOfMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    const start = new Date(firstOfMonth);
    start.setDate(start.getDate() - start.getDay());
    const end = new Date(lastOfMonth);
    end.setDate(end.getDate() + (6 - end.getDay()));
    const days: Date[] = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) days.push(new Date(d));
    return days;
  };
  const cells = cellsForView();
  const monthLabel = anchor.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const dayHeaderLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
      <div className="px-4 py-2 border-b border-stone-100 flex items-baseline justify-between">
        <div className="text-sm font-semibold text-stone-900">
          {spec.title ?? (view === "week" ? "This week" : monthLabel)}
        </div>
        {spec.title && (
          <div className="text-xs text-stone-500">{view === "week" ? `${cells[0].toLocaleDateString()} – ${cells[6].toLocaleDateString()}` : monthLabel}</div>
        )}
      </div>
      <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider font-semibold text-stone-500 border-b border-stone-100">
        {dayHeaderLabels.map((d) => (
          <div key={d} className="px-2 py-1 text-center">{d}</div>
        ))}
      </div>
      <div className={`grid grid-cols-7 ${view === "week" ? "min-h-[120px]" : ""}`}>
        {cells.map((d, i) => {
          const k = dayKey(d);
          const isCurrentMonth = d.getMonth() === anchor.getMonth();
          const isToday = k === todayKey;
          const events = spec.events?.[k] ?? [];
          return (
            <div
              key={i}
              className={`border-b border-r border-stone-100 last:border-r-0 p-1.5 ${isCurrentMonth ? "bg-white" : "bg-stone-50"} ${view === "week" ? "min-h-[120px]" : "min-h-[80px]"}`}
            >
              <div className={`text-[11px] mb-1 flex items-center gap-1 ${isCurrentMonth ? "text-stone-700" : "text-stone-400"} ${isToday ? "font-bold" : ""}`}>
                {isToday ? (
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-ink text-accent text-[10px] font-bold">
                    {d.getDate()}
                  </span>
                ) : (
                  <span>{d.getDate()}</span>
                )}
              </div>
              <div className="flex flex-col gap-0.5">
                {events.slice(0, view === "week" ? 8 : 3).map((e, j) => {
                  const chip = (
                    <div
                      className={`text-[10px] px-1.5 py-0.5 rounded truncate flex items-center gap-1 ${CAL_TONE[e.tone ?? "default"]}`}
                      title={`${e.time ? e.time + " · " : ""}${e.title}`}
                    >
                      {e.icon && <SpecIcon icon={e.icon} size={10} className="shrink-0" />}
                      {e.time && <span className="font-mono text-[9px]">{e.time}</span>}
                      <span className="truncate">{e.title}</span>
                    </div>
                  );
                  return (
                    <div key={j}>
                      {e.href ? <a href={e.href} className="no-underline">{chip}</a> : chip}
                    </div>
                  );
                })}
                {events.length > (view === "week" ? 8 : 3) && (
                  <div className="text-[9px] text-stone-400 px-1.5">+{events.length - (view === "week" ? 8 : 3)} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Tiny inline sparkline used inside stat_card. Inlined here (rather
 * than imported from charts/) so PanelRenderer stays a single
 * dependency for hosts that only want layout primitives.
 */
function InlineSparkline({ values }: { values: number[] }): JSX.Element | null {
  if (!values || values.length === 0) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 100;
  const h = 18;
  const padX = 1;
  const padY = 2;
  const innerW = w - padX * 2;
  const innerH = h - padY * 2;
  const xPx = (i: number): number =>
    padX + (values.length === 1 ? innerW / 2 : (i / (values.length - 1)) * innerW);
  const yPx = (v: number): number => padY + innerH - ((v - min) / range) * innerH;
  const points = values.map((v, i) => `${xPx(i)},${yPx(v)}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden style={{ display: "inline-block" }}>
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth={1} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={xPx(values.length - 1)} cy={yPx(values[values.length - 1])} r={1.4} fill="currentColor" />
    </svg>
  );
}

/**
 * CarouselNode — pill-tab rotation between PanelSpec slides.
 *
 * Autoplay timer cycles slides every `autoplay_seconds` (default 6).
 * Clicking a tab pauses autoplay so the user can dwell on a slide.
 * autoplay_seconds=0 disables autoplay entirely (manual-only).
 */
function CarouselNode({
  spec,
  workspaceId,
  resolveWidget,
  fullscreen,
  buildDeckHref,
  deckInstanceId,
}: {
  spec: CarouselSpec;
  workspaceId?: string | null;
  resolveWidget?: PanelRendererProps["resolveWidget"];
  fullscreen?: boolean;
  buildDeckHref?: (deckId: string) => string;
  deckInstanceId?: string;
}): JSX.Element | null {
  const slides = spec.slides ?? [];
  const initial = Math.max(0, Math.min(slides.length - 1, spec.initial ?? 0));
  const [activeIndex, setActiveIndex] = useState(initial);
  const [paused, setPaused] = useState(false);
  const tv = useTvMode();
  const stopped = paused || (tv.active && tv.paused);

  // Clamp activeIndex if the slide count changes (live re-compose).
  useEffect(() => {
    if (slides.length === 0) {
      if (activeIndex !== 0) setActiveIndex(0);
      return;
    }
    if (activeIndex >= slides.length) setActiveIndex(slides.length - 1);
  }, [slides.length, activeIndex]);

  const autoplayMs = (spec.autoplay_seconds ?? 6) * 1000;
  useEffect(() => {
    if (stopped) return;
    if (autoplayMs <= 0) return;
    if (slides.length < 2) return;
    const t = setInterval(() => {
      setActiveIndex((i) => (i + 1) % slides.length);
    }, autoplayMs);
    return () => clearInterval(t);
  }, [stopped, autoplayMs, slides.length]);

  if (slides.length === 0) return null;
  const active = slides[activeIndex];

  return (
    <div className="border border-stone-200 rounded-xl bg-white overflow-hidden">
      {(spec.title || slides.length > 0) && (
        <div className="px-3 py-2 border-b border-stone-100 flex items-center gap-2">
          {spec.title && (
            <div className="text-sm font-semibold text-stone-900 mr-2">{spec.title}</div>
          )}
          <div className="flex items-center gap-1 flex-1 flex-wrap">
            {slides.map((s, i) => {
              const isActive = i === activeIndex;
              return (
                <button
                  key={i}
                  onClick={() => { setPaused(true); setActiveIndex(i); }}
                  className={
                    "text-xs font-semibold px-3 py-1 rounded-full transition-colors " +
                    (isActive
                      ? "bg-stone-900 text-white"
                      : "bg-stone-100 text-stone-700 hover:bg-stone-200")
                  }
                >
                  {s.label}
                </button>
              );
            })}
          </div>
          {autoplayMs > 0 && slides.length > 1 && (
            <button
              onClick={() => setPaused((p) => !p)}
              className="text-[10px] uppercase tracking-wider text-stone-400 hover:text-stone-700 font-mono"
              aria-label={paused ? "Resume autoplay" : "Pause autoplay"}
              title={paused ? "Autoplay paused" : `Auto-rotates every ${spec.autoplay_seconds ?? 6}s`}
            >
              {paused ? <Play size={11} aria-hidden /> : <Pause size={11} aria-hidden />}
            </button>
          )}
        </div>
      )}
      <div className="p-3">
        <PanelNode
          spec={active.spec}
          workspaceId={workspaceId}
          resolveWidget={resolveWidget}
          fullscreen={fullscreen}
          buildDeckHref={buildDeckHref}
          deckInstanceId={deckInstanceId}
        />
      </div>
    </div>
  );
}

// ─── Cosponsor influence-map primitives ───────────────────────────────
//
// Live primitives that wrap /api/v1/cosponsor-influence-network/:bioguide.
// One spec line renders what used to take ~150 lines of hand-composed
// JSON. Data auto-updates every 15 minutes upstream (pg_cron refreshes
// the cmcp_cosponsor_influence_network materialized view).

interface CIPeer {
  bioguide: string;
  name: string | null;
  party_letter: "R" | "D" | "I" | null;
  state: string | null;
  cascade_count: number;
  same_day_count: number;
  shared_bills: number;
  median_gap_days: number | null;
  pattern: "reactive" | "same-week" | "two-week" | "slow-consistent" | null;
  relationship_type: "coordinator" | "cascade" | "mixed" | "weak" | null;
  same_day_pct: number | null;
  follower_dependency_pct: number | null;
}

interface CIResponse {
  anchor: {
    bioguide: string;
    name: string | null;
    party_letter: "R" | "D" | "I" | null;
    state: string | null;
    district: number | null;
    chamber: string | null;
    total_bills_cosponsored: number | null;
  };
  congress: number;
  pattern_mix: Record<string, number>;
  summary: {
    total_followers: number;
    total_leaders: number;
    total_coordinators: number;
    total_short_order: number;
    total_non_listeners: number;
  };
  coordinators: CIPeer[];
  short_order_leaders: CIPeer[];
  cascade_leaders: CIPeer[];
  cascade_followers: CIPeer[];
  non_listeners: CIPeer[];
  zero_engagement: CIPeer[];
}

function useCosponsorInfluenceNetwork(bioguide: string, congress: number) {
  return useQuery<CIResponse>({
    queryKey: ["cosponsor-influence", bioguide, congress],
    queryFn: async () => {
      const res = await fetch(
        `/api/v1/cosponsor-influence-network/${encodeURIComponent(bioguide)}?congress=${congress}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`cosponsor-influence ${bioguide} failed: ${res.status}`);
      return (await res.json()) as CIResponse;
    },
    staleTime: 60 * 1000,
    retry: false,
  });
}

function PeerChipGrid({
  peers,
  workspaceId,
  subtitle,
  cols = 4,
}: {
  peers: CIPeer[];
  workspaceId?: string;
  subtitle: (p: CIPeer) => string;
  cols?: number;
}): JSX.Element {
  return (
    <div className={`grid ${COLS_CLASS[cols]} gap-2`}>
      {peers.map((p) => (
        <PeerChip key={p.bioguide} peer={p} workspaceId={workspaceId} subtitle={subtitle(p)} />
      ))}
    </div>
  );
}

function PeerChip({
  peer,
  workspaceId,
  subtitle,
}: {
  peer: CIPeer;
  workspaceId?: string;
  subtitle: string;
}): JSX.Element {
  const partyClass =
    PARTY_BADGE[(peer.party_letter ?? "").toUpperCase()] ?? "bg-stone-100 text-stone-700";
  const initials = legislatorInitials(peer.name ?? peer.bioguide);
  return (
    <div className="border border-stone-200 rounded-lg bg-white p-2 flex items-center gap-2 min-w-0">
      <div className="w-8 h-8 rounded-full bg-stone-200 overflow-hidden flex-shrink-0 relative">
        <img
          src={legislatorPhotoUrl(peer.bioguide)}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center text-stone-500 text-[9px] font-bold tracking-wider pointer-events-none">
          {initials}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <EntityRef
            kind="member"
            id={peer.bioguide}
            name={peer.name ?? peer.bioguide}
            workspace_id={workspaceId}
            className="text-xs font-semibold text-stone-900 truncate"
          />
          {peer.party_letter && (
            <span className={`text-[9px] px-1 py-0.5 rounded font-bold ${partyClass}`}>
              {peer.party_letter}
            </span>
          )}
          {peer.state && (
            <span className="text-[10px] text-stone-500 font-mono">{peer.state.slice(0, 2).toUpperCase()}</span>
          )}
        </div>
        <div className="text-[10px] text-stone-500 truncate mt-0.5">{subtitle}</div>
      </div>
    </div>
  );
}

function CosponsorInfluenceMap({
  spec,
  workspaceId,
}: {
  spec: CosponsorInfluenceMapSpec;
  workspaceId?: string;
}): JSX.Element {
  const congress = spec.congress ?? 119;
  const limit = spec.limit_per_section ?? 8;
  const sections =
    spec.sections ?? [
      "pattern_mix",
      "coordinators",
      "short_order_leaders",
      "cascade_leaders",
      "cascade_followers",
      "non_listeners",
    ];
  const { data, isLoading, error } = useCosponsorInfluenceNetwork(spec.bioguide, congress);

  if (isLoading) {
    return (
      <div className="border border-stone-200 rounded-lg bg-stone-50 p-4 text-xs text-stone-500 italic">
        Loading cosponsor influence map for {spec.bioguide}…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="border border-red-200 rounded-lg bg-red-50 p-3 text-xs text-red-800">
        Failed to load cosponsor influence map: {String(error)}
      </div>
    );
  }

  const labelOf = (k: string) =>
    ({
      reactive: "Reactive (≤3d)",
      "same-week": "Same-week (4-7d)",
      "two-week": "Two-week (8-14d)",
      "slow-consistent": "Slow (>14d)",
    }[k] ?? k);
  const themeOf = (k: string): "default" | "success" | "warning" | "alert" =>
    k === "reactive" ? "success" : k === "slow-consistent" ? "warning" : "default";

  return (
    <div className="space-y-4">
      {sections.includes("pattern_mix") && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5">
            Follower pattern mix — {data.summary.total_followers} cascade followers total
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {["reactive", "same-week", "two-week", "slow-consistent"].map((k) => (
              <div
                key={k}
                className={`border rounded-lg p-3 ${
                  themeOf(k) === "success"
                    ? "border-emerald-200 bg-emerald-50"
                    : themeOf(k) === "warning"
                      ? "border-amber-200 bg-amber-50"
                      : "border-stone-200 bg-white"
                }`}
              >
                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
                  {labelOf(k)}
                </div>
                <div className="text-2xl font-bold tabular-nums text-stone-900 mt-1">
                  {data.pattern_mix[k] ?? 0}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {sections.includes("coordinators") && data.coordinators.length > 0 && (
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
              Coordinators · same-day cosponsors (strongest signal)
            </div>
            <div className="text-[10px] text-stone-400">
              {data.summary.total_coordinators} total
            </div>
          </div>
          <PeerChipGrid
            peers={data.coordinators.slice(0, limit + 4)}
            workspaceId={workspaceId}
            subtitle={(p) =>
              `${p.same_day_count} same-day · ${p.same_day_pct ?? "?"}%`
            }
            cols={4}
          />
        </div>
      )}

      {sections.includes("short_order_leaders") && data.short_order_leaders.length > 0 && (
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
              Short-order leaders · who reliably precedes by ≤3 days
            </div>
            <div className="text-[10px] text-stone-400">
              {data.summary.total_short_order} total
            </div>
          </div>
          <PeerChipGrid
            peers={data.short_order_leaders.slice(0, limit)}
            workspaceId={workspaceId}
            subtitle={(p) =>
              `${p.cascade_count}x · ${p.median_gap_days ?? "?"}d med${p.same_day_count > 0 ? ` · ${p.same_day_count} same-day` : ""}`
            }
            cols={4}
          />
        </div>
      )}

      {sections.includes("cascade_leaders") && data.cascade_leaders.length > 0 && (
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
              Cascade leaders · who they tend to follow
            </div>
            <div className="text-[10px] text-stone-400">{data.summary.total_leaders} total</div>
          </div>
          <PeerChipGrid
            peers={data.cascade_leaders.slice(0, limit)}
            workspaceId={workspaceId}
            subtitle={(p) =>
              `${p.cascade_count}x · ${p.median_gap_days ?? "?"}d med · ${p.pattern ?? "?"}`
            }
            cols={4}
          />
        </div>
      )}

      {sections.includes("cascade_followers") && data.cascade_followers.length > 0 && (
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
              Cascade followers · who tends to follow them
            </div>
            <div className="text-[10px] text-stone-400">
              {data.summary.total_followers} total
            </div>
          </div>
          <PeerChipGrid
            peers={data.cascade_followers.slice(0, limit)}
            workspaceId={workspaceId}
            subtitle={(p) =>
              `${p.cascade_count}x · ${p.median_gap_days ?? "?"}d med · ${p.pattern ?? "?"}`
            }
            cols={4}
          />
        </div>
      )}

      {sections.includes("non_listeners") && data.non_listeners.length > 0 && (
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
              High-overlap non-listeners · share bills but no cascade signal
            </div>
            <div className="text-[10px] text-stone-400">
              {data.summary.total_non_listeners} total
            </div>
          </div>
          <PeerChipGrid
            peers={data.non_listeners.slice(0, limit)}
            workspaceId={workspaceId}
            subtitle={(p) =>
              `${p.shared_bills} shared · 0 lead${p.same_day_count > 0 ? ` · ${p.same_day_count} same-day` : ""}`
            }
            cols={4}
          />
        </div>
      )}
    </div>
  );
}

function CosponsorAvalancheBench({
  spec,
  workspaceId,
}: {
  spec: CosponsorAvalancheBenchSpec;
  workspaceId?: string;
}): JSX.Element {
  const congress = spec.congress ?? 119;
  const targetSize = spec.target_size ?? 5;
  const maxMedian = spec.max_median_days ?? 7;
  const { data, isLoading, error } = useCosponsorInfluenceNetwork(spec.bioguide, congress);

  if (isLoading) {
    return (
      <div className="border border-stone-200 rounded-lg bg-stone-50 p-4 text-xs text-stone-500 italic">
        Loading avalanche bench for {spec.bioguide}…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="border border-red-200 rounded-lg bg-red-50 p-3 text-xs text-red-800">
        Failed to load avalanche bench: {String(error)}
      </div>
    );
  }

  const candidates = [...data.short_order_leaders, ...data.cascade_leaders]
    .filter((p, i, arr) => arr.findIndex((q) => q.bioguide === p.bioguide) === i)
    .filter((p) => p.median_gap_days != null && p.median_gap_days <= maxMedian)
    .sort((a, b) => {
      const aScore = (a.cascade_count ?? 0) + (a.same_day_count ?? 0) * 0.5 + (1 / Math.max(a.median_gap_days ?? 99, 1)) * 10;
      const bScore = (b.cascade_count ?? 0) + (b.same_day_count ?? 0) * 0.5 + (1 / Math.max(b.median_gap_days ?? 99, 1)) * 10;
      return bScore - aScore;
    })
    .slice(0, targetSize);

  const anchorName = data.anchor.name ?? spec.bioguide;
  const partyState =
    [data.anchor.party_letter, data.anchor.state].filter(Boolean).join("-");

  return (
    <div className="border border-stone-200 rounded-xl bg-gradient-to-br from-amber-50 to-white overflow-hidden">
      <div className="px-4 py-3 border-b border-stone-200 bg-amber-50/50">
        <div className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">
          Avalanche bench
        </div>
        <div className="text-sm font-semibold text-stone-900 mt-0.5">
          The {candidates.length} people you need to get to net {anchorName}
          {partyState && <span className="font-normal text-stone-500 ml-1">({partyState})</span>}
        </div>
      </div>
      <div className="p-3">
        <PeerChipGrid
          peers={candidates}
          workspaceId={workspaceId}
          subtitle={(p) =>
            `${p.cascade_count}x cascade · ${p.median_gap_days ?? "?"}d med${
              p.same_day_count > 0 ? ` · ${p.same_day_count} same-day` : ""
            }`
          }
          cols={candidates.length >= 5 ? 5 : Math.max(candidates.length, 1)}
        />
        <div className="text-[11px] text-stone-600 mt-3 leading-relaxed">
          These returned relationships show historical cosponsorship timing
          with a median gap of at most {maxMedian} days. They do not establish
          that recruiting these members causes {anchorName.split(" ").pop()}{" "}
          to cosponsor, or that a future cosponsorship will occur.
        </div>
      </div>
    </div>
  );
}

// ─── Bill cosponsor avalanche primitive ───────────────────────────────
//
// Time-aware projection for a specific bill. Wraps
// /api/v1/bill-cosponsor-projection/:billRef and renders cosponsor
// timeline + predicted next cosponsors bucketed by status (in-window /
// overdue / late). Use on bill_detail pages.

interface BillProjectionPeer {
  bioguide: string;
  name: string | null;
  party_letter: "R" | "D" | "I" | null;
  state: string | null;
}

interface BillProjectionCosponsor extends BillProjectionPeer {
  date_cosponsored: string;
  is_original: boolean;
  days_ago: number;
}

interface BillProjectionPrediction extends BillProjectionPeer {
  leaders_on_bill: number;
  earliest_predicted_date: string;
  latest_predicted_date: string;
  earliest_overdue_days: number;
  total_cascade_evidence: number;
  strongest_via: BillProjectionPeer;
  strongest_via_gap_days: number;
  status: "in-window" | "overdue" | "late" | "stale";
}

interface KeyHoldoutCohortMember {
  bioguide: string;
  name: string | null;
  party_letter: "R" | "D" | "I" | null;
  role_on_bill: "original_cosponsor" | "follower";
  signed_at_day: number;
  relationship_type: "coordinator" | "leader" | "follower";
  relationship_strength_pct: number;
  shared_bills: number;
}

interface KeyHoldoutEntry extends BillProjectionPeer {
  cohort_total: number;
  cohort_quorum_pct: number;
  cohort_signed: KeyHoldoutCohortMember[];
  predicted_signing_day: number | null;
  current_day: number;
}

interface BillProjectionResponse {
  bill: {
    bill_ref: string;
    congress: number;
    type: string;
    number: number;
    title: string | null;
    latest_action_date: string | null;
    intro_day_zero?: string | null;
  };
  as_of: string;
  current_day?: number;
  cosponsors: BillProjectionCosponsor[];
  predictions: BillProjectionPrediction[];
  saturation?: {
    is_saturated: boolean;
    days_since_latest_cosponsor: number;
    latest_cosponsor_date: string;
  };
  key_holdouts?: KeyHoldoutEntry[];
  summary: {
    cosponsor_count: number;
    predicted_total: number;
    by_status: Record<string, number>;
  };
}

function useBillCosponsorProjection(billRef: string) {
  return useQuery<BillProjectionResponse>({
    queryKey: ["bill-cosponsor-projection", billRef],
    queryFn: async () => {
      const res = await fetch(
        `/api/v1/bill-cosponsor-projection/${encodeURIComponent(billRef)}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`bill-projection ${billRef} failed: ${res.status}`);
      return (await res.json()) as BillProjectionResponse;
    },
    staleTime: 60 * 1000,
    retry: false,
  });
}

function PredictionChip({
  pred,
  workspaceId,
}: {
  pred: BillProjectionPrediction;
  workspaceId?: string;
}): JSX.Element {
  const partyClass =
    PARTY_BADGE[(pred.party_letter ?? "").toUpperCase()] ?? "bg-stone-100 text-stone-700";
  const initials = legislatorInitials(pred.name ?? pred.bioguide);
  const statusLabel =
    pred.status === "in-window"
      ? `Predicted by ${pred.earliest_predicted_date}`
      : pred.status === "overdue"
        ? `Overdue ${pred.earliest_overdue_days}d`
        : pred.status === "late"
          ? `Late by ${pred.earliest_overdue_days}d`
          : `Stale (${pred.earliest_overdue_days}d)`;
  const statusColor =
    pred.status === "in-window"
      ? "text-emerald-700 bg-emerald-50"
      : pred.status === "overdue"
        ? "text-amber-700 bg-amber-50"
        : pred.status === "late"
          ? "text-stone-500 bg-stone-50"
          : "text-stone-400 bg-stone-50";
  return (
    <div className="border border-stone-200 rounded-lg bg-white p-2 flex items-center gap-2 min-w-0">
      <div className="w-9 h-9 rounded-full bg-stone-200 overflow-hidden flex-shrink-0 relative">
        <img
          src={legislatorPhotoUrl(pred.bioguide)}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center text-stone-500 text-[9px] font-bold tracking-wider pointer-events-none">
          {initials}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <EntityRef
            kind="member"
            id={pred.bioguide}
            name={pred.name ?? pred.bioguide}
            workspace_id={workspaceId}
            className="text-xs font-semibold text-stone-900 truncate"
          />
          {pred.party_letter && (
            <span className={`text-[9px] px-1 py-0.5 rounded font-bold ${partyClass}`}>
              {pred.party_letter}
            </span>
          )}
          {pred.state && (
            <span className="text-[10px] text-stone-500 font-mono">{pred.state.slice(0, 2).toUpperCase()}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${statusColor}`}>
            {statusLabel}
          </span>
          <span className="text-[10px] text-stone-500 truncate">
            via {pred.strongest_via.name?.split(" ").pop() ?? pred.strongest_via.bioguide} (
            {pred.strongest_via_gap_days}d) · {pred.leaders_on_bill} leaders on bill
          </span>
        </div>
      </div>
    </div>
  );
}

function CosponsorTimelineRow({
  c,
  workspaceId,
}: {
  c: BillProjectionCosponsor;
  workspaceId?: string;
}): JSX.Element {
  const partyClass =
    PARTY_BADGE[(c.party_letter ?? "").toUpperCase()] ?? "bg-stone-100 text-stone-700";
  return (
    <div className="flex items-center gap-2 text-[11px] py-1 border-b border-stone-100 last:border-0">
      <span className="text-stone-400 font-mono tabular-nums w-20 flex-shrink-0">
        {c.date_cosponsored}
      </span>
      <span className="text-stone-400 font-mono tabular-nums w-10 text-right flex-shrink-0">
        {c.days_ago}d
      </span>
      {c.is_original && (
        <span className="text-[9px] px-1 py-0.5 rounded font-bold bg-amber-100 text-amber-800 flex-shrink-0">
          ORIG
        </span>
      )}
      <EntityRef
        kind="member"
        id={c.bioguide}
        name={c.name ?? c.bioguide}
        workspace_id={workspaceId}
        className="text-stone-900 font-medium truncate"
      />
      {c.party_letter && (
        <span className={`text-[9px] px-1 py-0.5 rounded font-bold ${partyClass} flex-shrink-0`}>
          {c.party_letter}
        </span>
      )}
      {c.state && (
        <span className="text-[10px] text-stone-500 font-mono flex-shrink-0">
          {c.state.slice(0, 2).toUpperCase()}
        </span>
      )}
    </div>
  );
}

function BillCosponsorAvalanche({
  spec,
  workspaceId,
}: {
  spec: BillCosponsorAvalancheSpec;
  workspaceId?: string;
}): JSX.Element {
  const sections = spec.sections ?? ["timeline", "in_window", "overdue", "late"];
  const limit = spec.limit_per_bucket ?? 8;
  const { data, isLoading, error } = useBillCosponsorProjection(spec.bill_ref);

  if (isLoading) {
    return (
      <div className="border border-stone-200 rounded-lg bg-stone-50 p-4 text-xs text-stone-500 italic">
        Loading bill cosponsor avalanche for {spec.bill_ref}…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="border border-red-200 rounded-lg bg-red-50 p-3 text-xs text-red-800">
        Failed to load bill projection: {String(error)}
      </div>
    );
  }

  const inWindow = data.predictions.filter((p) => p.status === "in-window").slice(0, limit);
  const overdue = data.predictions.filter((p) => p.status === "overdue").slice(0, limit);
  const late = data.predictions.filter((p) => p.status === "late").slice(0, limit);

  return (
    <div className="space-y-4">
      {/* Summary header */}
      <div className="border border-stone-200 rounded-lg bg-white p-3 flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">
            Cosponsor avalanche · {data.bill.bill_ref}
          </div>
          <div className="text-sm font-semibold text-stone-900 mt-0.5 truncate max-w-[600px]">
            {data.bill.title ?? data.bill.bill_ref}
          </div>
        </div>
        <div className="flex items-baseline gap-4 text-[11px] tabular-nums">
          <span><span className="text-stone-500">on bill:</span> <b>{data.summary.cosponsor_count}</b></span>
          <span><span className="text-emerald-700">in-window:</span> <b>{data.summary.by_status["in-window"] ?? 0}</b></span>
          <span><span className="text-amber-700">overdue:</span> <b>{data.summary.by_status.overdue ?? 0}</b></span>
          <span><span className="text-stone-500">late:</span> <b>{data.summary.by_status.late ?? 0}</b></span>
        </div>
      </div>

      {sections.includes("timeline") && data.cosponsors.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5">
            Cosponsor timeline · {data.cosponsors.length} on the bill
          </div>
          <div className="border border-stone-200 rounded-lg bg-white px-3 py-1 max-h-72 overflow-y-auto">
            {data.cosponsors.map((c) => (
              <CosponsorTimelineRow key={c.bioguide} c={c} workspaceId={workspaceId} />
            ))}
          </div>
        </div>
      )}

      {sections.includes("in_window") && inWindow.length > 0 && (
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700">
              In-window · likely already moving (cold ask)
            </div>
            <div className="text-[10px] text-stone-400">{data.summary.by_status["in-window"] ?? 0} total</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {inWindow.map((p) => (
              <PredictionChip key={p.bioguide} pred={p} workspaceId={workspaceId} />
            ))}
          </div>
        </div>
      )}

      {sections.includes("overdue") && overdue.length > 0 && (
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700">
              Overdue · typical window just closed (warm ask, may need a nudge)
            </div>
            <div className="text-[10px] text-stone-400">{data.summary.by_status.overdue ?? 0} total</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {overdue.map((p) => (
              <PredictionChip key={p.bioguide} pred={p} workspaceId={workspaceId} />
            ))}
          </div>
        </div>
      )}

      {sections.includes("late") && late.length > 0 && (
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">
              Late · long past typical window (probably not coming)
            </div>
            <div className="text-[10px] text-stone-400">{data.summary.by_status.late ?? 0} total</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {late.map((p) => (
              <PredictionChip key={p.bioguide} pred={p} workspaceId={workspaceId} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Bill reactivation plan primitive ────────────────────────────────
//
// The consultant deliverable for stalled bills. Reuses the same
// bill-cosponsor-projection endpoint as bill_cosponsor_avalanche but
// reframes the data as an actionable plan:
//   - DIAGNOSIS (where the bill is stuck + saturation flag)
//   - KEY HOLDOUTS (members whose stand-with cohort — peers they reliably
//     stand with by both time signal and percentage signal — is on the
//     bill while they are not, with cohort quorum ≥60%)
//   - LATEST WAVE (next likely joiners — the in-window predictions)
//   - LAST WAVE (slow-consistent stragglers expected late — overdue
//     bucket if the bill isn't saturated, else folded into holdouts)
//   - FIRST-CALL LIST (top operational recruitment targets)

function BillReactivationPlan({
  spec,
  workspaceId,
}: {
  spec: BillReactivationPlanSpec;
  workspaceId?: string;
}): JSX.Element {
  const holdoutLimit = spec.holdout_limit ?? 8;
  const firstCallLimit = spec.first_call_limit ?? 6;
  const { data, isLoading, error } = useBillCosponsorProjection(spec.bill_ref);

  if (isLoading) {
    return (
      <div className="border border-stone-200 rounded-lg bg-stone-50 p-4 text-xs text-stone-500 italic">
        Building reactivation plan for {spec.bill_ref}…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="border border-red-200 rounded-lg bg-red-50 p-3 text-xs text-red-800">
        Failed to build reactivation plan: {String(error)}
      </div>
    );
  }

  const sat = data.saturation;
  const isSaturated = sat?.is_saturated ?? false;
  const daysSince = sat?.days_since_latest_cosponsor ?? 0;
  const holdouts = (data.key_holdouts ?? []).slice(0, holdoutLimit);
  const latestWave = data.predictions
    .filter((p) => p.status === "in-window")
    .slice(0, 6);
  const lastWave = data.predictions
    .filter((p) => p.status === "overdue")
    .slice(0, 6);

  // First-call list: combine the warmest signals.
  // Priority: overdue (warm ask) > in-window-with-multiple-leaders >
  // highest-quorum key holdouts.
  const firstCallCandidates: BillProjectionPrediction[] = [];
  // 1. Overdue picks (warm — typical window just closed)
  for (const p of data.predictions) {
    if (p.status === "overdue" && firstCallCandidates.length < firstCallLimit) {
      firstCallCandidates.push(p);
    }
  }
  // 2. Top in-window with multiple leaders on bill
  if (firstCallCandidates.length < firstCallLimit) {
    for (const p of data.predictions) {
      if (
        p.status === "in-window" &&
        p.leaders_on_bill >= 3 &&
        !firstCallCandidates.find((c) => c.bioguide === p.bioguide) &&
        firstCallCandidates.length < firstCallLimit
      ) {
        firstCallCandidates.push(p);
      }
    }
  }
  // 3. Key holdouts with highest cohort quorum — the asymmetry plays.
  // Reshape into the prediction-chip shape so the renderer below can
  // use the same chip. cohort_total stands in for leaders_on_bill in
  // the chip; the holdout's own card already showed the full evidence.
  if (firstCallCandidates.length < firstCallLimit && isSaturated) {
    for (const h of holdouts) {
      if (
        !firstCallCandidates.find((c) => c.bioguide === h.bioguide) &&
        firstCallCandidates.length < firstCallLimit
      ) {
        const overdueProxy =
          h.predicted_signing_day != null
            ? Math.max(0, h.current_day - h.predicted_signing_day)
            : 0;
        firstCallCandidates.push({
          bioguide: h.bioguide,
          name: h.name,
          party_letter: h.party_letter,
          state: h.state,
          leaders_on_bill: h.cohort_total,
          earliest_predicted_date: data.as_of,
          latest_predicted_date: data.as_of,
          earliest_overdue_days: overdueProxy,
          total_cascade_evidence: h.cohort_signed.reduce(
            (s, c) => s + c.relationship_strength_pct,
            0,
          ),
          strongest_via: h.cohort_signed[0]
            ? {
                bioguide: h.cohort_signed[0].bioguide,
                name: h.cohort_signed[0].name,
                party_letter: h.cohort_signed[0].party_letter,
                state: null,
              }
            : { bioguide: "", name: null, party_letter: null, state: null },
          strongest_via_gap_days: 0,
          status: "late",
        });
      }
    }
  }

  const diagnosisLine = isSaturated
    ? `SATURATED — last cosponsor ${daysSince} days ago. Cosponsor recruitment has run its course. Reactivation requires a fresh signal: an amendment, a markup motion, a high-visibility press moment, or a new endorsement that resets the cascade clock.`
    : daysSince <= 14
      ? `ACTIVE — last cosponsor ${daysSince} days ago. Bill is still in its natural recruitment phase. Predicted-incoming list below names who's likely already moving.`
      : `STALLING — last cosponsor ${daysSince} days ago. Past peak recruitment but not yet saturated. The overdue bucket is where the leverage is right now.`;

  return (
    <div className="border border-stone-200 rounded-xl bg-gradient-to-br from-amber-50/40 to-white overflow-hidden">
      <div className="px-4 py-3 border-b border-stone-200 bg-amber-50/50">
        <div className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">
          Bill reactivation plan · {data.bill.bill_ref}
        </div>
        <div className="text-sm font-semibold text-stone-900 mt-0.5">
          {data.bill.title ?? data.bill.bill_ref}
        </div>
      </div>

      {/* DIAGNOSIS */}
      <div className="px-4 py-3 border-b border-stone-200">
        <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-1.5">
          1 · Diagnosis
        </div>
        <div className="text-[11px] text-stone-700 leading-relaxed">{diagnosisLine}</div>
        <div className="flex items-baseline gap-4 mt-2 text-[11px] tabular-nums">
          <span>
            <span className="text-stone-500">On bill:</span> <b>{data.summary.cosponsor_count}</b>
          </span>
          <span>
            <span className="text-emerald-700">In-window:</span>{" "}
            <b>{data.summary.by_status["in-window"] ?? 0}</b>
          </span>
          <span>
            <span className="text-amber-700">Overdue:</span>{" "}
            <b>{data.summary.by_status.overdue ?? 0}</b>
          </span>
          <span>
            <span className="text-stone-500">Late / missing:</span>{" "}
            <b>{(data.summary.by_status.late ?? 0) + (data.summary.by_status.stale ?? 0)}</b>
          </span>
        </div>
      </div>

      {/* KEY HOLDOUTS — saturated bills only */}
      {isSaturated && holdouts.length > 0 && (
        <div className="px-4 py-3 border-b border-stone-200">
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-1">
            2 · Key holdouts · cohort signed, they didn't
          </div>
          <div className="text-[11px] text-stone-600 mb-2 leading-relaxed">
            Members whose stand-with cohort — peers they reliably stand with by both temporal
            proximity and overlap percentage — is on the bill while they are not. Each carries
            cohort evidence so the asymmetry is visible, not asserted.
          </div>
          <div className="grid grid-cols-1 gap-2">
            {holdouts.map((h) => {
              const partyCls =
                PARTY_BADGE[(h.party_letter ?? "").toUpperCase()] ??
                "bg-stone-100 text-stone-700";
              const signedCount = h.cohort_signed.length;
              const topThree = h.cohort_signed.slice(0, 3);
              const remaining = signedCount - topThree.length;
              const overdueDays =
                h.predicted_signing_day != null
                  ? h.current_day - h.predicted_signing_day
                  : null;
              return (
                <div
                  key={h.bioguide}
                  className="border border-stone-200 rounded-lg bg-white p-2.5 flex items-start gap-2.5 min-w-0"
                >
                  <div className="w-10 h-10 rounded-full bg-stone-200 overflow-hidden flex-shrink-0 relative">
                    <img
                      src={legislatorPhotoUrl(h.bioguide)}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center text-stone-500 text-[9px] font-bold tracking-wider pointer-events-none">
                      {legislatorInitials(h.name ?? h.bioguide)}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <EntityRef
                        kind="member"
                        id={h.bioguide}
                        name={h.name ?? h.bioguide}
                        workspace_id={workspaceId}
                        className="text-xs font-semibold text-stone-900 truncate"
                      />
                      {h.party_letter && (
                        <span className={`text-[9px] px-1 py-0.5 rounded font-bold ${partyCls}`}>
                          {h.party_letter}
                        </span>
                      )}
                      {h.state && (
                        <span className="text-[10px] text-stone-500 font-mono">
                          {h.state.slice(0, 2).toUpperCase()}
                        </span>
                      )}
                      <span className="text-[10px] text-stone-500">
                        · {signedCount}/{h.cohort_total} cohort signed ({h.cohort_quorum_pct}%)
                      </span>
                    </div>
                    <div className="text-[10px] text-stone-600 mt-1 leading-snug">
                      {topThree.map((c, i) => (
                        <span key={c.bioguide}>
                          {i > 0 && <span className="text-stone-400"> · </span>}
                          <EntityRef
                            kind="member"
                            id={c.bioguide}
                            name={c.name ?? c.bioguide}
                            workspace_id={workspaceId}
                            className="font-medium text-stone-700"
                          />
                          <span className="text-stone-400">
                            {" "}
                            ({c.relationship_type}, day {c.signed_at_day})
                          </span>
                        </span>
                      ))}
                      {remaining > 0 && (
                        <span className="text-stone-400"> · +{remaining} more</span>
                      )}
                    </div>
                    {h.predicted_signing_day != null && (
                      <div className="text-[10px] text-stone-500 mt-1 tabular-nums">
                        Pattern-predicted ~day {h.predicted_signing_day} ·{" "}
                        <span className="text-stone-700">
                          currently day {h.current_day}
                        </span>
                        {overdueDays != null && overdueDays > 0 && (
                          <span className="text-amber-700"> ({overdueDays}d past)</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* LATEST WAVE — predicted incoming */}
      {latestWave.length > 0 && (
        <div className="px-4 py-3 border-b border-stone-200">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-1">
            {isSaturated ? "3" : "2"} · Latest wave · predicted incoming
          </div>
          <div className="text-[11px] text-stone-600 mb-2">
            Typical follow-window is still open. Cold ask — they're likely already moving.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {latestWave.map((p) => (
              <PredictionChip key={p.bioguide} pred={p} workspaceId={workspaceId} />
            ))}
          </div>
        </div>
      )}

      {/* LAST WAVE — overdue */}
      {lastWave.length > 0 && (
        <div className="px-4 py-3 border-b border-stone-200">
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mb-1">
            {isSaturated ? "4" : "3"} · Last wave · overdue stragglers (warm ask)
          </div>
          <div className="text-[11px] text-stone-600 mb-2">
            Typical window just closed. They may need a nudge — most leveraged ask right now.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {lastWave.map((p) => (
              <PredictionChip key={p.bioguide} pred={p} workspaceId={workspaceId} />
            ))}
          </div>
        </div>
      )}

      {/* FIRST-CALL LIST — the operational recommendation */}
      {firstCallCandidates.length > 0 && (
        <div className="px-4 py-3 bg-amber-50/30">
          <div className="text-[10px] uppercase tracking-wider text-amber-900 font-bold mb-1 flex items-center gap-1">
            <Star size={11} aria-hidden /> First-call list · who to contact this week
          </div>
          <div className="text-[11px] text-stone-700 mb-2 leading-relaxed">
            Ranked by leverage: overdue first (warm), then in-window-with-multiple-leaders, then
            highest-quorum key holdouts (their cohort's already on the bill — they may be
            persuadable with a fresh angle).
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {firstCallCandidates.slice(0, firstCallLimit).map((p) => (
              <PredictionChip key={p.bioguide} pred={p} workspaceId={workspaceId} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Bill cascade simulator primitive ─────────────────────────────────
//
// Forward-projection of cosponsor waves. Wraps
// /api/v1/bill-cascade-simulation. Two modes: real bill (with intro
// date + actual cosponsors for comparison) or hypothetical (sponsor +
// seeds, projecting before the bill exists).

interface CascadeSimMember {
  bioguide: string;
  name: string | null;
  party_letter: "R" | "D" | "I" | null;
  state: string | null;
}

interface CascadeSimWaveMember extends CascadeSimMember {
  expected_day: number;
  via: CascadeSimMember;
  via_gap_days: number;
  cascade_count: number;
  same_day_count: number;
  actual_outcome: "as-predicted" | "slightly-late" | "late" | "missing" | null;
}

interface CascadeSimResponse {
  mode: "actual" | "hypothetical";
  congress: number;
  bill: {
    bill_ref: string;
    congress: number;
    type: string;
    number: number;
    title: string | null;
    intro_date: string | null;
  } | null;
  sponsor: CascadeSimMember;
  seed_members: CascadeSimMember[];
  waves: Array<{
    wave: number;
    day_range: { min: number; max: number };
    members: CascadeSimWaveMember[];
  }>;
  predicted_blockers: Array<
    CascadeSimMember & {
      shared_bills_with_sponsor: number;
      actual_outcome: "as-predicted" | "slightly-late" | "late" | "missing" | null;
    }
  >;
  summary: {
    total_predicted: number;
    total_blockers: number;
    waves_computed: number;
    max_wave_day: number;
  };
}

function useBillCascadeSimulation(params: {
  billRef?: string;
  sponsor?: string;
  seed?: string;
  maxWaves?: number;
  congress?: number;
}) {
  const qs = new URLSearchParams();
  if (params.billRef) qs.set("bill_ref", params.billRef);
  if (params.sponsor) qs.set("sponsor", params.sponsor);
  if (params.seed) qs.set("seed", params.seed);
  if (params.maxWaves != null) qs.set("max_waves", String(params.maxWaves));
  if (params.congress != null) qs.set("congress", String(params.congress));
  const url = `/api/v1/bill-cascade-simulation?${qs.toString()}`;
  return useQuery<CascadeSimResponse>({
    queryKey: ["bill-cascade-sim", params.billRef ?? "", params.sponsor ?? "", params.seed ?? "", params.maxWaves ?? 4, params.congress ?? 119],
    queryFn: async () => {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`cascade-sim failed: ${res.status}`);
      return (await res.json()) as CascadeSimResponse;
    },
    staleTime: 60 * 1000,
    retry: false,
  });
}

function WaveMemberChip({
  m,
  workspaceId,
}: {
  m: CascadeSimWaveMember;
  workspaceId?: string;
}): JSX.Element {
  const partyClass =
    PARTY_BADGE[(m.party_letter ?? "").toUpperCase()] ?? "bg-stone-100 text-stone-700";
  const outcomeColor =
    m.actual_outcome === "as-predicted"
      ? "text-emerald-700 bg-emerald-50"
      : m.actual_outcome === "slightly-late"
        ? "text-amber-700 bg-amber-50"
        : m.actual_outcome === "late"
          ? "text-stone-600 bg-stone-100"
          : m.actual_outcome === "missing"
            ? "text-red-700 bg-red-50"
            : null;
  const outcomeLabel =
    m.actual_outcome === "as-predicted"
      ? "as predicted"
      : m.actual_outcome === "slightly-late"
        ? "slightly late"
        : m.actual_outcome === "late"
          ? "late"
          : m.actual_outcome === "missing"
            ? "missing"
            : null;
  return (
    <div className="border border-stone-200 rounded-lg bg-white p-2 flex items-center gap-2 min-w-0">
      <div className="w-8 h-8 rounded-full bg-stone-200 overflow-hidden flex-shrink-0 relative">
        <img
          src={legislatorPhotoUrl(m.bioguide)}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center text-stone-500 text-[9px] font-bold tracking-wider pointer-events-none">
          {legislatorInitials(m.name ?? m.bioguide)}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <EntityRef
            kind="member"
            id={m.bioguide}
            name={m.name ?? m.bioguide}
            workspace_id={workspaceId}
            className="text-xs font-semibold text-stone-900 truncate"
          />
          {m.party_letter && (
            <span className={`text-[9px] px-1 py-0.5 rounded font-bold ${partyClass}`}>
              {m.party_letter}
            </span>
          )}
          {m.state && (
            <span className="text-[10px] text-stone-500 font-mono">
              {m.state.slice(0, 2).toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <span className="text-[10px] text-stone-500 font-mono tabular-nums">
            d+{m.expected_day}
          </span>
          <span className="text-[10px] text-stone-500 truncate">
            via {m.via.name?.split(" ").pop() ?? m.via.bioguide} ({m.via_gap_days}d)
          </span>
          {outcomeLabel && (
            <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${outcomeColor}`}>
              {outcomeLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function BillCascadeSimulator({
  spec,
  workspaceId,
}: {
  spec: BillCascadeSimulatorSpec;
  workspaceId?: string;
}): JSX.Element {
  const { data, isLoading, error } = useBillCascadeSimulation({
    billRef: spec.bill_ref,
    sponsor: spec.sponsor,
    seed: spec.seed,
    maxWaves: spec.max_waves ?? 4,
    congress: spec.congress ?? 119,
  });

  if (isLoading) {
    return (
      <div className="border border-stone-200 rounded-lg bg-stone-50 p-4 text-xs text-stone-500 italic">
        Running cascade simulation…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="border border-red-200 rounded-lg bg-red-50 p-3 text-xs text-red-800">
        Failed to run cascade simulation: {String(error)}
      </div>
    );
  }

  // Mode-aware header copy
  const headerLabel =
    data.mode === "actual"
      ? `Cascade simulation · ${data.bill?.bill_ref ?? "bill"}`
      : `Hypothetical cascade · sponsor ${data.sponsor.name ?? data.sponsor.bioguide}`;
  const headerSubtitle =
    data.mode === "actual"
      ? `Sponsor ${data.sponsor.name?.split(" ").pop() ?? data.sponsor.bioguide} · introduced ${data.bill?.intro_date ?? "?"}`
      : `${data.seed_members.length > 0 ? `Seeded with ${data.seed_members.length} cosponsors` : "Solo sponsor"} · simulating ${data.summary.waves_computed} waves`;

  // For mode=actual: count outcomes for the summary line
  const outcomeCounts: Record<string, number> = {};
  if (data.mode === "actual") {
    for (const w of data.waves) {
      for (const m of w.members) {
        if (m.actual_outcome) {
          outcomeCounts[m.actual_outcome] = (outcomeCounts[m.actual_outcome] ?? 0) + 1;
        }
      }
    }
  }

  return (
    <div className="border border-stone-200 rounded-xl bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-stone-200 bg-gradient-to-br from-accent/20 to-white">
        <div className="text-[10px] uppercase tracking-wider text-action font-bold">
          {headerLabel}
        </div>
        <div className="text-sm text-stone-700 mt-0.5">{headerSubtitle}</div>
        {data.bill?.title && (
          <div className="text-xs text-stone-500 mt-0.5 truncate">{data.bill.title}</div>
        )}
        <div className="flex items-baseline gap-4 mt-2 text-[11px] tabular-nums">
          <span>
            <span className="text-stone-500">Predicted:</span> <b>{data.summary.total_predicted}</b>
          </span>
          <span>
            <span className="text-stone-500">Blockers:</span>{" "}
            <b>{data.summary.total_blockers}</b>
          </span>
          <span>
            <span className="text-stone-500">Saturation est.:</span>{" "}
            <b>d+{data.summary.max_wave_day}</b>
          </span>
          {data.mode === "actual" && (
            <>
              <span>
                <span className="text-emerald-700">As predicted:</span>{" "}
                <b>{outcomeCounts["as-predicted"] ?? 0}</b>
              </span>
              <span>
                <span className="text-red-700">Missing:</span>{" "}
                <b>{outcomeCounts.missing ?? 0}</b>
              </span>
            </>
          )}
        </div>
      </div>

      {/* WAVES */}
      <div className="px-4 py-3 space-y-3">
        {data.waves.map((w) => (
          <div key={w.wave}>
            <div className="flex items-baseline justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">
                Wave {w.wave} · d+{w.day_range.min} to d+{w.day_range.max}
              </div>
              <div className="text-[10px] text-stone-400">
                {w.members.length} predicted
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {w.members.slice(0, 12).map((m) => (
                <WaveMemberChip key={m.bioguide} m={m} workspaceId={workspaceId} />
              ))}
            </div>
            {w.members.length > 12 && (
              <div className="text-[10px] text-stone-400 mt-1">
                + {w.members.length - 12} more in this wave
              </div>
            )}
          </div>
        ))}
      </div>

      {/* PREDICTED BLOCKERS */}
      {data.predicted_blockers.length > 0 && (
        <div className="px-4 py-3 border-t border-stone-200 bg-red-50/30">
          <div className="text-[10px] uppercase tracking-wider text-red-800 font-bold mb-1">
            Predicted blockers · structurally absent
          </div>
          <div className="text-[11px] text-stone-700 mb-2 leading-relaxed">
            Members with high historical shared-bill volume with the sponsor but{" "}
            <b>zero cascade signal</b>. They're in the policy neighborhood but never follow this
            sponsor's cascade. Expect them to not cosponsor without a fresh signal.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {data.predicted_blockers.slice(0, 9).map((b) => {
              const partyClass =
                PARTY_BADGE[(b.party_letter ?? "").toUpperCase()] ??
                "bg-stone-100 text-stone-700";
              return (
                <div
                  key={b.bioguide}
                  className="border border-stone-200 rounded-lg bg-white p-2 flex items-center gap-2 min-w-0"
                >
                  <div className="w-8 h-8 rounded-full bg-stone-200 overflow-hidden flex-shrink-0 relative">
                    <img
                      src={legislatorPhotoUrl(b.bioguide)}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center text-stone-500 text-[9px] font-bold tracking-wider pointer-events-none">
                      {legislatorInitials(b.name ?? b.bioguide)}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <EntityRef
                        kind="member"
                        id={b.bioguide}
                        name={b.name ?? b.bioguide}
                        workspace_id={workspaceId}
                        className="text-xs font-semibold text-stone-900 truncate"
                      />
                      {b.party_letter && (
                        <span className={`text-[9px] px-1 py-0.5 rounded font-bold ${partyClass}`}>
                          {b.party_letter}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-stone-500 mt-0.5 truncate">
                      {b.shared_bills_with_sponsor} shared · 0 cascade
                      {b.actual_outcome === "missing" && (
                        <span className="ml-1 text-red-700 font-medium">· confirmed missing</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Bill obstacle network primitive ──────────────────────────────────
//
// Wraps /api/v1/bill-obstacle-network/:billRef. Surfaces the members
// standing between the bill and passage, ranked by committee power ×
// network distance from sponsor.

interface ObstacleCascade {
  cascade_count: number;
  same_day_count: number;
  shared_bills: number;
  median_gap_days: number | null;
  relationship_type: string | null;
  interpretation: string;
}
interface ObstacleRow {
  bioguide: string;
  name: string | null;
  party_letter: "R" | "D" | "I" | null;
  state: string | null;
  committee_system_code: string;
  committee_name: string;
  role: string;
  obstacle_score: number;
  cascade: ObstacleCascade | null;
  strategy: string;
}
interface ObstacleNetworkResponse {
  bill: {
    bill_ref: string;
    congress: number;
    type: string;
    number: number;
    title: string | null;
  };
  sponsor: {
    bioguide: string;
    name: string | null;
    party_letter: "R" | "D" | "I" | null;
    state: string | null;
  } | null;
  referral_committees: Array<{ system_code: string; name: string; chamber: string }>;
  cosponsor_count: number;
  obstacles: ObstacleRow[];
  summary: {
    total_obstacles: number;
    chairs_blocking: number;
    ranking_members_blocking: number;
    high_anomaly: number;
  };
}

function useBillObstacleNetwork(billRef: string) {
  return useQuery<ObstacleNetworkResponse>({
    queryKey: ["bill-obstacle-network", billRef],
    queryFn: async () => {
      const res = await fetch(
        `/api/v1/bill-obstacle-network/${encodeURIComponent(billRef)}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`obstacle-network failed: ${res.status}`);
      return (await res.json()) as ObstacleNetworkResponse;
    },
    staleTime: 60 * 1000,
    retry: false,
  });
}

function ObstacleChip({
  o,
  workspaceId,
}: {
  o: ObstacleRow;
  workspaceId?: string;
}): JSX.Element {
  const partyClass =
    PARTY_BADGE[(o.party_letter ?? "").toUpperCase()] ?? "bg-stone-100 text-stone-700";
  const roleColor =
    o.role === "Chair"
      ? "text-red-800 bg-red-100"
      : o.role === "Ranking Member"
        ? "text-orange-800 bg-orange-100"
        : o.role === "Vice Chair"
          ? "text-amber-800 bg-amber-100"
          : "text-stone-700 bg-stone-100";
  const anomalyBadge =
    o.cascade && o.cascade.cascade_count === 0 && o.cascade.shared_bills >= 6
      ? "ANOMALOUS"
      : o.cascade && (o.cascade.cascade_count > 0 || o.cascade.same_day_count > 0)
        ? "HAS SIGNAL"
        : null;
  const anomalyColor =
    anomalyBadge === "ANOMALOUS"
      ? "text-red-700 bg-red-50"
      : anomalyBadge === "HAS SIGNAL"
        ? "text-emerald-700 bg-emerald-50"
        : "";
  return (
    <div className="border border-stone-200 rounded-lg bg-white p-3 flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-stone-200 overflow-hidden flex-shrink-0 relative">
        <img
          src={legislatorPhotoUrl(o.bioguide)}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center text-stone-500 text-[10px] font-bold tracking-wider pointer-events-none">
          {legislatorInitials(o.name ?? o.bioguide)}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <EntityRef
            kind="member"
            id={o.bioguide}
            name={o.name ?? o.bioguide}
            workspace_id={workspaceId}
            className="text-sm font-semibold text-stone-900 truncate"
          />
          {o.party_letter && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${partyClass}`}>
              {o.party_letter}
            </span>
          )}
          {o.state && (
            <span className="text-xs text-stone-500 font-mono">
              {o.state.slice(0, 2).toUpperCase()}
            </span>
          )}
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${roleColor}`}>
            {o.role.toUpperCase()}
          </span>
          <span className="text-[10px] text-stone-500 font-mono tabular-nums">
            score: {o.obstacle_score}
          </span>
          {anomalyBadge && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${anomalyColor}`}>
              {anomalyBadge}
            </span>
          )}
        </div>
        {o.cascade && (
          <div className="text-[11px] text-stone-600 mt-1.5 leading-relaxed">
            <span className="italic">{o.cascade.interpretation}</span>
            {(o.cascade.cascade_count > 0 ||
              o.cascade.same_day_count > 0 ||
              o.cascade.shared_bills > 0) && (
              <span className="text-stone-500 ml-1">
                ({o.cascade.cascade_count}x cascade · {o.cascade.same_day_count} same-day ·{" "}
                {o.cascade.shared_bills} shared bills)
              </span>
            )}
          </div>
        )}
        <div className="text-[11px] text-stone-700 mt-1 leading-relaxed">
          <b>Strategy:</b> {o.strategy}
        </div>
      </div>
    </div>
  );
}

function BillObstacleNetwork({
  spec,
  workspaceId,
}: {
  spec: BillObstacleNetworkSpec;
  workspaceId?: string;
}): JSX.Element {
  const limit = spec.limit ?? 15;
  const { data, isLoading, error } = useBillObstacleNetwork(spec.bill_ref);

  if (isLoading) {
    return (
      <div className="border border-stone-200 rounded-lg bg-stone-50 p-4 text-xs text-stone-500 italic">
        Mapping the obstacle network for {spec.bill_ref}…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="border border-red-200 rounded-lg bg-red-50 p-3 text-xs text-red-800">
        Failed to map obstacles: {String(error)}
      </div>
    );
  }

  const sortedObstacles = data.obstacles.slice(0, limit);
  const chairBlockers = sortedObstacles.filter((o) => o.role === "Chair");
  const rmBlockers = sortedObstacles.filter((o) => o.role === "Ranking Member");
  const otherBlockers = sortedObstacles.filter(
    (o) => o.role !== "Chair" && o.role !== "Ranking Member",
  );

  return (
    <div className="border border-stone-200 rounded-xl bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-stone-200 bg-gradient-to-br from-red-50/40 to-white">
        <div className="text-[10px] uppercase tracking-wider text-red-800 font-bold">
          Obstacle network · {data.bill.bill_ref}
        </div>
        <div className="text-sm font-semibold text-stone-900 mt-0.5">
          {data.bill.title ?? data.bill.bill_ref}
        </div>
        <div className="text-[11px] text-stone-600 mt-1">
          Sponsor:{" "}
          {data.sponsor ? (
            <span className="text-stone-800 font-medium">
              {data.sponsor.name ?? data.sponsor.bioguide}
              {data.sponsor.party_letter && ` (${data.sponsor.party_letter})`}
              {data.sponsor.state && `-${data.sponsor.state.slice(0, 2)}`}
            </span>
          ) : (
            <span className="italic">unknown</span>
          )}{" "}
          · {data.cosponsor_count} cosponsors
        </div>
        {data.referral_committees.length > 0 && (
          <div className="text-[11px] text-stone-600 mt-0.5">
            Referred to:{" "}
            <span className="text-stone-800">
              {data.referral_committees.map((c) => c.name).join(", ")}
            </span>
          </div>
        )}
        <div className="flex items-baseline gap-4 mt-2 text-[11px] tabular-nums">
          <span>
            <span className="text-stone-500">Obstacles:</span>{" "}
            <b>{data.summary.total_obstacles}</b>
          </span>
          <span>
            <span className="text-red-700">Chair-level:</span>{" "}
            <b>{data.summary.chairs_blocking}</b>
          </span>
          <span>
            <span className="text-orange-700">RM-level:</span>{" "}
            <b>{data.summary.ranking_members_blocking}</b>
          </span>
          <span>
            <span className="text-red-600">High-anomaly:</span>{" "}
            <b>{data.summary.high_anomaly}</b>
          </span>
        </div>
      </div>

      {chairBlockers.length > 0 && (
        <div className="px-4 py-3 border-b border-stone-200 bg-red-50/30">
          <div className="text-[10px] uppercase tracking-wider text-red-800 font-bold mb-2 flex items-center gap-1">
            <Star size={11} aria-hidden /> Chair-level obstacles · markup gate
          </div>
          <div className="space-y-2">
            {chairBlockers.map((o) => (
              <ObstacleChip key={o.bioguide + o.committee_system_code} o={o} workspaceId={workspaceId} />
            ))}
          </div>
        </div>
      )}

      {rmBlockers.length > 0 && (
        <div className="px-4 py-3 border-b border-stone-200">
          <div className="text-[10px] uppercase tracking-wider text-orange-800 font-bold mb-2">
            Ranking-member obstacles · minority gate
          </div>
          <div className="space-y-2">
            {rmBlockers.map((o) => (
              <ObstacleChip key={o.bioguide + o.committee_system_code} o={o} workspaceId={workspaceId} />
            ))}
          </div>
        </div>
      )}

      {otherBlockers.length > 0 && (
        <div className="px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-2">
            Committee members · non-cosponsors
          </div>
          <div className="space-y-2">
            {otherBlockers.map((o) => (
              <ObstacleChip key={o.bioguide + o.committee_system_code} o={o} workspaceId={workspaceId} />
            ))}
          </div>
        </div>
      )}

      {sortedObstacles.length === 0 && (
        <div className="px-4 py-3 text-xs text-stone-500 italic">
          No committee obstacles found — bill may not be tracked in a committee yet, or the
          committee data hasn't synced.
        </div>
      )}
    </div>
  );
}

// ── CommitteeMarkupSummary — compact panel-spec primitive ───────────
//
// Fetches /api/v1/workspaces/:wsId/bills/:bill_ref/committee-reports
// and renders a small summary card. Distinct from the full
// CommitteeReportsSection on the Amendments tab — this is the
// state-of-play / overview variant. AI composes this into bill_detail
// pages to surface committee context without expanding every
// amendment line.

interface CMSReport {
  id: string;
  report_number: string;
  congress: number;
  chamber: string;
  committee_name: string | null;
  filed_date: string | null;
  final_vote_ayes: number | null;
  final_vote_nays: number | null;
  has_dissenting_views: boolean;
  pdf_url: string | null;
  amendments: Array<{
    amendment_seq: number;
    amendment_kind: string | null;
    sponsor_name: string | null;
    sponsor_party: string | null;
    description: string | null;
    result: string | null;
    ayes: number | null;
    nays: number | null;
  }>;
}

const CMS_RESULT_STYLE: Record<string, string> = {
  passed: "bg-emerald-100 text-emerald-800",
  agreed_by_voice: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-100 text-red-800",
  withdrawn: "bg-stone-200 text-stone-700",
  not_offered: "bg-stone-100 text-stone-600",
};
const CMS_RESULT_LABEL: Record<string, string> = {
  passed: "passed",
  agreed_by_voice: "voice",
  failed: "failed",
  withdrawn: "withdrawn",
  not_offered: "not offered",
};

function CommitteeMarkupSummary({
  spec,
  workspaceId,
}: {
  spec: CommitteeMarkupSummarySpec;
  workspaceId?: string | null;
}): JSX.Element {
  const limit = Math.min(spec.limit ?? 5, 12);
  const wsId = workspaceId ?? "";
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["committee-markup-summary", wsId, spec.bill_ref] as const,
    enabled: !!wsId,
    queryFn: async () => {
      const res = await fetch(
        `/api/v1/workspaces/${encodeURIComponent(wsId)}/bills/${encodeURIComponent(spec.bill_ref)}/committee-reports`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`Committee markup request failed (${res.status})`);
      return (await res.json()) as { count: number; reports: CMSReport[] };
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const title = spec.title ?? "Committee markup";
  if (!wsId) return <div role="status" className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600">Select a workspace to view {title.toLowerCase()}. No committee records have been requested.</div>;

  if (isLoading) {
    return (
      <div className="border border-stone-200 rounded-lg bg-stone-50 p-3 text-xs text-stone-500 italic">
        Loading {title}…
      </div>
    );
  }

  if (error) {
    return <div role="alert" className="border border-red-200 rounded-lg bg-red-50 p-3 text-xs text-red-800">
      <div className="font-semibold">{title} could not be loaded.</div>
      <p className="mt-1">The request failed. This does not mean that no committee markup is recorded.</p>
      <button type="button" onClick={() => void refetch()} className="mt-2 rounded border border-red-300 bg-white px-2 py-1 font-semibold">Try again</button>
    </div>;
  }

  const reports = data?.reports ?? [];
  if (reports.length === 0) {
    return (
      <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-stone-100 bg-stone-50/60 text-[10px] uppercase tracking-wider text-stone-500 font-bold">
          {title}
        </div>
        <div className="px-3 py-3 text-[11px] text-stone-500 italic">
          No committee markup recorded for {spec.bill_ref} yet — either it
          hasn't been reported out of committee, or the markup data hasn't
          synced.
        </div>
      </div>
    );
  }

  // For state-of-play surfaces we typically only want the most recent
  // report. If multiple exist (rare — usually only one per chamber),
  // render the most recent at the top with a compact secondary list.
  const primary = reports[0];
  const ams = (primary.amendments ?? []).slice(0, limit);
  const more = (primary.amendments?.length ?? 0) - ams.length;
  const passed = (primary.amendments ?? []).filter(
    (a) => a.result === "passed" || a.result === "agreed_by_voice",
  ).length;
  const failed = (primary.amendments ?? []).filter((a) => a.result === "failed")
    .length;

  return (
    <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
      <div className="px-3 py-2 border-b border-stone-100 bg-stone-50/60 flex items-baseline justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">
          {title}
          {primary.committee_name && (
            <span className="ml-2 normal-case tracking-normal text-stone-700 font-normal">
              {primary.committee_name}
            </span>
          )}
        </span>
        <span className="text-[10px] font-mono tabular-nums text-stone-400">
          {primary.report_number}
          {primary.filed_date && (
            <span className="ml-1">· filed {primary.filed_date}</span>
          )}
        </span>
      </div>

      {/* Final vote + amendment-result strip */}
      <div className="px-3 py-2 flex items-baseline gap-3 flex-wrap text-[11px]">
        {primary.final_vote_ayes != null && primary.final_vote_nays != null && (
          <span className="font-mono tabular-nums">
            Vote: <b>{primary.final_vote_ayes}</b>–<b>{primary.final_vote_nays}</b>
            {primary.final_vote_ayes > primary.final_vote_nays ? (
              <span className="ml-1 text-emerald-700">favorably reported</span>
            ) : (
              <span className="ml-1 text-red-700">rejected</span>
            )}
          </span>
        )}
        <span className="text-stone-500 tabular-nums">
          {primary.amendments?.length ?? 0} amendment
          {(primary.amendments?.length ?? 0) === 1 ? "" : "s"}
          {passed > 0 && (
            <span className="ml-1 text-emerald-700">· {passed} passed</span>
          )}
          {failed > 0 && (
            <span className="ml-1 text-red-700">· {failed} failed</span>
          )}
        </span>
        {primary.has_dissenting_views && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
            dissenting views
          </span>
        )}
        {primary.pdf_url && (
          <a
            href={primary.pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-[11px] text-action hover:underline"
          >
            Full report →
          </a>
        )}
      </div>

      {/* Compact amendment rows */}
      {ams.length > 0 && (
        <ul className="border-t border-stone-100 divide-y divide-stone-100">
          {ams.map((a) => {
            const style = a.result
              ? CMS_RESULT_STYLE[a.result] ?? "bg-stone-100 text-stone-600"
              : "bg-stone-100 text-stone-600";
            const label = a.result
              ? CMS_RESULT_LABEL[a.result] ?? a.result
              : "—";
            return (
              <li
                key={a.amendment_seq}
                className="px-3 py-1.5 flex items-center gap-2 text-[11px]"
              >
                <span className="font-mono text-[10px] text-stone-500 w-5 text-right tabular-nums flex-shrink-0">
                  #{a.amendment_seq}
                </span>
                <span className="font-medium text-stone-800 truncate flex-shrink-0">
                  {a.sponsor_name ?? (a.amendment_kind === "ans" ? "Committee" : "—")}
                </span>
                {a.sponsor_party && (
                  <span className="text-[10px] font-bold text-stone-500 w-3 text-center flex-shrink-0">
                    {a.sponsor_party}
                  </span>
                )}
                <span className="flex-1 text-stone-600 truncate min-w-0">
                  {a.description ??
                    (a.amendment_kind === "ans"
                      ? "Amendment in the nature of a substitute"
                      : "")}
                </span>
                {a.ayes != null && a.nays != null && (
                  <span className="font-mono text-[10px] text-stone-500 tabular-nums flex-shrink-0">
                    {a.ayes}–{a.nays}
                  </span>
                )}
                <span
                  className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded flex-shrink-0 ${style}`}
                >
                  {label}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {more > 0 && (
        <div className="px-3 py-1.5 border-t border-stone-100 text-[10px] text-stone-400">
          +{more} more amendment{more === 1 ? "" : "s"} in the full report
        </div>
      )}
      {reports.length > 1 && (
        <div className="px-3 py-1.5 border-t border-stone-100 text-[10px] text-stone-400">
          {reports.length - 1} additional report{reports.length - 1 === 1 ? "" : "s"} filed
        </div>
      )}
    </div>
  );
}
